import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  FaFacebookF,
  FaHeart,
  FaRegHeart,
  FaInstagram,
  FaLinkedinIn,
  FaPinterestP,
  FaRedditAlien,
  FaBars,
  FaChevronDown,
  FaFilter,
  FaShoppingCart,
  FaStar,
  FaTiktok,
  FaTimes,
  FaTwitter,
} from 'react-icons/fa';
import './App.css';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { getSupabaseEnvDebugInfo, isSupabaseConfigured, supabase } from './supabaseClient';
import { fileToDataUrl, uploadProductImage } from './productImageUpload';
import * as XLSX from 'xlsx';

/** Alias: same glyph as `FaShoppingCart` (header); old code paths used the name `FaShoppingBag`. */
const FaShoppingBag = FaShoppingCart;

const CUSTOMER_SESSION_KEY = 'maisonLoggedIn';
const CUSTOMER_EMAIL_KEY = 'maisonUserEmail';
const CUSTOMER_NAME_KEY = 'maisonUserName';
const CUSTOMER_CART_KEY = 'maisonCartItems';
const GUEST_WISHLIST_KEY = 'maisonGuestWishlist';
const AUTH_DEBUG_LOGS_KEY = 'maisonAuthDebugLogs';
const FREE_DELIVERY_THRESHOLD = 19.99;
const STANDARD_DELIVERY_CHARGE = 3.79;

/** Supabase Edge Function names (deploy these in your Supabase project). */
const EDGE_FN_CREATE_CHECKOUT = 'create-checkout-session';
const EDGE_FN_FINALIZE_STRIPE = 'finalize-stripe-checkout';
const EDGE_FN_SEND_CUSTOMER_EMAIL = 'send-customer-email';

/**
 * Non-2xx Edge Function responses use FunctionsHttpError; the real message is in the response JSON.
 * See: https://supabase.com/docs/reference/javascript/functions-invoke
 */
async function edgeFunctionErrorMessage(fnError, data) {
  if (data && typeof data === 'object') {
    if (typeof data.error === 'string') return data.error;
    if (typeof data.message === 'string') return data.message;
  }
  if (
    fnError instanceof FunctionsHttpError &&
    fnError.context &&
    typeof fnError.context.json === 'function'
  ) {
    try {
      const body = await fnError.context.json();
      if (body && typeof body === 'object') {
        if (typeof body.error === 'string') return body.error;
        if (typeof body.message === 'string') return body.message;
      }
    } catch {
      /* ignore */
    }
  }
  return fnError?.message || 'Request failed';
}

function persistAuthDebugLog(level, label, payload = null) {
  if (typeof window === 'undefined') return;
  const entry = {
    ts: new Date().toISOString(),
    level,
    label,
    payload,
  };
  try {
    const raw = window.sessionStorage.getItem(AUTH_DEBUG_LOGS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const safe = Array.isArray(list) ? list : [];
    safe.push(entry);
    window.sessionStorage.setItem(AUTH_DEBUG_LOGS_KEY, JSON.stringify(safe.slice(-40)));
  } catch {
    /* ignore */
  }
}

function replayAuthDebugLogsToConsole() {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.sessionStorage.getItem(AUTH_DEBUG_LOGS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const safe = Array.isArray(list) ? list : [];
    if (safe.length === 0) return;
    safe.forEach((entry) => {
      const ts = entry?.ts ? `[${entry.ts}] ` : '';
      const label = entry?.label || 'auth_debug';
      const payload = entry?.payload;
      if (entry?.level === 'error') {
        console.error(`${ts}${label}`, payload ?? {});
      } else {
        console.log(`${ts}${label}`, payload ?? {});
      }
    });
    window.sessionStorage.removeItem(AUTH_DEBUG_LOGS_KEY);
  } catch {
    /* ignore */
  }
}

function calculateDeliveryFee(subtotal) {
  const n = Number(subtotal) || 0;
  if (n >= FREE_DELIVERY_THRESHOLD) return 0;
  return STANDARD_DELIVERY_CHARGE;
}

const REPORT_CHART_COLORS = [
  '#b88a36',
  '#deac4d',
  '#8b7355',
  '#5c5346',
  '#c4a574',
  '#d4c4a8',
  '#2d2926',
  '#e8bc66',
  '#a67c52',
  '#9a8b76',
];

function formatGBP(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '£0';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Match catalog `section` (string or array) to a shop / category slug. */
function productSectionsMatchCategorySlug(sectionField, categorySlug) {
  const arr = Array.isArray(sectionField)
    ? sectionField.map((s) => String(s || '').trim().toLowerCase()).filter(Boolean)
    : sectionField
      ? [String(sectionField).trim().toLowerCase()]
      : [];
  const slug = String(categorySlug || '').trim().toLowerCase();
  if (!slug) return false;
  if (slug === 'fragrance' || slug === 'fragrances') {
    return arr.some((s) => s === 'fragrance' || s === 'fragrances' || s === 'beauty');
  }
  return arr.includes(slug);
}

/** Normalized section slugs from a product `section` field (string or array). */
function getProductSectionSlugs(sectionField) {
  const arr = Array.isArray(sectionField)
    ? sectionField.map((s) => String(s || '').trim().toLowerCase()).filter(Boolean)
    : sectionField
      ? [String(sectionField).trim().toLowerCase()]
      : [];
  return arr.map((s) => (s === 'fragrances' ? 'fragrance' : s));
}

/** Shop listing: null/empty stock = not tracked — treat as in stock for filter counts and “in stock only”. */
function shopProductHasAvailableStock(item) {
  const s = item?.stock;
  if (s === null || s === undefined) return true;
  const n = Number(s);
  if (Number.isNaN(n)) return true;
  return n > 0;
}

function shopProductIsSoldOut(item) {
  const s = item?.stock;
  if (s === null || s === undefined) return false;
  const n = Number(s);
  if (Number.isNaN(n)) return false;
  return n <= 0;
}

function sanitizeProductCardTitle(name) {
  return String(name || 'Product')
    .replace(/\bleather\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function addProductToLocalCart(item) {
  const productId = item.id || null;
  const cartLineId = `${productId || slugifyCatalog(item.name)}::`;
  const name = item.name || 'Product';
  const price = Number(item.priceNumber ?? item.price) || 0;
  const image = item.image || '';
  const sku = item.sku && String(item.sku).trim() ? String(item.sku).trim() : null;
  try {
    const raw = window.localStorage.getItem(CUSTOMER_CART_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const cart = Array.isArray(list) ? list : [];
    const idx = cart.findIndex((row) => row?.id === cartLineId);
    if (idx >= 0) {
      cart[idx] = {
        ...cart[idx],
        qty: (Number(cart[idx].qty) || 0) + 1,
        price,
        image: image || cart[idx].image || '',
        name,
        sku: sku ?? cart[idx].sku ?? null,
        product_id: productId || cart[idx].product_id || null,
        variation: null,
      };
    } else {
      cart.push({
        id: cartLineId,
        product_id: productId,
        sku,
        name,
        price,
        qty: 1,
        image,
        variation: null,
      });
    }
    window.localStorage.setItem(CUSTOMER_CART_KEY, JSON.stringify(cart));
    window.dispatchEvent(new Event('cart-updated'));
  } catch {
    /* ignore */
  }
}

/** Cart line id prefix for quick-add cards (matches `addProductToLocalCart`). PDP lines use the same prefix plus a variation suffix after `::`. */
function catalogProductCartLineId(item) {
  const productId = item?.id || null;
  return `${productId || slugifyCatalog(item?.name || 'product')}::`;
}

function cartRowBaseCatalogLineId(rowId) {
  const s = String(rowId || '');
  const i = s.indexOf('::');
  if (i < 0) return s;
  return `${s.slice(0, i)}::`;
}

/** Base line ids (`productId::`) that appear in localStorage cart with qty > 0. */
function readLocalCartBaseLineIdsWithQty() {
  const set = new Set();
  try {
    const raw = window.localStorage.getItem(CUSTOMER_CART_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const cart = Array.isArray(list) ? list : [];
    cart.forEach((row) => {
      if ((Number(row?.qty) || 0) <= 0) return;
      const id = String(row?.id || '');
      if (!id) return;
      set.add(cartRowBaseCatalogLineId(id));
    });
  } catch {
    /* ignore */
  }
  return set;
}

function readGuestWishlistRows() {
  try {
    const raw = window.localStorage.getItem(GUEST_WISHLIST_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writeGuestWishlistRows(rows) {
  window.localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(rows));
  window.dispatchEvent(new Event('guest-wishlist-updated'));
}

function removeGuestWishlistProductRef(productRef) {
  const ref = String(productRef || '').trim();
  if (!ref) return;
  const next = readGuestWishlistRows().filter((row) => String(row.product_ref) !== ref);
  writeGuestWishlistRows(next);
}

function guestWishlistProductRefsSet() {
  return new Set(
    readGuestWishlistRows().map((row) => String(row?.product_ref || '').trim()).filter(Boolean)
  );
}

/**
 * @returns {Promise<{ kind: 'ok' | 'duplicate' | 'config' | 'error'; productRef?: string }>}
 */
function addGuestWishlistFromProduct(item) {
  const productRef = item.id ? String(item.id) : slugifyCatalog(item.name || 'product');
  if (!productRef) return { kind: 'error' };
  const rows = readGuestWishlistRows();
  if (rows.some((r) => String(r.product_ref) === productRef)) {
    return { kind: 'duplicate', productRef };
  }
  rows.unshift({
    product_ref: productRef,
    product_name: item.name || 'Product',
    product_price: Number(item.priceNumber ?? item.price) || 0,
    product_image: item.image || '',
    added_at: new Date().toISOString(),
  });
  writeGuestWishlistRows(rows);
  return { kind: 'ok', productRef };
}

/**
 * Signed-in users: Supabase. Guests: browser wishlist (same shape for the wishlist page).
 * @returns {Promise<{ kind: 'ok' | 'duplicate' | 'config' | 'error'; productRef?: string }>}
 */
async function wishlistProductFromCard(item) {
  const userEmail = (window.localStorage.getItem(CUSTOMER_EMAIL_KEY) || '').trim().toLowerCase();
  const loggedIn = Boolean(userEmail) && window.localStorage.getItem(CUSTOMER_SESSION_KEY) === 'true';
  if (!loggedIn) {
    return addGuestWishlistFromProduct(item);
  }
  if (!isSupabaseConfigured || !supabase) return { kind: 'config' };
  const productRef = item.id ? String(item.id) : slugifyCatalog(item.name || 'product');
  const productPrice = Number(item.priceNumber ?? item.price) || 0;
  const { error } = await supabase.from('wishlist_items').insert([
    {
      user_email: userEmail,
      product_ref: productRef,
      product_name: item.name || 'Product',
      product_price: productPrice,
      product_image: item.image || '',
    },
  ]);
  if (error) {
    const msg = String(error.message || '').toLowerCase();
    if (msg.includes('duplicate') || msg.includes('unique')) return { kind: 'duplicate', productRef };
    return { kind: 'error' };
  }
  try {
    window.dispatchEvent(new Event('wishlist-updated'));
  } catch {
    /* ignore */
  }
  return { kind: 'ok', productRef };
}

function ProductCardUi({
  item,
  productHref,
  priceDisplay,
  oldPriceDisplay = null,
  hoverImage = null,
  isWishlisted = false,
  isInCart = false,
  onWishlistClick,
  onAddToCartClick,
  hideWishlist = false,
  hideCart = false,
  footer = null,
}) {
  const title = sanitizeProductCardTitle(item.name);
  const hi = hoverImage && hoverImage !== item.image ? hoverImage : null;

  return (
    <article className="product-card-ui">
      <div className="product-card-ui-media">
        <a className="product-card-ui-img-link" href={productHref}>
          <div className="product-card-ui-img-frame">
            <img src={item.image} alt={title} className="img-main" loading="lazy" />
            {hi ? <img src={hi} alt="" className="img-hover" aria-hidden="true" /> : null}
          </div>
        </a>
        {!(hideWishlist && hideCart) ? (
          <div className="product-card-ui-fabs">
            {!hideWishlist ? (
              <button
                type="button"
                className={`product-card-ui-fab${isWishlisted ? ' is-saved' : ''}`}
                aria-label={isWishlisted ? 'In wishlist' : 'Add to wishlist'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onWishlistClick?.(e);
                }}
              >
                <FaHeart />
              </button>
            ) : null}
            {!hideCart ? (
              <button
                type="button"
                className={`product-card-ui-fab${isInCart ? ' is-in-cart' : ''}`}
                aria-label={isInCart ? 'In cart' : 'Add to cart'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAddToCartClick?.(e);
                }}
              >
                <FaShoppingBag aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="product-card-ui-body">
        <a className="product-card-ui-body-link" href={productHref}>
          <h3 className="product-card-ui-title">{title}</h3>
          <div className="product-card-ui-stars" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <FaStar key={i} className="product-card-ui-star" />
            ))}
          </div>
          <div className="product-card-ui-price-row">
            {oldPriceDisplay ? <span className="product-card-ui-old-price">{oldPriceDisplay}</span> : null}
            <span className="product-card-ui-price">{priceDisplay}</span>
          </div>
        </a>
        {footer ? <div className="product-card-ui-footer">{footer}</div> : null}
      </div>
    </article>
  );
}

const NAV_GROUPS = [
  {
    title: 'NEW IN',
    slug: 'new-in',
    links: ['Latest Arrivals', 'Runway Highlights', 'Editor Picks', 'Bestsellers'],
    feature: {
      title: 'The New Edit',
      image:
        'https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=900&q=80',
    },
  },
  {
    title: 'For Her',
    slug: 'for-her',
    links: ['Ready To Wear', 'Dresses', 'Bags', 'Shoes'],
    feature: {
      title: 'Modern Femininity',
      image:
        'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=900&q=80',
    },
  },
  {
    title: 'For Him',
    slug: 'for-him',
    links: ['Tailoring', 'Knitwear', 'Outerwear', 'Accessories'],
    feature: {
      title: 'Quiet Tailoring',
      image:
        'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=900&q=80',
    },
  },
  {
    title: 'Beauty',
    slug: 'beauty',
    links: ['Skincare', 'Beauty Sets', 'Travel Essentials', 'Makeup'],
    feature: {
      title: 'Modern Beauty',
      image:
        'https://images.unsplash.com/photo-1596462502278-27bfdc403348?auto=format&fit=crop&w=900&q=80',
    },
  },
  {
    title: 'Fragrances',
    slug: 'fragrance',
    links: ['Signature Scents', 'Fresh Notes', 'Woody Notes', 'Gift Sets'],
    feature: {
      title: 'Dstyluxe Scents',
      image:
        'https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=900&q=80',
    },
  },
  {
    title: 'Sunglasses',
    slug: 'trending',
    links: ['Cat Eye', 'Aviator', 'Square Frames', 'New Shapes'],
    feature: {
      title: 'Sun Edit',
      image:
        'https://images.unsplash.com/photo-1511499767150-a48a237f0083?auto=format&fit=crop&w=900&q=80',
    },
  },
  {
    title: 'Gifts',
    slug: 'trending',
    links: ['For Her', 'For Him', 'Beauty Gifts', 'Gift Cards'],
    feature: {
      title: 'Gift Atelier',
      image:
        'https://images.unsplash.com/photo-1512909006721-3d6018887383?auto=format&fit=crop&w=900&q=80',
    },
  },
  {
    title: 'Offers',
    slug: 'sale',
    links: ['Seasonal Offers', 'Limited Deals', 'Bundle Savings', 'Member Exclusive'],
    feature: {
      title: 'Private Offers',
      image:
        'https://images.unsplash.com/photo-1607082349566-187342175e2f?auto=format&fit=crop&w=900&q=80',
    },
  },
];

function SiteHeader() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newInDesktopOpen, setNewInDesktopOpen] = useState(false);
  const [mobileOpenSubmenuSlug, setMobileOpenSubmenuSlug] = useState('');
  const [menuCategories, setMenuCategories] = useState([]);
  const [menuLinksBySlug, setMenuLinksBySlug] = useState({});
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  useEffect(() => {
    setIsLoggedIn(window.localStorage.getItem(CUSTOMER_SESSION_KEY) === 'true');
  }, []);

  useEffect(() => {
    const readCount = () => {
      try {
        const raw = window.localStorage.getItem(CUSTOMER_CART_KEY);
        const list = raw ? JSON.parse(raw) : [];
        const total = Array.isArray(list)
          ? list.reduce((sum, item) => sum + Math.max(0, Number(item?.qty) || 0), 0)
          : 0;
        setCartCount(total);
      } catch {
        setCartCount(0);
      }
    };
    readCount();
    const onStorage = (event) => {
      if (!event || event.key === CUSTOMER_CART_KEY) readCount();
    };
    const onCartUpdated = () => readCount();
    window.addEventListener('storage', onStorage);
    window.addEventListener('cart-updated', onCartUpdated);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('cart-updated', onCartUpdated);
    };
  }, []);

  useEffect(() => {
    const refreshWishlistCount = async () => {
      const userEmail = (window.localStorage.getItem(CUSTOMER_EMAIL_KEY) || '').trim().toLowerCase();
      const loggedIn = Boolean(userEmail) && window.localStorage.getItem(CUSTOMER_SESSION_KEY) === 'true';
      if (!loggedIn) {
        try {
          setWishlistCount(readGuestWishlistRows().length);
        } catch {
          setWishlistCount(0);
        }
        return;
      }
      if (!isSupabaseConfigured || !supabase) {
        setWishlistCount(0);
        return;
      }
      const { count, error } = await supabase
        .from('wishlist_items')
        .select('id', { count: 'exact', head: true })
        .eq('user_email', userEmail);
      if (error) {
        setWishlistCount(0);
        return;
      }
      setWishlistCount(typeof count === 'number' ? count : 0);
    };

    void refreshWishlistCount();
    const onGuestWishlist = () => void refreshWishlistCount();
    const onWishlistUpdated = () => void refreshWishlistCount();
    const onVis = () => {
      if (document.visibilityState === 'visible') void refreshWishlistCount();
    };
    window.addEventListener('guest-wishlist-updated', onGuestWishlist);
    window.addEventListener('wishlist-updated', onWishlistUpdated);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('guest-wishlist-updated', onGuestWishlist);
      window.removeEventListener('wishlist-updated', onWishlistUpdated);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadMenuSubcategories() {
      if (!isSupabaseConfigured || !supabase) return;
      const [{ data: subcategoryData, error: subError }, { data: categoryData, error: catError }] =
        await Promise.all([
          supabase
            .from('subcategories')
            .select('name, slug, sort_order, categories(slug)')
            .order('sort_order', { ascending: true })
            .order('name', { ascending: true }),
          supabase
            .from('categories')
            .select('name, slug, image, sort_order')
            .order('sort_order', { ascending: true })
            .order('name', { ascending: true }),
        ]);
      if (cancelled) return;

      const next = {};
      (Array.isArray(subcategoryData) ? subcategoryData : []).forEach((row) => {
        const categorySlug = row?.categories?.slug;
        const label = typeof row?.name === 'string' ? row.name.trim() : '';
        const slug = typeof row?.slug === 'string' ? row.slug.trim() : '';
        if (!categorySlug || !label || !slug) return;
        if (!next[categorySlug]) next[categorySlug] = [];
        next[categorySlug].push({ name: label, slug });
      });
      if (!subError) setMenuLinksBySlug(next);
      if (!catError && Array.isArray(categoryData)) {
        setMenuCategories(categoryData.filter((c) => c?.slug && c?.name));
      }
    }

    loadMenuSubcategories();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    window.localStorage.removeItem(CUSTOMER_SESSION_KEY);
    window.localStorage.removeItem(CUSTOMER_EMAIL_KEY);
    window.localStorage.removeItem(CUSTOMER_NAME_KEY);
    setIsLoggedIn(false);
    window.location.href = '/';
  };

  const newInCategoryLinks = useMemo(() => {
    const base = menuCategories.length > 0
      ? menuCategories
      : NAV_GROUPS.map((group) => ({ name: group.title, slug: group.slug }));
    const seen = new Set();
    return base
      .map((row) => {
        const name = String(row?.name || row?.title || '').trim();
        const slug = String(row?.slug || '').trim().toLowerCase();
        return { name, slug };
      })
      .filter((row) => row.name && row.slug && row.slug !== 'new-in')
      .filter((row) => {
        if (seen.has(row.slug)) return false;
        seen.add(row.slug);
        return true;
      });
  }, [menuCategories]);

  const navGroups =
    menuCategories.length > 0
      ? menuCategories.map((c) => ({
          title: c.name,
          slug: c.slug,
          links: c.slug === 'new-in' ? newInCategoryLinks : (menuLinksBySlug[c.slug] || []),
        }))
      : NAV_GROUPS.map((group) => ({
          ...group,
          links: group.slug === 'new-in'
            ? newInCategoryLinks
            : (menuLinksBySlug[group.slug]?.length
              ? menuLinksBySlug[group.slug]
              : group.links.map((name) => ({ name, slug: slugifyCatalog(name) }))),
        }));

  useEffect(() => {
    if (!mobileMenuOpen) setMobileOpenSubmenuSlug('');
  }, [mobileMenuOpen]);

  return (
    <header className="site-header">
      <div className="header-top-strip" role="status" aria-live="polite">
        Free shipping on orders over {formatGBP(FREE_DELIVERY_THRESHOLD)}.
      </div>
      <div className="header-bar container">
        <a className="brand-mark brand-link" href="/" aria-label="Dstyluxe">
          <img
            className="header-logo"
            src="https://i.postimg.cc/DzSXQ0HK/Whats-App-Image-2026-03-31-at-5-48-43-PM-2-removebg-preview.png"
            alt="Dstyluxe"
          />
        </a>
        <nav className={`main-nav${mobileMenuOpen ? ' is-open' : ''}`} aria-label="Primary">
          <ul className="nav-list">
            {navGroups.map((group) => (
              <li className="nav-item" key={group.title}>
                {(() => {
                  const hasSubmenu = (group.links || []).length > 0;
                  const isMobileSubmenuOpen = mobileOpenSubmenuSlug === group.slug;
                  return (
                    <>
                {group.slug === 'new-in' ? (
                  <div className="desktop-nav-with-toggle">
                    <a
                      className="nav-trigger nav-link desktop-nav-trigger"
                      href={`/shop/${group.slug}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {group.title}
                    </a>
                    <button
                      type="button"
                      className={`desktop-nav-dropdown-toggle${newInDesktopOpen ? ' is-open' : ''}`}
                      aria-label={newInDesktopOpen ? 'Collapse New In menu' : 'Expand New In menu'}
                      aria-expanded={newInDesktopOpen}
                      onClick={(event) => {
                        event.preventDefault();
                        setNewInDesktopOpen((prev) => !prev);
                      }}
                    >
                      ▾
                    </button>
                  </div>
                ) : (
                  <a
                    className="nav-trigger nav-link desktop-nav-trigger"
                    href={`/shop/${group.slug}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {group.title}
                  </a>
                )}
                <div className="mobile-nav-row">
                  {hasSubmenu ? (
                    <div className="mobile-nav-with-toggle">
                      <a
                        className="mobile-nav-link"
                        href={`/shop/${group.slug}`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {group.title}
                      </a>
                      <button
                        type="button"
                        className={`mobile-nav-dropdown-toggle${isMobileSubmenuOpen ? ' is-open' : ''}`}
                        aria-label={isMobileSubmenuOpen ? `Collapse ${group.title} menu` : `Expand ${group.title} menu`}
                        aria-expanded={isMobileSubmenuOpen}
                        onClick={() =>
                          setMobileOpenSubmenuSlug((prev) => (prev === group.slug ? '' : group.slug))
                        }
                      >
                        ▾
                      </button>
                    </div>
                  ) : (
                    <a
                      className="mobile-nav-link"
                      href={`/shop/${group.slug}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {group.title}
                    </a>
                  )}
                </div>
                <div
                  id={`mobile-submenu-${group.slug}`}
                  className={`mobile-submenu${hasSubmenu ? ' is-collapsible' : ''}${hasSubmenu && isMobileSubmenuOpen ? ' is-open' : ''}`}
                >
                  {(group.links || []).map((link) => (
                    <a
                      href={`/shop/${group.slug}/${encodeURIComponent(link.slug)}`}
                      key={`m-${group.title}-${link.slug}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </a>
                  ))}
                </div>
                {group.slug === 'new-in' && (
                  <div className={`desktop-submenu${newInDesktopOpen ? ' is-open' : ''}`}>
                    {(group.links || []).map((link) => (
                      <a
                        href={`/shop/${group.slug}/${encodeURIComponent(link.slug)}`}
                        key={`d-${group.title}-${link.slug}`}
                        onClick={() => setNewInDesktopOpen(false)}
                      >
                        {link.name}
                      </a>
                    ))}
                  </div>
                )}
                    </>
                  );
                })()}
              </li>
            ))}
          </ul>
          <div className="mobile-menu-header-actions">
            <a
              className="mobile-nav-link mobile-menu-sale-link"
              href="/shop/sale"
              onClick={() => setMobileMenuOpen(false)}
            >
              Sale
            </a>
            {isLoggedIn ? (
              <button
                className="mobile-nav-link mobile-menu-auth-link"
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
              >
                Logout
              </button>
            ) : (
              <a
                className="mobile-nav-link mobile-menu-auth-link"
                href="/account"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign in
              </a>
            )}
          </div>
        </nav>
        <div className="header-actions">
          <a className="icon-label-btn icon-link desktop-sale-btn" href="/shop/sale">
            Sale
          </a>
          <a className="icon-label-btn icon-link header-account-link" href={isLoggedIn ? '/orders' : '/account'}>
            {isLoggedIn ? 'My Orders' : 'Account'}
          </a>
          {isLoggedIn && (
            <button className="header-logout-btn" type="button" onClick={handleLogout}>
              Logout
            </button>
          )}
          <a
            className="icon-btn icon-link wishlist-icon-link"
            href="/wishlist"
            aria-label={wishlistCount > 0 ? `Wishlist, ${wishlistCount} items` : 'Wishlist'}
          >
            <FaHeart aria-hidden size={18} />
            {wishlistCount > 0 && (
              <span className="cart-count-badge">{wishlistCount > 99 ? '99+' : wishlistCount}</span>
            )}
          </a>
          <a className="icon-btn icon-link cart-icon-link" href="/cart" aria-label="Shopping cart">
            <FaShoppingCart aria-hidden size={18} />
            {cartCount > 0 && <span className="cart-count-badge">{cartCount > 99 ? '99+' : cartCount}</span>}
          </a>
          <button
            className="icon-btn mobile-menu-toggle"
            type="button"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
          >
            {mobileMenuOpen ? (
              <span className="mobile-menu-close" aria-hidden>
                ×
              </span>
            ) : (
              <span className="mobile-menu-hamburger" aria-hidden />
            )}
          </button>
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  const [footerCategories, setFooterCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function loadFooterCategories() {
      if (!isSupabaseConfigured || !supabase) return;
      const { data, error } = await supabase
        .from('categories')
        .select('name, slug, sort_order')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (cancelled || error || !Array.isArray(data)) return;
      setFooterCategories(data);
    }

    loadFooterCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  const rawShopLinks = footerCategories.length > 0
    ? footerCategories.map((cat) => ({
      name: cat.name || 'Category',
      slug: cat.slug || slugifyCatalog(cat.name || 'category'),
    }))
    : [
      { name: 'NEW IN', slug: 'new-in' },
      { name: 'For Her', slug: 'for-her' },
      { name: 'For Him', slug: 'for-him' },
      { name: 'Beauty & Fragrance', slug: 'beauty' },
      { name: 'The Sunglasses', slug: 'sunglasses' },
      { name: 'Luxury Collection', slug: 'luxury-collection' },
      { name: 'Trending', slug: 'trending' },
      { name: 'SALE', slug: 'sale' },
    ];
  const shopLinks = useMemo(() => {
    const seen = new Set();
    return rawShopLinks.filter((link) => {
      const slug = String(link?.slug || '').trim().toLowerCase();
      if (!slug || seen.has(slug)) return false;
      seen.add(slug);
      return true;
    });
  }, [rawShopLinks]);

  return (
    <footer className="lux-footer">
      <div className="container footer-main">
        <div className="footer-brand">
          <a className="brand-mark brand-link" href="/" aria-label="Dstyluxe">
            <img
              className="footer-logo"
              src="https://i.postimg.cc/6pk9SQT6/Whats-App-Image-2026-03-31-at-8-20-03-PM.jpg"
              alt="Dstyluxe"
            />
          </a>
          <p>Modern luxury essentials crafted with timeless tailoring and refined detail.</p>
          <div className="footer-socials">
            <a href="https://www.instagram.com/dstyluxe/profilecard/?igsh=MXphNDhtanBmazZj" target="_blank" rel="noreferrer" aria-label="Instagram" title="Instagram"><FaInstagram aria-hidden size={14} /></a>
            <a href="https://x.com/dstyluxe?s=21&t=GvxtpNhTo7HH6qyEBYrl7g" target="_blank" rel="noreferrer" aria-label="X" title="X"><FaTwitter aria-hidden size={14} /></a>
            <a href="https://www.facebook.com/share/15qGFK47WZ/?mibextid=wwXIfr" target="_blank" rel="noreferrer" aria-label="Facebook" title="Facebook"><FaFacebookF aria-hidden size={14} /></a>
            <a href="https://uk.trustpilot.com/review/dstyluxe.co.uk" target="_blank" rel="noreferrer" aria-label="Trustpilot" title="Trustpilot"><FaStar aria-hidden size={14} /></a>
            <a href="https://pin.it/3ewGB0AEb" target="_blank" rel="noreferrer" aria-label="Pinterest" title="Pinterest"><FaPinterestP aria-hidden size={14} /></a>
            <a href="https://www.reddit.com/user/dstyluxe/?utm_source=share&utm_medium=mweb3x&utm_name=mweb3xcss&utm_term=1&utm_content=share_button" target="_blank" rel="noreferrer" aria-label="Reddit" title="Reddit"><FaRedditAlien aria-hidden size={14} /></a>
            <a href="https://www.tiktok.com/@dstyluxe?_t=ZG-8sqVi9X9WIS&_r=1" target="_blank" rel="noreferrer" aria-label="TikTok" title="TikTok"><FaTiktok aria-hidden size={14} /></a>
            <a href="https://www.linkedin.com/in/dstyluxe-fashion-and-beauty-6593b1324?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app" target="_blank" rel="noreferrer" aria-label="LinkedIn" title="LinkedIn"><FaLinkedinIn aria-hidden size={14} /></a>
          </div>
        </div>

        <div className="footer-links-col">
          <h4>Shop</h4>
          {shopLinks.map((link) => (
            <a key={link.slug} href={`/shop/${link.slug}`}>
              {link.name}
            </a>
          ))}
        </div>

        <div className="footer-links-col">
          <h4>Customer</h4>
          <a href="/profile">Account</a>
          <a href="/orders">Orders</a>
          <a href="/wishlist">Wishlist</a>
          <a href="/custom-services">Custom Services</a>
        </div>

        <div className="footer-links-col">
          <h4>Support</h4>
          <a href="/delivery-returns">Delivery &amp; Returns</a>
          <a href="/shipping-service">Shipping Service</a>
          <a href="/returns-policy">Returns Policy</a>
          <a href="/contact">Contact Us</a>
          <a href="/help-faq">Help / FAQ</a>
        </div>

        <div className="footer-links-col">
          <h4>Company</h4>
          <a href="/about-us">About Us</a>
          <a href="/privacy-policy">Privacy Policy</a>
          <a href="/terms">Terms</a>
        </div>

        <div className="footer-contact">
          <h4>Contact</h4>
          <div className="footer-contact-body">
            <p>38 Thrales Close, Luton, UK, LU3 3RS</p>
            <p>
              <a href="tel:03300168168">0330 0 168 168</a>
            </p>
            <p>
              <a href="mailto:contact@dstyluxe.co.uk">contact@dstyluxe.co.uk</a>
            </p>
          </div>
        </div>
      </div>

      <div className="container footer-bottom">
        <p>© 2026 Dstyluxe. All rights reserved.</p>
        <div className="footer-payments" aria-label="Payment methods">
          <span>VISA</span>
          <span>MC</span>
          <span>AMEX</span>
          <span>PAYPAL</span>
        </div>
      </div>
    </footer>
  );
}

function HomePage() {
  const [homeProducts, setHomeProducts] = useState([]);
  const [homeCategories, setHomeCategories] = useState([]);
  const [activeHomeSlide, setActiveHomeSlide] = useState(0);
  const [activeCategoryProductSlide, setActiveCategoryProductSlide] = useState(0);
  const [rangeProductSlidePageSize, setRangeProductSlidePageSize] = useState(() => {
    if (typeof window === 'undefined') return 4;
    return window.matchMedia('(max-width: 1000px)').matches ? 2 : 4;
  });
  const [homeToast, setHomeToast] = useState('');
  const [homeWishlisted, setHomeWishlisted] = useState(() => new Set());
  const [homeCartBaseLineIds, setHomeCartBaseLineIds] = useState(() => readLocalCartBaseLineIdsWithQty());

  useEffect(() => {
    replayAuthDebugLogsToConsole();
  }, []);

  const flashHomeToast = useCallback((message) => {
    setHomeToast(message);
    window.clearTimeout(window.__homeToastTimer);
    window.__homeToastTimer = window.setTimeout(() => setHomeToast(''), 2000);
  }, []);

  const refreshHomeWishlist = useCallback(async () => {
    const userEmail = (window.localStorage.getItem(CUSTOMER_EMAIL_KEY) || '').trim().toLowerCase();
    const loggedIn = Boolean(userEmail) && window.localStorage.getItem(CUSTOMER_SESSION_KEY) === 'true';
    if (!loggedIn) {
      setHomeWishlisted(guestWishlistProductRefsSet());
      return;
    }
    if (!isSupabaseConfigured || !supabase) return;
    const { data, error } = await supabase
      .from('wishlist_items')
      .select('product_ref')
      .eq('user_email', userEmail);
    if (error || !Array.isArray(data)) return;
    setHomeWishlisted(
      new Set(data.map((row) => String(row?.product_ref || '').trim()).filter(Boolean))
    );
  }, []);

  useEffect(() => {
    refreshHomeWishlist();
  }, [refreshHomeWishlist]);

  useEffect(() => {
    const onGuestWishlist = () => refreshHomeWishlist();
    window.addEventListener('guest-wishlist-updated', onGuestWishlist);
    return () => window.removeEventListener('guest-wishlist-updated', onGuestWishlist);
  }, [refreshHomeWishlist]);

  const refreshHomeCartLineIds = useCallback(() => {
    setHomeCartBaseLineIds(readLocalCartBaseLineIdsWithQty());
  }, []);

  useEffect(() => {
    refreshHomeCartLineIds();
    const onCart = () => refreshHomeCartLineIds();
    const onStorage = (event) => {
      if (!event || event.key === CUSTOMER_CART_KEY) refreshHomeCartLineIds();
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshHomeCartLineIds();
    };
    window.addEventListener('cart-updated', onCart);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('cart-updated', onCart);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refreshHomeCartLineIds]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1000px)');
    const sync = () => setRangeProductSlidePageSize(mq.matches ? 2 : 4);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const handleHomeWishlist = useCallback(
    async (item) => {
      const result = await wishlistProductFromCard(item);
      if (result.kind === 'config') {
        flashHomeToast('Supabase not configured');
        return;
      }
      if (result.kind === 'duplicate') {
        if (result.productRef) setHomeWishlisted((prev) => new Set(prev).add(result.productRef));
        flashHomeToast('Already in wishlist');
        return;
      }
      if (result.kind === 'error') {
        flashHomeToast('Could not add to wishlist');
        return;
      }
      if (result.kind === 'ok' && result.productRef) {
        setHomeWishlisted((prev) => new Set(prev).add(result.productRef));
        flashHomeToast('Added to wishlist');
      }
    },
    [flashHomeToast]
  );

  const handleHomeAddToCart = useCallback(
    (item) => {
      addProductToLocalCart(item);
      flashHomeToast('Product added to cart');
    },
    [flashHomeToast]
  );

  const homeSlides = [
    {
      image: 'https://i.postimg.cc/QC3bBqdm/SUNGLASSES.png',
      alt: 'Dstyluxe sunglasses collection banner',
    },
    {
      image: 'https://i.postimg.cc/L6fVtw1V/SUNGLASSES-(3).png',
      alt: 'Dstyluxe timeless fragrances banner',
    },
    {
      image: 'https://i.postimg.cc/FHVgqSGB/SUNGLASSES-(4).png',
      alt: 'Dstyluxe women collection banner',
    },
  ];

  useEffect(() => {
    let cancelled = false;
    async function loadHomeData() {
      if (!isSupabaseConfigured || !supabase) return;
      const [{ data: productData, error: productError }, { data: categoryData, error: categoryError }] =
        await Promise.all([
          supabase
            .from('products')
            .select('id, name, price, section, image, sku, created_at')
            .order('created_at', { ascending: false }),
          supabase
            .from('categories')
            .select('id, name, slug, image, sort_order')
            .order('sort_order', { ascending: true })
            .order('name', { ascending: true }),
        ]);
      if (cancelled) return;
      if (!productError && Array.isArray(productData)) {
        setHomeProducts(productData);
      }
      if (!categoryError && Array.isArray(categoryData)) {
        setHomeCategories(categoryData);
      }
    }
    loadHomeData();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const slideTimer = window.setInterval(() => {
      setActiveHomeSlide((prev) => (prev + 1) % homeSlides.length);
    }, 4500);
    return () => window.clearInterval(slideTimer);
  }, [homeSlides.length]);

  const showcaseCategories = {
    forHer: {
      title: 'For Her',
      slug: 'for-her',
      image: 'https://i.postimg.cc/Fsp39v5p/Untitled-design-(69).png',
    },
    forHim: {
      title: 'For Him',
      slug: 'for-him',
      image: 'https://i.postimg.cc/9FwH2mqP/Untitled-design-(81).png',
    },
    fragrance: {
      title: 'Fragrance',
      slug: 'fragrance',
      image: 'https://i.postimg.cc/7ZpgRVrV/Untitled-design-(82).png',
    },
    sunglasses: {
      title: 'Sunglasses',
      slug: 'sunglasses',
      image: 'https://i.postimg.cc/2SBWnZLZ/Untitled-design-(83).png',
    },
    gifts: {
      title: 'Gifts',
      slug: 'gifts',
      image: 'https://i.postimg.cc/0NszmHsN/Untitled-design-(84).png',
    },
  };

  const productRangeCategories = useMemo(() => {
    const fallback = [
      { name: 'For Her', slug: 'for-her' },
      { name: 'For Him', slug: 'for-him' },
      { name: 'Beauty', slug: 'beauty' },
      { name: 'Fragrance', slug: 'fragrance' },
      { name: 'Sunglasses', slug: 'sunglasses' },
      { name: 'Gifts', slug: 'gifts' },
    ];
    const source =
      homeCategories.length > 0
        ? homeCategories
            .filter((cat) => cat?.slug)
            .map((cat) => ({
              name: cat.name || humanizeCatalogSlug(cat.slug),
              slug: cat.slug,
            }))
        : fallback;
    const alias = { fragrances: 'fragrance' };
    const seen = new Set();
    return source
      .map((cat) => ({ ...cat, slug: alias[cat.slug] || cat.slug }))
      .filter((cat) => {
        if (!cat.slug || seen.has(cat.slug)) return false;
        seen.add(cat.slug);
        return true;
      });
  }, [homeCategories]);

  const trending = useMemo(() => {
    if (homeProducts.length === 0) return [];
    const cleaned = homeProducts.filter((p) => !/leather/i.test(String(p?.name || '')));
    const pool = cleaned.length > 0 ? cleaned : homeProducts;
    const perCategory = 3;
    const items = [];
    productRangeCategories.forEach((cat) => {
      pool
        .filter((p) => productSectionsMatchCategorySlug(p?.section, cat.slug))
        .slice(0, perCategory)
        .forEach((p) => {
          items.push({
            id: p.id,
            name: p.name,
            price: formatGBP(p.price || 0),
            priceNumber: Number(p.price) || 0,
            sku: p.sku && String(p.sku).trim() ? String(p.sku).trim() : '',
            image:
              p.image ||
              'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=900&q=80',
          });
        });
    });
    return items;
  }, [homeProducts, productRangeCategories]);

  const productRangeItems = useMemo(() => {
    if (homeProducts.length === 0) return [];
    const cleaned = homeProducts.filter((p) => !/leather/i.test(String(p?.name || '')));
    const pool = cleaned.length > 0 ? cleaned : homeProducts;
    const items = [];
    productRangeCategories.forEach((cat) => {
      const matches = pool
        .filter((p) => productSectionsMatchCategorySlug(p?.section, cat.slug))
        .slice(0, 2)
        .map((p) => ({
          id: p.id,
          name: p.name,
          price: formatGBP(p.price || 0),
          priceNumber: Number(p.price) || 0,
          sku: p.sku && String(p.sku).trim() ? String(p.sku).trim() : '',
          image:
            p.image ||
            'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=900&q=80',
        }));
      items.push(...matches);
    });
    return items;
  }, [homeProducts, productRangeCategories]);

  /** Wide: 4 per slide (one row); narrow: 2 per slide (one row) — matches `.home-product-range-slide` grid. */
  const rangeSliderPages = useMemo(() => {
    if (productRangeItems.length === 0) return [];
    const step = rangeProductSlidePageSize;
    const pages = [];
    for (let i = 0; i < productRangeItems.length; i += step) {
      pages.push(productRangeItems.slice(i, i + step));
    }
    return pages;
  }, [productRangeItems, rangeProductSlidePageSize]);

  useEffect(() => {
    setActiveCategoryProductSlide(0);
  }, [rangeSliderPages.length, rangeProductSlidePageSize]);

  useEffect(() => {
    if (rangeSliderPages.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setActiveCategoryProductSlide((prev) => (prev + 1) % rangeSliderPages.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [rangeSliderPages.length]);

  return (
    <div className="App">
      <SiteHeader />

      <main>
        <section className="home-slider" aria-label="Featured promotions">
          <div className="home-slider-track">
            {homeSlides.map((slide, idx) => (
              <article
                className={`home-slide ${idx === activeHomeSlide ? 'is-active' : ''}`}
                aria-hidden={idx !== activeHomeSlide}
                key={slide.image}
              >
                <img src={slide.image} alt={slide.alt} loading={idx === 0 ? 'eager' : 'lazy'} />
              </article>
            ))}
          </div>
          <div className="home-slider-dots" role="tablist" aria-label="Choose featured slide">
            {homeSlides.map((slide, idx) => (
              <button
                type="button"
                key={`${slide.image}-dot`}
                className={`home-slider-dot ${idx === activeHomeSlide ? 'is-active' : ''}`}
                onClick={() => setActiveHomeSlide(idx)}
                aria-label={`Go to slide ${idx + 1}`}
                aria-selected={idx === activeHomeSlide}
                role="tab"
              />
            ))}
          </div>
        </section>

        <section className="home-category-showcase section-space soft-bg">
          <div className="container">
            <div className="home-category-showcase-head">
              <h2>Shop by Category</h2>
            </div>
            <div className="home-category-layout">
              <a className="home-category-card home-category-card--for-her" href={`/shop/${showcaseCategories.forHer.slug}`}>
                <img src={showcaseCategories.forHer.image} alt={showcaseCategories.forHer.title} />
                <h3>{showcaseCategories.forHer.title}</h3>
              </a>

              <a className="home-category-card home-category-card--for-him" href={`/shop/${showcaseCategories.forHim.slug}`}>
                <img src={showcaseCategories.forHim.image} alt={showcaseCategories.forHim.title} />
                <h3>{showcaseCategories.forHim.title}</h3>
              </a>

              <a
                className="home-category-card home-category-card--fragrance home-category-card--center"
                href={`/shop/${showcaseCategories.fragrance.slug}`}
              >
                <img src={showcaseCategories.fragrance.image} alt={showcaseCategories.fragrance.title} />
                <h3>{showcaseCategories.fragrance.title}</h3>
              </a>

              <a
                className="home-category-card home-category-card--sunglasses"
                href={`/shop/${showcaseCategories.sunglasses.slug}`}
              >
                <img src={showcaseCategories.sunglasses.image} alt={showcaseCategories.sunglasses.title} />
                <h3>{showcaseCategories.sunglasses.title}</h3>
              </a>

              <a className="home-category-card home-category-card--gifts" href={`/shop/${showcaseCategories.gifts.slug}`}>
                <img src={showcaseCategories.gifts.image} alt={showcaseCategories.gifts.title} />
                <h3>{showcaseCategories.gifts.title}</h3>
              </a>
            </div>
          </div>
        </section>

        {productRangeItems.length > 0 ? (
          <section className="section-space home-product-range">
            <div className="container">
              <div className="home-product-range-head">
                <h2>Explore Our Product Range</h2>
                <p>
                  Curated pieces that blend timeless craftsmanship with a modern eye—made to feel special, every day.
                </p>
              </div>

              <div className="home-product-range-slider" aria-label="Product range">
                <div
                  className="home-product-range-slider-track"
                  style={{ transform: `translateX(-${activeCategoryProductSlide * 100}%)` }}
                >
                  {rangeSliderPages.map((page, pageIndex) => (
                    <div className="home-product-range-slide" key={`range-slide-${pageIndex}`}>
                      {page.map((item) => {
                        const wishlistRef = item.id ? String(item.id) : slugifyCatalog(item.name || 'product');
                        return (
                          <ProductCardUi
                            key={`${item.id || item.name}-${pageIndex}`}
                            item={{
                              id: item.id,
                              name: item.name,
                              image: item.image,
                              priceNumber: item.priceNumber,
                              sku: item.sku,
                            }}
                            productHref={`/product/${encodeURIComponent(item.id || slugifyCatalog(item.name))}`}
                            priceDisplay={item.price}
                            isWishlisted={homeWishlisted.has(wishlistRef)}
                            isInCart={homeCartBaseLineIds.has(
                              catalogProductCartLineId({
                                id: item.id,
                                name: item.name,
                              })
                            )}
                            onWishlistClick={() => {
                              void handleHomeWishlist({
                                id: item.id,
                                name: item.name,
                                image: item.image,
                                priceNumber: item.priceNumber,
                                sku: item.sku,
                              });
                            }}
                            onAddToCartClick={() =>
                              handleHomeAddToCart({
                                id: item.id,
                                name: item.name,
                                image: item.image,
                                priceNumber: item.priceNumber,
                                sku: item.sku,
                              })
                            }
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>

                {rangeSliderPages.length > 1 ? (
                  <div className="home-product-range-dots" role="tablist" aria-label="Choose products slide">
                    {rangeSliderPages.map((_, idx) => (
                      <button
                        type="button"
                        key={`range-dot-${idx}`}
                        className={`home-product-range-dot ${idx === activeCategoryProductSlide ? 'is-active' : ''}`}
                        onClick={() => setActiveCategoryProductSlide(idx)}
                        aria-label={`Go to products slide ${idx + 1}`}
                        aria-selected={idx === activeCategoryProductSlide}
                        role="tab"
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        ) : null}

        {trending.length > 0 ? (
          <section className="section-space">
            <div className="container">
              <div className="section-heading">
                <p className="eyebrow">Trending</p>
                <h2>Trending Products</h2>
              </div>
              <div className="product-grid">
                {trending.map((item) => {
                  const wishlistRef = item.id ? String(item.id) : slugifyCatalog(item.name || 'product');
                  return (
                    <ProductCardUi
                      key={item.id || item.name}
                      item={{
                        id: item.id,
                        name: item.name,
                        image: item.image,
                        priceNumber: item.priceNumber,
                        sku: item.sku,
                      }}
                      productHref={`/product/${encodeURIComponent(item.id || slugifyCatalog(item.name))}`}
                      priceDisplay={item.price}
                      isWishlisted={homeWishlisted.has(wishlistRef)}
                      isInCart={homeCartBaseLineIds.has(
                        catalogProductCartLineId({
                          id: item.id,
                          name: item.name,
                        })
                      )}
                      onWishlistClick={() => {
                        void handleHomeWishlist({
                          id: item.id,
                          name: item.name,
                          image: item.image,
                          priceNumber: item.priceNumber,
                          sku: item.sku,
                        });
                      }}
                      onAddToCartClick={() =>
                        handleHomeAddToCart({
                          id: item.id,
                          name: item.name,
                          image: item.image,
                          priceNumber: item.priceNumber,
                          sku: item.sku,
                        })
                      }
                    />
                  );
                })}
              </div>
            </div>
          </section>
        ) : null}

        <section className="section-space gifting-section">
          <div className="container gifting-grid">
            <article className="gifting-card gifting-primary">
              <p className="eyebrow">Gift Concierge</p>
              <h2>Luxury Gifting, Personally Curated</h2>
              <p>
                From elegant accessories to signature beauty sets, discover thoughtful gifts
                wrapped with premium presentation and concierge care.
              </p>
              <a className="primary-btn gifting-link" href="/shop/gifts">Discover Gifts</a>
            </article>
            <article className="gifting-card gifting-visual">
              <div className="gifting-image" />
            </article>
            <article className="gifting-card gifting-note">
              <h3>Private Wrapping & Notes</h3>
              <p>Complimentary luxury packaging and personalized message cards for every order.</p>
            </article>
          </div>
        </section>

        <section className="section-space story-manifesto">
          <div className="container manifesto-grid">
            <div className="manifesto-visual" />
            <article className="manifesto-copy">
              <p className="eyebrow">Dstyluxe Story</p>
              <h2>Wardrobe, fragrance, beauty, and more</h2>
              <p>
                Shop for her and for him, explore the luxury collection, and browse fragrance, beauty,
                sunglasses, and gifts—everything we stock is chosen to feel premium, wearable, and easy to
                love.
              </p>
              <ul className="manifesto-points">
                <li>Fashion and luxury pieces for everyday and special moments</li>
                <li>Fragrance and beauty for routines that feel elevated</li>
                <li>Sunglasses and gifts with standout finishing touches</li>
              </ul>
              <a className="ghost-btn story-link" href="/shop/new-in">Explore new in</a>
            </article>
          </div>
        </section>

        <section className="section-space newsletter">
          <div className="container newsletter-inner">
            <h2>Join the Private List</h2>
            <p>Receive early access to drops, styling notes, and exclusive invitations.</p>
            <form className="newsletter-form">
              <input type="email" placeholder="Enter your email" aria-label="Email address" />
              <button className="primary-btn" type="submit">Subscribe</button>
            </form>
          </div>
        </section>
      </main>

      {homeToast ? (
        <div className="lux-toast" role="status" aria-live="polite">
          <span>✓</span> {homeToast}
        </div>
      ) : null}

      <SiteFooter />
    </div>
  );
}

function ShopPage({ sectionSlug, subcategorySlug = '' }) {
  const [sortBy, setSortBy] = useState('newest');
  const [visibleCount, setVisibleCount] = useState(8);
  const [dbProducts, setDbProducts] = useState([]);
  const [shopToast, setShopToast] = useState('');
  const [wishlistedRefs, setWishlistedRefs] = useState(() => new Set());
  const [shopCartBaseLineIds, setShopCartBaseLineIds] = useState(() => readLocalCartBaseLineIdsWithQty());
  const [shopProductsLoading, setShopProductsLoading] = useState(() =>
    Boolean(isSupabaseConfigured && supabase)
  );

  const refreshShopWishlistRefs = useCallback(async () => {
    const userEmail = (window.localStorage.getItem(CUSTOMER_EMAIL_KEY) || '').trim().toLowerCase();
    const loggedIn = Boolean(userEmail) && window.localStorage.getItem(CUSTOMER_SESSION_KEY) === 'true';
    if (!loggedIn) {
      setWishlistedRefs(guestWishlistProductRefsSet());
      return;
    }
    if (!isSupabaseConfigured || !supabase) return;
    const { data, error } = await supabase
      .from('wishlist_items')
      .select('product_ref')
      .eq('user_email', userEmail);
    if (error || !Array.isArray(data)) return;
    setWishlistedRefs(
      new Set(data.map((row) => String(row?.product_ref || '').trim()).filter(Boolean))
    );
  }, []);

  useEffect(() => {
    refreshShopWishlistRefs();
  }, [refreshShopWishlistRefs]);

  useEffect(() => {
    const onGuestWishlist = () => refreshShopWishlistRefs();
    window.addEventListener('guest-wishlist-updated', onGuestWishlist);
    return () => window.removeEventListener('guest-wishlist-updated', onGuestWishlist);
  }, [refreshShopWishlistRefs]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshShopWishlistRefs();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshShopWishlistRefs]);

  const refreshShopCartLineIds = useCallback(() => {
    setShopCartBaseLineIds(readLocalCartBaseLineIdsWithQty());
  }, []);

  useEffect(() => {
    refreshShopCartLineIds();
    const onCart = () => refreshShopCartLineIds();
    const onStorage = (event) => {
      if (!event || event.key === CUSTOMER_CART_KEY) refreshShopCartLineIds();
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshShopCartLineIds();
    };
    window.addEventListener('cart-updated', onCart);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('cart-updated', onCart);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refreshShopCartLineIds]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPriceRanges, setSelectedPriceRanges] = useState([]);
  const [saleOnly, setSaleOnly] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [newArrivalsOnly, setNewArrivalsOnly] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [outOfStockOnly, setOutOfStockOnly] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const shopViewLayout = 'two';
  const [mobilePriceRange, setMobilePriceRange] = useState(null);
  const [drawerPriceMin, setDrawerPriceMin] = useState(0);
  const [drawerPriceMax, setDrawerPriceMax] = useState(0);

  const config = {
    'new-in': {
      title: 'New In',
      subtitle: `Newest pieces first — up to ${SHOP_NEW_IN_PER_CATEGORY_MAX} per category (For Her, For Him, Beauty, Fragrance, and more) from the last ${SHOP_NEW_IN_RECENT_DAYS} days, plus items tagged New In.`,
    },
    sale: { title: 'Sale', subtitle: 'Rare finds with refined discounts.' },
    trending: { title: 'Trending', subtitle: 'Most desired styles across the maison.' },
    'luxury-collection': { title: 'Luxury Collection', subtitle: 'Signature pieces crafted in limited runs.' },
    'for-her': { title: 'For Her', subtitle: 'Modern silhouettes designed for timeless elegance.' },
    'for-him': { title: 'For Him', subtitle: 'Tailored essentials with contemporary precision.' },
    beauty: { title: 'Beauty', subtitle: 'Scent and skincare rituals in premium formulations.' },
    fragrance: { title: 'Fragrance', subtitle: 'Signature scents and olfactory rituals.' },
    fragrances: { title: 'Fragrances', subtitle: 'Signature scents and olfactory rituals.' },
    sunglasses: { title: 'Sunglasses', subtitle: 'Refined eyewear for every season.' },
    gifts: { title: 'Gifts', subtitle: 'Curated gifts with premium presentation.' },
  };

  const sectionAliasMap = {
    fragrances: 'fragrance',
  };
  const normalizedSectionSlug = sectionAliasMap[sectionSlug] || sectionSlug;
  const currentSection = config[normalizedSectionSlug] ? normalizedSectionSlug : 'for-her';
  const currentSubcategorySlug = safeDecodeURIComponent(subcategorySlug || '').trim().toLowerCase();
  const currentSubcategoryDisplay = currentSubcategorySlug
    ? humanizeCatalogSlug(currentSubcategorySlug)
    : '';

  useEffect(() => {
    setVisibleCount(currentSection === 'new-in' ? 48 : 8);
    setSearchQuery('');
    setSelectedPriceRanges([]);
    setSaleOnly(false);
    setInStockOnly(false);
    setNewArrivalsOnly(false);
    setSelectedBrands([]);
    setSelectedTags([]);
    setOutOfStockOnly(false);
    setMobilePriceRange(null);
    setMobileFilterOpen(false);
  }, [currentSection]);

  useEffect(() => {
    let cancelled = false;
    async function loadShopProducts() {
      if (!isSupabaseConfigured || !supabase) {
        setShopProductsLoading(false);
        return;
      }
      setShopProductsLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select(
          'id, name, price, compare_at_price, section, sub_category, image, gallery_images, created_at, sku, stock, brand, tags, status'
        )
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error || !Array.isArray(data)) {
        setDbProducts([]);
        setShopProductsLoading(false);
        return;
      }
      const mapped = data.map((row) => {
        const fallbackImage =
          'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=900&q=80';
        const mainImage = row.image || fallbackImage;
        const gallery = Array.isArray(row.gallery_images)
          ? row.gallery_images.filter((url) => typeof url === 'string' && url.trim())
          : [];
        // Prefer a different gallery image for hover so the hover effect is visible.
        const hoverFromGallery = gallery.find((url) => url !== mainImage) || gallery[0];
        return {
          id: row.id,
          name: row.name,
          price: Number(row.price) || 0,
          oldPrice: row.compare_at_price != null ? Number(row.compare_at_price) : null,
          popularity: 0,
          section: [row.section].filter(Boolean),
          sub_category: row.sub_category || '',
          image: mainImage,
          hoverImage: hoverFromGallery || mainImage,
          sku: row.sku ? String(row.sku).trim() : '',
          stock: (() => {
            const v = row.stock;
            if (v === null || v === undefined) return null;
            if (typeof v === 'string' && v.trim() === '') return null;
            const n = Number(v);
            return Number.isNaN(n) ? null : n;
          })(),
          brand: (row.brand && String(row.brand).trim()) || '',
          tags: (row.tags && String(row.tags).trim()) || '',
          status: (row.status && String(row.status).trim()) || 'active',
          created_at: row.created_at || null,
        };
      });
      setDbProducts(mapped);
      setShopProductsLoading(false);
    }
    loadShopProducts();
    return () => {
      cancelled = true;
    };
  }, []);

  const localProducts = useMemo(
    () =>
      [
        { name: 'Silk Evening Dress', price: 1290, oldPrice: null, popularity: 90, section: ['new-in', 'for-her', 'trending'], sub_category: 'Dresses', sku: 'DL-HER-001', image: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1464863979621-258859e62245?auto=format&fit=crop&w=900&q=80' },
        { name: 'Structured Leather Bag', price: 1540, oldPrice: null, popularity: 84, section: ['new-in', 'for-her', 'luxury-collection'], sub_category: 'Bags', sku: 'DL-HER-002', image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1594633313593-bab3825d0caf?auto=format&fit=crop&w=900&q=80' },
        { name: 'Tailored Wool Blazer', price: 980, oldPrice: null, popularity: 88, section: ['for-him', 'trending'], sub_category: 'Tailoring', sku: 'DL-HIM-001', image: 'https://images.unsplash.com/photo-1617127365659-c47fa864d8bc?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1618886614638-80e3c103d31a?auto=format&fit=crop&w=900&q=80' },
        { name: 'Signature Fragrance 100ml', price: 230, oldPrice: null, popularity: 95, section: ['beauty', 'trending', 'new-in'], sub_category: 'Fragrance', sku: 'DL-BEA-001', image: 'https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1594035910387-fea47794261f?auto=format&fit=crop&w=900&q=80' },
        { name: 'Minimal Gold Sunglasses', price: 420, oldPrice: 560, popularity: 91, section: ['sale', 'trending'], sub_category: 'Accessories', sku: 'DL-ACC-001', image: 'https://images.unsplash.com/photo-1511499767150-a48a237f0083?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1577803645773-f96470509666?auto=format&fit=crop&w=900&q=80' },
        { name: 'Cashmere Long Coat', price: 2240, oldPrice: null, popularity: 79, section: ['for-her', 'luxury-collection'], sub_category: 'Outerwear', sku: 'DL-HER-003', image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=crop&w=900&q=80' },
        { name: 'Monogram Card Holder', price: 340, oldPrice: 460, popularity: 73, section: ['sale', 'for-him'], sub_category: 'Accessories', sku: 'DL-HIM-002', image: 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1559563458-527698bf5295?auto=format&fit=crop&w=900&q=80' },
        { name: 'Velvet Evening Heel', price: 890, oldPrice: null, popularity: 80, section: ['for-her', 'new-in'], sub_category: 'Shoes', sku: 'DL-HER-004', image: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1525966222134-fcfa99b8ae77?auto=format&fit=crop&w=900&q=80' },
        { name: 'Limited Atelier Gown', price: 3190, oldPrice: null, popularity: 99, section: ['luxury-collection', 'for-her'], sub_category: 'Dresses', sku: 'DL-LUX-001', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1529635436167-b5e513f4a4f0?auto=format&fit=crop&w=900&q=80' },
        { name: 'Reserve Tailoring Suit', price: 2590, oldPrice: null, popularity: 89, section: ['luxury-collection', 'for-him'], sub_category: 'Tailoring', sku: 'DL-LUX-002', image: 'https://images.unsplash.com/photo-1593032465171-8bd47f6f4d58?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1487222477894-8943e31ef7b2?auto=format&fit=crop&w=900&q=80' },
        { name: 'Travel Beauty Ritual Set', price: 180, oldPrice: 250, popularity: 75, section: ['sale', 'beauty'], sub_category: 'Skincare', sku: 'DL-BEA-002', image: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1498843053639-170ff2122f35?auto=format&fit=crop&w=900&q=80' },
        { name: 'Fine Knit Polo', price: 490, oldPrice: null, popularity: 82, section: ['for-him', 'new-in'], sub_category: 'Knitwear', sku: 'DL-HIM-003', image: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?auto=format&fit=crop&w=900&q=80', hoverImage: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=900&q=80' },
      ].map((p, idx) => ({
        stock: idx === 5 ? 0 : 14,
        brand: ['Dstyluxe', 'Atelier', 'Edition'][idx % 3],
        tags: ['evening', 'tailored; gift', 'beauty', 'accessories', ''][idx % 5],
        status: 'active',
        created_at: idx < 5 ? '2026-04-09T10:00:00.000Z' : '2025-10-15T10:00:00.000Z',
        ...p,
      })),
    []
  );

  // With Supabase: never flash dummy localProducts — wait for API (shopProductsLoading).
  const sourceProducts = isSupabaseConfigured && supabase ? dbProducts : localProducts;
  const sectionProducts = useMemo(() => {
    const active = sourceProducts.filter((item) => !item.status || item.status === 'active');
    if (currentSection !== 'new-in') {
      return active.filter((item) => productSectionsMatchCategorySlug(item.section, currentSection));
    }
    const cutoff = Date.now() - SHOP_NEW_IN_RECENT_DAYS * 864e5;
    return active.filter((item) => {
      if (item.section.includes('new-in')) return true;
      if (!item.created_at) return false;
      const ts = new Date(item.created_at).getTime();
      return !Number.isNaN(ts) && ts >= cutoff;
    });
  }, [sourceProducts, currentSection]);
  const scopedProducts = useMemo(() => {
    if (!currentSubcategorySlug) return sectionProducts;
    if (currentSection === 'new-in') {
      return sectionProducts.filter((item) =>
        productSectionsMatchCategorySlug(item.section, currentSubcategorySlug)
      );
    }
    return sectionProducts.filter((item) => {
      const raw = (item.sub_category || '').trim();
      if (!raw) return false;
      return slugifyCatalog(raw) === currentSubcategorySlug;
    });
  }, [sectionProducts, currentSubcategorySlug, currentSection]);

  const brandOptions = useMemo(() => {
    const set = new Set();
    scopedProducts.forEach((item) => {
      const b = (item.brand || '').trim();
      if (b) set.add(b);
    });
    return Array.from(set).sort((a, x) => a.localeCompare(x));
  }, [scopedProducts]);

  const tagOptions = useMemo(() => {
    const map = new Map();
    scopedProducts.forEach((item) => {
      splitProductTags(item.tags).forEach((t) => {
        const key = t.toLowerCase();
        if (!map.has(key)) map.set(key, t);
      });
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  }, [scopedProducts]);

  const shopPriceExtent = useMemo(() => {
    const prices = scopedProducts.map((p) => Number(p.price) || 0);
    if (prices.length === 0) return { min: 0, max: 0 };
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [scopedProducts]);

  const passesNonAvailabilityFilters = useCallback(
    (item) => {
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        const hay = `${item.name || ''} ${item.sku || ''} ${item.brand || ''} ${item.tags || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (newArrivalsOnly) {
        if (!item.created_at) return false;
        const ts = new Date(item.created_at).getTime();
        if (Number.isNaN(ts)) return false;
        if (Date.now() - ts > SHOP_NEW_ARRIVAL_DAYS * 864e5) return false;
      }
      if (selectedBrands.length > 0) {
        const b = (item.brand || '').trim();
        if (!b || !selectedBrands.some((sel) => b.toLowerCase() === sel.toLowerCase())) return false;
      }
      if (selectedTags.length > 0) {
        const tagsLower = splitProductTags(item.tags).map((t) => t.toLowerCase());
        const tagOk = selectedTags.some((sel) => tagsLower.includes(sel.toLowerCase()));
        if (!tagOk) return false;
      }
      if (saleOnly) {
        const markedDown =
          item.oldPrice != null && Number(item.oldPrice) > Number(item.price);
        if (!markedDown) return false;
      }
      if (selectedPriceRanges.length > 0) {
        const price = Number(item.price) || 0;
        const matchesAny = selectedPriceRanges.some((rangeId) => {
          const bucket = SHOP_PRICE_BUCKETS.find((b) => b.id === rangeId);
          return bucket ? bucket.test(price) : false;
        });
        if (!matchesAny) return false;
      }
      if (mobilePriceRange) {
        const pr = Number(item.price) || 0;
        if (pr < mobilePriceRange.min || pr > mobilePriceRange.max) return false;
      }
      return true;
    },
    [
      searchQuery,
      newArrivalsOnly,
      selectedBrands,
      selectedTags,
      saleOnly,
      selectedPriceRanges,
      mobilePriceRange,
    ]
  );

  const poolForStockCounts = useMemo(
    () => scopedProducts.filter((item) => passesNonAvailabilityFilters(item)),
    [scopedProducts, passesNonAvailabilityFilters]
  );

  const inStockCount = useMemo(
    () => poolForStockCounts.filter((p) => shopProductHasAvailableStock(p)).length,
    [poolForStockCounts]
  );
  const outStockCount = useMemo(
    () => poolForStockCounts.filter((p) => shopProductIsSoldOut(p)).length,
    [poolForStockCounts]
  );

  const showInStockFilter = useMemo(
    () => scopedProducts.some((p) => shopProductHasAvailableStock(p)),
    [scopedProducts]
  );
  const showNewArrivalsFilter = useMemo(
    () =>
      scopedProducts.some((p) => {
        if (!p.created_at) return false;
        const ts = new Date(p.created_at).getTime();
        if (Number.isNaN(ts)) return false;
        return Date.now() - ts <= SHOP_NEW_ARRIVAL_DAYS * 864e5;
      }),
    [scopedProducts]
  );

  const showMarkdownFilter = useMemo(
    () =>
      scopedProducts.some(
        (p) => p.oldPrice != null && Number(p.oldPrice) > Number(p.price)
      ),
    [scopedProducts]
  );

  useEffect(() => {
    if (!showMarkdownFilter && saleOnly) setSaleOnly(false);
  }, [showMarkdownFilter, saleOnly]);

  useEffect(() => {
    if (!showInStockFilter && inStockOnly) setInStockOnly(false);
  }, [showInStockFilter, inStockOnly]);

  useEffect(() => {
    if (inStockOnly && inStockCount === 0) setInStockOnly(false);
  }, [inStockOnly, inStockCount]);

  useEffect(() => {
    if (outStockCount === 0 && outOfStockOnly) setOutOfStockOnly(false);
  }, [outStockCount, outOfStockOnly]);

  useEffect(() => {
    if (!showNewArrivalsFilter && newArrivalsOnly) setNewArrivalsOnly(false);
  }, [showNewArrivalsFilter, newArrivalsOnly]);

  useEffect(() => {
    if (!mobileFilterOpen) return;
    const { min, max } = shopPriceExtent;
    if (max <= min) {
      setDrawerPriceMin(min);
      setDrawerPriceMax(max);
      return;
    }
    if (mobilePriceRange) {
      setDrawerPriceMin(mobilePriceRange.min);
      setDrawerPriceMax(mobilePriceRange.max);
    } else {
      setDrawerPriceMin(min);
      setDrawerPriceMax(max);
    }
  }, [mobileFilterOpen, shopPriceExtent, mobilePriceRange]);

  useEffect(() => {
    if (!mobileFilterOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileFilterOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileFilterOpen]);

  const priceRangeOptions = useMemo(() => {
    const prices = scopedProducts.map((item) => Number(item.price) || 0).filter((n) => n > 0);
    if (prices.length === 0) return [];
    return SHOP_PRICE_BUCKETS.filter((b) => prices.some((n) => b.test(n)));
  }, [scopedProducts]);

  const filtered = useMemo(() => {
    return scopedProducts.filter((item) => {
      if (!passesNonAvailabilityFilters(item)) return false;
      if (inStockOnly && !shopProductHasAvailableStock(item)) return false;
      if (outOfStockOnly && !shopProductIsSoldOut(item)) return false;
      return true;
    });
  }, [scopedProducts, passesNonAvailabilityFilters, inStockOnly, outOfStockOnly]);

  const shopListingPool = useMemo(() => {
    if (currentSection !== 'new-in') return filtered;
    return pickNewInProductsByCategoryCap(filtered);
  }, [currentSection, filtered]);

  const togglePriceRange = (rangeId) => {
    setSelectedPriceRanges((prev) =>
      prev.includes(rangeId) ? prev.filter((v) => v !== rangeId) : [...prev, rangeId]
    );
  };

  const toggleBrand = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((v) => v !== brand) : [...prev, brand]
    );
  };

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((v) => v !== tag) : [...prev, tag]
    );
  };

  const toggleInStockOnly = () => {
    setInStockOnly((prev) => {
      const next = !prev;
      if (next) setOutOfStockOnly(false);
      return next;
    });
  };

  const toggleOutOfStockOnly = () => {
    setOutOfStockOnly((prev) => {
      const next = !prev;
      if (next) setInStockOnly(false);
      return next;
    });
  };

  const applyDrawerPriceFilter = () => {
    const lo = Math.min(drawerPriceMin, drawerPriceMax);
    const hi = Math.max(drawerPriceMin, drawerPriceMax);
    setSelectedPriceRanges([]);
    setMobilePriceRange({ min: lo, max: hi });
    setMobileFilterOpen(false);
  };

  const sortedProducts = useMemo(() => {
    const list = [...shopListingPool];
    if (sortBy === 'newest') {
      list.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
    }
    if (sortBy === 'price-low') list.sort((a, b) => a.price - b.price);
    if (sortBy === 'price-high') list.sort((a, b) => b.price - a.price);
    if (sortBy === 'popular') list.sort((a, b) => b.popularity - a.popularity);
    return list;
  }, [shopListingPool, sortBy]);

  const shopGridProducts = useMemo(() => {
    if (currentSection !== 'sale') {
      return sortedProducts.slice(0, visibleCount);
    }
    const list = [...sortedProducts].sort(
      (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0)
    );
    return list.slice(0, 20);
  }, [currentSection, sortedProducts, visibleCount]);

  const showShopToast = (message) => {
    setShopToast(message);
    window.clearTimeout(window.__shopToastTimer);
    window.__shopToastTimer = window.setTimeout(() => setShopToast(''), 2000);
  };

  const handleShopAddToCart = (item, event) => {
    event.preventDefault();
    event.stopPropagation();
    addProductToLocalCart({
      id: item.id,
      name: item.name,
      priceNumber: Number(item.price) || 0,
      image: item.image,
      sku: item.sku,
    });
    showShopToast('Product added to cart');
  };

  const handleShopWishlist = async (item, event) => {
    event.preventDefault();
    event.stopPropagation();
    const result = await wishlistProductFromCard({
      id: item.id,
      name: item.name,
      image: item.image,
      priceNumber: Number(item.price) || 0,
      sku: item.sku,
    });
    if (result.kind === 'config') {
      showShopToast('Supabase not configured');
      return;
    }
    if (result.kind === 'duplicate') {
      if (result.productRef) setWishlistedRefs((prev) => new Set(prev).add(result.productRef));
      showShopToast('Already in wishlist');
      return;
    }
    if (result.kind === 'error') {
      showShopToast('Could not add to wishlist');
      return;
    }
    if (result.kind === 'ok' && result.productRef) {
      setWishlistedRefs((prev) => new Set(prev).add(result.productRef));
      showShopToast('Added to wishlist');
    }
  };

  return (
    <div className="App">
      <SiteHeader />

      <main className="shop-page">
        <section className="shop-hero">
          <div className="container">
            <p className="eyebrow">{currentSection.replace('-', ' ')}</p>
            <h1>
              {config[currentSection].title}
              {currentSubcategoryDisplay ? ` / ${currentSubcategoryDisplay}` : ''}
            </h1>
            <p>
              {currentSubcategoryDisplay
                ? `Showing products in ${currentSubcategoryDisplay}.`
                : config[currentSection].subtitle}
            </p>
          </div>
        </section>

        <section className="shop-content container">
          <aside className="shop-sidebar shop-sidebar--desktop">
            <h3>Filters</h3>
            <div className="filter-group filter-group--search">
              <p>Search</p>
              <label className="shop-filter-search-label" htmlFor="shopFilterSearch">
                <span className="visually-hidden">Search in this category</span>
                <input
                  id="shopFilterSearch"
                  className="shop-filter-search"
                  type="search"
                  autoComplete="off"
                  placeholder="Name, SKU, brand…"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </label>
            </div>
            {showNewArrivalsFilter ? (
              <div className="filter-group">
                <p>Collection</p>
                <label>
                  <input
                    type="checkbox"
                    checked={newArrivalsOnly}
                    onChange={() => setNewArrivalsOnly((prev) => !prev)}
                  />{' '}
                  New arrivals (last {SHOP_NEW_ARRIVAL_DAYS} days)
                </label>
              </div>
            ) : null}
            {scopedProducts.length > 0 ? (
              <div className="filter-group">
                <p>Availability</p>
                <label className={inStockCount === 0 ? 'shop-filter-label--muted' : ''}>
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    disabled={inStockCount === 0}
                    onChange={toggleInStockOnly}
                  />{' '}
                  In stock ({inStockCount})
                </label>
                <label className={outStockCount === 0 ? 'shop-filter-label--muted' : ''}>
                  <input
                    type="checkbox"
                    checked={outOfStockOnly}
                    disabled={outStockCount === 0}
                    onChange={toggleOutOfStockOnly}
                  />{' '}
                  Out of stock ({outStockCount})
                </label>
              </div>
            ) : null}
            {brandOptions.length > 0 ? (
              <div className="filter-group">
                <p>Brand</p>
                {brandOptions.map((brand) => (
                  <label key={brand}>
                    <input
                      type="checkbox"
                      checked={selectedBrands.includes(brand)}
                      onChange={() => toggleBrand(brand)}
                    />{' '}
                    {brand}
                  </label>
                ))}
              </div>
            ) : null}
            {tagOptions.length > 0 ? (
              <div className="filter-group">
                <p>Tags</p>
                {tagOptions.map((tag) => (
                  <label key={tag}>
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag)}
                      onChange={() => toggleTag(tag)}
                    />{' '}
                    {tag}
                  </label>
                ))}
              </div>
            ) : null}
            <div className="filter-group">
              <p>Price Range</p>
              {priceRangeOptions.length === 0 ? (
                <label><input type="checkbox" disabled /> Not available</label>
              ) : (
                priceRangeOptions.map((range) => (
                  <label key={range.id}>
                    <input
                      type="checkbox"
                      checked={selectedPriceRanges.includes(range.id)}
                      onChange={() => togglePriceRange(range.id)}
                    />{' '}
                    {range.label}
                  </label>
                ))
              )}
            </div>
            {showMarkdownFilter ? (
              <div className="filter-group">
                <p>Offers</p>
                <label>
                  <input
                    type="checkbox"
                    checked={saleOnly}
                    onChange={() => setSaleOnly((prev) => !prev)}
                  />{' '}
                  Markdown / sale
                </label>
              </div>
            ) : null}
            <div className="filter-group">
              <p>Quick actions</p>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedPriceRanges([]);
                  setSaleOnly(false);
                  setInStockOnly(false);
                  setOutOfStockOnly(false);
                  setNewArrivalsOnly(false);
                  setSelectedBrands([]);
                  setSelectedTags([]);
                  setMobilePriceRange(null);
                }}
              >
                Reset filters
              </button>
            </div>
          </aside>

          <div className="shop-main">
            <div className="shop-mobile-toolbar" aria-label="Shop tools">
              <button type="button" className="shop-toolbar-filter" onClick={() => setMobileFilterOpen(true)}>
                <FaFilter aria-hidden size={14} />
                <span>Filter</span>
              </button>
              <label className="shop-toolbar-sort">
                <span className="shop-toolbar-sort-label">Sort</span>
                <span className="shop-toolbar-sort-wrap">
                  <select
                    id="shopSortMobile"
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value)}
                    aria-label="Sort products"
                  >
                    <option value="newest">Newest</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="popular">Popularity</option>
                  </select>
                  <FaChevronDown aria-hidden className="shop-toolbar-sort-chevron" size={10} />
                </span>
              </label>
            </div>

            <div className="shop-sortbar shop-sortbar--desktop">
              <p>
                {shopProductsLoading && isSupabaseConfigured && supabase
                  ? 'Loading…'
                  : currentSection === 'sale' && sortedProducts.length > 0
                    ? `${shopGridProducts.length} lowest-priced sale ${shopGridProducts.length === 1 ? 'item' : 'items'}`
                    : `${sortedProducts.length} pieces`}
              </p>
              <label htmlFor="shopSortDesk">
                Sort by
                <select id="shopSortDesk" value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                  <option value="newest">Newest</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="popular">Popularity</option>
                </select>
              </label>
            </div>

            <div className={`shop-grid shop-grid--view-${shopViewLayout}`}>
              {shopProductsLoading && isSupabaseConfigured && supabase ? (
                <p className="shop-loading-msg">Loading products…</p>
              ) : sortedProducts.length === 0 ? (
                <p className="shop-empty-msg">No products in this category yet.</p>
              ) : (
                shopGridProducts.map((item, cardIndex) => {
                  const wishlistRef = item.id ? String(item.id) : slugifyCatalog(item.name || 'product');
                  const isWishlisted = wishlistedRefs.has(wishlistRef);
                  return (
                    <ProductCardUi
                      key={item.id || `${slugifyCatalog(item.name)}-${cardIndex}`}
                      item={{
                        id: item.id,
                        name: item.name,
                        image: item.image,
                        priceNumber: Number(item.price) || 0,
                        sku: item.sku,
                      }}
                      productHref={`/product/${encodeURIComponent(item.id || slugifyCatalog(item.name))}`}
                      priceDisplay={formatGBP(item.price)}
                      oldPriceDisplay={
                        item.oldPrice != null && Number(item.oldPrice) > Number(item.price)
                          ? formatGBP(item.oldPrice)
                          : null
                      }
                      hoverImage={item.hoverImage}
                      isWishlisted={isWishlisted}
                      isInCart={shopCartBaseLineIds.has(
                        catalogProductCartLineId({
                          id: item.id,
                          name: item.name,
                        })
                      )}
                      onWishlistClick={(e) => {
                        void handleShopWishlist(item, e);
                      }}
                      onAddToCartClick={(e) => handleShopAddToCart(item, e)}
                    />
                  );
                })
              )}
            </div>

            {!shopProductsLoading &&
              currentSection !== 'sale' &&
              visibleCount < sortedProducts.length && (
              <div className="load-more-wrap">
                <button className="ghost-btn" type="button" onClick={() => setVisibleCount((count) => count + 4)}>
                  Load More
                </button>
              </div>
            )}
          </div>
        </section>

        {mobileFilterOpen ? (
          <>
            <button
              type="button"
              className="shop-filter-backdrop"
              aria-label="Close filters"
              onClick={() => setMobileFilterOpen(false)}
            />
            <aside
              className="shop-filter-drawer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="shopDrawerFilterTitle"
            >
              <header className="shop-filter-drawer-head">
                <h2 id="shopDrawerFilterTitle">Filter</h2>
                <button
                  type="button"
                  className="shop-filter-drawer-close"
                  aria-label="Close filters"
                  onClick={() => setMobileFilterOpen(false)}
                >
                  <FaTimes aria-hidden size={18} />
                </button>
              </header>
              <div className="shop-filter-drawer-body">
                <div className="filter-group filter-group--search shop-drawer-filter-group">
                  <p>Search</p>
                  <label className="shop-filter-search-label" htmlFor="shopFilterSearchDrawer">
                    <span className="visually-hidden">Search in this category</span>
                    <input
                      id="shopFilterSearchDrawer"
                      className="shop-filter-search"
                      type="search"
                      autoComplete="off"
                      placeholder="Name, SKU, brand…"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                    />
                  </label>
                </div>
                {showNewArrivalsFilter ? (
                  <div className="filter-group shop-drawer-filter-group">
                    <p>Collection</p>
                    <label>
                      <input
                        type="checkbox"
                        checked={newArrivalsOnly}
                        onChange={() => setNewArrivalsOnly((prev) => !prev)}
                      />{' '}
                      New arrivals (last {SHOP_NEW_ARRIVAL_DAYS} days)
                    </label>
                  </div>
                ) : null}
                {scopedProducts.length > 0 ? (
                  <div className="filter-group shop-drawer-filter-group">
                    <p>Availability</p>
                    <label className={inStockCount === 0 ? 'shop-filter-label--muted' : ''}>
                      <input
                        type="checkbox"
                        checked={inStockOnly}
                        disabled={inStockCount === 0}
                        onChange={toggleInStockOnly}
                      />{' '}
                      In stock ({inStockCount})
                    </label>
                    <label className={outStockCount === 0 ? 'shop-filter-label--muted' : ''}>
                      <input
                        type="checkbox"
                        checked={outOfStockOnly}
                        disabled={outStockCount === 0}
                        onChange={toggleOutOfStockOnly}
                      />{' '}
                      Out of stock ({outStockCount})
                    </label>
                  </div>
                ) : null}
                {shopPriceExtent.max > shopPriceExtent.min ? (
                  <div className="filter-group shop-drawer-filter-group shop-drawer-price-block">
                    <p>Price</p>
                    <div className="shop-drawer-range-wrap">
                      <div className="shop-drawer-range-track" aria-hidden />
                      <input
                        type="range"
                        className="shop-drawer-range-input shop-drawer-range-input--min"
                        min={Math.floor(shopPriceExtent.min)}
                        max={Math.ceil(shopPriceExtent.max)}
                        value={drawerPriceMin}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setDrawerPriceMin(v);
                          if (v > drawerPriceMax) setDrawerPriceMax(v);
                        }}
                      />
                      <input
                        type="range"
                        className="shop-drawer-range-input shop-drawer-range-input--max"
                        min={Math.floor(shopPriceExtent.min)}
                        max={Math.ceil(shopPriceExtent.max)}
                        value={drawerPriceMax}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setDrawerPriceMax(v);
                          if (v < drawerPriceMin) setDrawerPriceMin(v);
                        }}
                      />
                    </div>
                    <p className="shop-drawer-price-label">
                      Price:{' '}
                      <strong>
                        {formatGBP(Math.min(drawerPriceMin, drawerPriceMax))} —{' '}
                        {formatGBP(Math.max(drawerPriceMin, drawerPriceMax))}
                      </strong>
                    </p>
                    <button type="button" className="shop-drawer-price-apply" onClick={applyDrawerPriceFilter}>
                      Filter
                    </button>
                  </div>
                ) : null}
                {brandOptions.length > 0 ? (
                  <div className="filter-group shop-drawer-filter-group">
                    <p>Brand</p>
                    {brandOptions.map((brand) => (
                      <label key={`d-${brand}`}>
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(brand)}
                          onChange={() => toggleBrand(brand)}
                        />{' '}
                        {brand}
                      </label>
                    ))}
                  </div>
                ) : null}
                {tagOptions.length > 0 ? (
                  <div className="filter-group shop-drawer-filter-group">
                    <p>Tags</p>
                    {tagOptions.map((tag) => (
                      <label key={`d-${tag}`}>
                        <input
                          type="checkbox"
                          checked={selectedTags.includes(tag)}
                          onChange={() => toggleTag(tag)}
                        />{' '}
                        {tag}
                      </label>
                    ))}
                  </div>
                ) : null}
                {showMarkdownFilter ? (
                  <div className="filter-group shop-drawer-filter-group">
                    <p>Offers</p>
                    <label>
                      <input
                        type="checkbox"
                        checked={saleOnly}
                        onChange={() => setSaleOnly((prev) => !prev)}
                      />{' '}
                      Markdown / sale
                    </label>
                  </div>
                ) : null}
                <div className="filter-group shop-drawer-filter-group">
                  <p>Quick actions</p>
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedPriceRanges([]);
                      setSaleOnly(false);
                      setInStockOnly(false);
                      setOutOfStockOnly(false);
                      setNewArrivalsOnly(false);
                      setSelectedBrands([]);
                      setSelectedTags([]);
                      setMobilePriceRange(null);
                    }}
                  >
                    Reset filters
                  </button>
                </div>
              </div>
            </aside>
          </>
        ) : null}
      </main>
      {shopToast && (
        <div className="lux-toast" role="status" aria-live="polite">
          <span>✓</span> {shopToast}
        </div>
      )}
      <SiteFooter />
    </div>
  );
}

function ProductPage() {
  const productRef = decodeURIComponent(window.location.pathname.split('/')[2] || '').trim();
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedOptionA, setSelectedOptionA] = useState('');
  const [selectedOptionB, setSelectedOptionB] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [toastMessage, setToastMessage] = useState('');
  const [dbProduct, setDbProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(Boolean(isSupabaseConfigured && supabase && productRef));
  const [productMissing, setProductMissing] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewForm, setReviewForm] = useState({ name: '', rating: '5', comment: '' });
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [wishlistedOnAccount, setWishlistedOnAccount] = useState(false);
  const [relatedCartBaseLineIds, setRelatedCartBaseLineIds] = useState(() => readLocalCartBaseLineIdsWithQty());

  const refreshRelatedCartLineIds = useCallback(() => {
    setRelatedCartBaseLineIds(readLocalCartBaseLineIdsWithQty());
  }, []);

  useEffect(() => {
    refreshRelatedCartLineIds();
    const onCart = () => refreshRelatedCartLineIds();
    const onStorage = (event) => {
      if (!event || event.key === CUSTOMER_CART_KEY) refreshRelatedCartLineIds();
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshRelatedCartLineIds();
    };
    window.addEventListener('cart-updated', onCart);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('cart-updated', onCart);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refreshRelatedCartLineIds]);

  useEffect(() => {
    let cancelled = false;
    async function checkWishlistRow() {
      if (!dbProduct?.id && !dbProduct?.name) {
        setWishlistedOnAccount(false);
        return;
      }
      const pref = String(dbProduct.id || slugifyCatalog(dbProduct.name || 'product'));
      const userEmail = (window.localStorage.getItem(CUSTOMER_EMAIL_KEY) || '').trim().toLowerCase();
      const loggedIn = Boolean(userEmail) && window.localStorage.getItem(CUSTOMER_SESSION_KEY) === 'true';
      if (!loggedIn) {
        if (!cancelled) {
          setWishlistedOnAccount(readGuestWishlistRows().some((r) => String(r.product_ref) === pref));
        }
        return;
      }
      if (!isSupabaseConfigured || !supabase) {
        setWishlistedOnAccount(false);
        return;
      }
      const { data } = await supabase
        .from('wishlist_items')
        .select('id')
        .eq('user_email', userEmail)
        .eq('product_ref', pref)
        .maybeSingle();
      if (!cancelled) setWishlistedOnAccount(Boolean(data?.id));
    }
    checkWishlistRow();
    return () => {
      cancelled = true;
    };
  }, [dbProduct?.id, dbProduct?.name]);

  useEffect(() => {
    let cancelled = false;
    async function loadProduct() {
      if (!isSupabaseConfigured || !supabase || !productRef) {
        setLoadingProduct(false);
        setProductMissing(false);
        return;
      }
      setLoadingProduct(true);
      setProductMissing(false);
      const { data, error } = await supabase
        .from('products')
        .select(
          'id, name, description, price, stock, sku, image, gallery_images, material, dimensions, weight, section, sub_category'
        )
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error || !Array.isArray(data)) {
        setLoadingProduct(false);
        setProductMissing(true);
        return;
      }
      const row = data.find(
        (p) => String(p.id) === String(productRef) || slugifyCatalog(p.name || '') === productRef
      );
      if (!row) {
        setLoadingProduct(false);
        setProductMissing(true);
        return;
      }
      setReviewsLoading(true);
      const { data: reviewData, error: reviewLoadError } = await supabase
        .from('product_reviews')
        .select('id, reviewer_name, rating, comment, created_at')
        .eq('product_id', row.id)
        .order('created_at', { ascending: false });
      if (!cancelled) {
        setReviewsLoading(false);
        if (!reviewLoadError && Array.isArray(reviewData)) {
          setReviews(reviewData);
        } else {
          setReviews([]);
        }
      }
      const { data: variationData, error: variationError } = await supabase
        .from('product_variations')
        .select('option_a, option_b, price, stock, sku, image_url')
        .eq('product_id', row.id)
        .order('created_at', { ascending: true });
      const gallery = Array.isArray(row.gallery_images)
        ? row.gallery_images.filter((url) => typeof url === 'string' && url.trim())
        : [];
      const images = [row.image, ...gallery].filter((url) => typeof url === 'string' && url.trim());
      const variations =
        !variationError && Array.isArray(variationData)
          ? variationData.map((v) => ({
              optionA: v.option_a || '',
              optionB: v.option_b || '',
              price: Number(v.price || 0),
              stock: Number(v.stock || 0),
              sku: v.sku || '',
              image: v.image_url || '',
            }))
          : [];
      const optionAValues = Array.from(new Set(variations.map((v) => v.optionA).filter(Boolean)));
      const optionBValues = Array.from(new Set(variations.map((v) => v.optionB).filter(Boolean)));
      if (cancelled) {
        setLoadingProduct(false);
        return;
      }
      setDbProduct({
        id: row.id,
        name: row.name || 'Product',
        basePrice: Number(row.price || 0),
        stock: Number(row.stock ?? 0),
        sku: row.sku || '',
        description: row.description || '',
        section: row.section || '',
        subCategory: row.sub_category || '',
        images:
          images.length > 0
            ? images
            : ['https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1000&q=80'],
        specs: [
          row.material ? `Material: ${row.material}` : null,
          row.dimensions ? `Dimensions: ${row.dimensions}` : null,
          row.weight ? `Weight: ${row.weight}` : null,
        ]
          .filter(Boolean)
          .join('. '),
        variations,
        optionAValues,
        optionBValues,
        isFromSupabase: true,
      });
      setSelectedOptionA(optionAValues[0] || '');
      setSelectedOptionB(optionBValues[0] || '');
      setActiveImage(0);
      setLoadingProduct(false);
    }
    loadProduct();
    return () => {
      cancelled = true;
    };
  }, [productRef]);

  useEffect(() => {
    let cancelled = false;
    async function loadRelatedProducts() {
      if (!isSupabaseConfigured || !supabase || !dbProduct?.id) {
        setRelatedProducts([]);
        return;
      }

      let query = supabase
        .from('products')
        .select('id, name, price, image, section, sub_category, sku, created_at')
        .neq('id', dbProduct.id)
        .order('created_at', { ascending: false })
        .limit(8);

      if (dbProduct.section) {
        query = query.eq('section', dbProduct.section);
      }

      const { data, error } = await query;
      if (cancelled) return;

      if (error || !Array.isArray(data)) {
        setRelatedProducts([]);
        return;
      }

      const sameSub = dbProduct.subCategory
        ? data.filter((p) => (p?.sub_category || '') === dbProduct.subCategory)
        : [];
      const pool = sameSub.length > 0 ? [...sameSub, ...data.filter((p) => !sameSub.includes(p))] : data;
      const mapped = pool.slice(0, 4).map((p) => ({
        id: p.id,
        name: p.name || 'Product',
        price: formatGBP(p.price || 0),
        priceNumber: Number(p.price) || 0,
        sku: p.sku && String(p.sku).trim() ? String(p.sku).trim() : '',
        image:
          p.image ||
          'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=900&q=80',
      }));
      setRelatedProducts(mapped);
    }

    loadRelatedProducts();
    return () => {
      cancelled = true;
    };
  }, [dbProduct?.id, dbProduct?.section, dbProduct?.subCategory]);

  const fallbackProduct = {
    id: '',
    name: 'Signature Silk Dress',
    basePrice: 1290,
    description:
      'A fluid silk silhouette with sculpted shoulders and a soft drape, designed for modern evening elegance. Cut to flatter the natural waist, this piece balances structure and movement for both formal occasions and refined evening wear. Finished with concealed closures and hand-pressed seams for a smooth, elevated fit. The softly luminous finish elevates the garment under evening light, while the breathable interior lining ensures comfort through long events. Designed as a timeless investment, it transitions effortlessly from intimate dinners to black-tie occasions with understated sophistication.',
    images: [
      'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1464863979621-258859e62245?auto=format&fit=crop&w=1000&q=80',
    ],
    variations: [],
    optionAValues: [],
    optionBValues: [],
    isFromSupabase: false,
  };

  const shouldUseFallback = !isSupabaseConfigured || !supabase;
  const product = shouldUseFallback ? dbProduct || fallbackProduct : dbProduct;
  const selectedVariation =
    product && Array.isArray(product.variations) && product.variations.length > 0
      ? product.variations.find(
          (v) =>
            (selectedOptionA ? v.optionA === selectedOptionA : true) &&
            (selectedOptionB ? v.optionB === selectedOptionB : true)
        ) || product.variations[0]
      : null;
  const displayPrice = selectedVariation?.price ?? product?.basePrice ?? 0;
  const totalPrice = displayPrice * quantity;

  const availableStock = useMemo(() => {
    if (shouldUseFallback || !product?.isFromSupabase) return Number.POSITIVE_INFINITY;
    if (Array.isArray(product.variations) && product.variations.length > 0) {
      return Math.max(0, Number(selectedVariation?.stock ?? 0));
    }
    return Math.max(0, Number(product.stock ?? 0));
  }, [shouldUseFallback, product, selectedVariation]);

  useEffect(() => {
    if (!Number.isFinite(availableStock)) return;
    setQuantity((q) => {
      if (availableStock <= 0) return 1;
      return Math.min(Math.max(1, q), availableStock);
    });
  }, [availableStock]);

  const handleThumbSelect = (index) => {
    if (!product) return;
    setActiveImage(index);
    const selectedImage = product.images?.[index];
    if (!selectedImage || !Array.isArray(product.variations) || product.variations.length === 0) return;
    const matchedVariation = product.variations.find((v) => v.image && v.image === selectedImage);
    if (!matchedVariation) return;
    if (matchedVariation.optionA) setSelectedOptionA(matchedVariation.optionA);
    if (matchedVariation.optionB) setSelectedOptionB(matchedVariation.optionB);
  };

  useEffect(() => {
    if (!selectedVariation?.image || !Array.isArray(product?.images) || product.images.length === 0) return;
    const idx = product.images.findIndex((img) => img === selectedVariation.image);
    if (idx >= 0 && idx !== activeImage) setActiveImage(idx);
  }, [selectedVariation?.image, product?.images, activeImage]);

  if (!product && loadingProduct) {
    return (
      <div className="App">
        <SiteHeader />
        <main className="pdp-page container section-space product-page-loading">
          <p className="product-page-loading-msg">Loading product…</p>
        </main>
        <SiteFooter />
      </div>
    );
  }
  if (!product && !loadingProduct && productMissing) {
    return (
      <div className="App">
        <SiteHeader />
        <main className="pdp-page container section-space">
          <p className="admin-products-empty">Product not found.</p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const tabs = [
    {
      id: 'description',
      label: 'Description',
      content: product.description || 'Description not available yet for this product.',
    },
    {
      id: 'specifications',
      label: 'Specifications',
      content:
        product.specs ||
        'Specifications not added yet. Add material, dimensions, and weight in admin.',
    },
    {
      id: 'reviews',
      label: 'Reviews',
      content: '',
    },
  ];

  const fallbackRelated = [
    {
      id: 'rel-fallback-heel',
      name: 'Atelier Evening Heel',
      priceNumber: 890,
      price: formatGBP(890),
      sku: '',
      image:
        'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?auto=format&fit=crop&w=900&q=80',
    },
    {
      id: 'rel-fallback-clutch',
      name: 'Structured Mini Clutch',
      priceNumber: 740,
      price: formatGBP(740),
      sku: '',
      image:
        'https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?auto=format&fit=crop&w=900&q=80',
    },
    {
      id: 'rel-fallback-earrings',
      name: 'Gold Line Earrings',
      priceNumber: 420,
      price: formatGBP(420),
      sku: '',
      image:
        'https://images.unsplash.com/photo-1635767798638-3e25273a8236?auto=format&fit=crop&w=900&q=80',
    },
    {
      id: 'rel-fallback-wrap',
      name: 'Cashmere Evening Wrap',
      priceNumber: 620,
      price: formatGBP(620),
      sku: '',
      image:
        'https://images.unsplash.com/photo-1487222477894-8943e31ef7b2?auto=format&fit=crop&w=900&q=80',
    },
  ];
  const related = relatedProducts.length > 0 ? relatedProducts : fallbackRelated;

  const showToast = (message) => {
    setToastMessage(message);
    window.clearTimeout(window.__dstyluxeToastTimer);
    window.__dstyluxeToastTimer = window.setTimeout(() => {
      setToastMessage('');
    }, 2000);
  };

  const handleRelatedAddToCart = (item, event) => {
    event.preventDefault();
    event.stopPropagation();
    addProductToLocalCart({
      id: item.id,
      name: item.name,
      priceNumber: item.priceNumber,
      image: item.image,
      sku: item.sku,
    });
    showToast('Product added to cart');
  };

  const handleRelatedWishlist = async (item, event) => {
    event.preventDefault();
    event.stopPropagation();
    const result = await wishlistProductFromCard({
      id: item.id,
      name: item.name,
      image: item.image,
      priceNumber: item.priceNumber,
      sku: item.sku,
    });
    if (result.kind === 'config') {
      showToast('Supabase not configured');
      return;
    }
    if (result.kind === 'duplicate') {
      showToast('Already in wishlist');
      return;
    }
    if (result.kind === 'error') {
      showToast('Could not add to wishlist');
      return;
    }
    showToast('Added to wishlist');
  };

  const handleAddToCart = () => {
    if (!product) return;
    const variationKey = selectedVariation
      ? `${selectedVariation.optionA || ''}__${selectedVariation.optionB || ''}`
      : '';
    const cartLineId = `${product.id || slugifyCatalog(product.name)}::${variationKey}`;
    const itemImage = selectedVariation?.image || product.images?.[activeImage] || product.images?.[0] || '';
    const itemName = selectedVariation
      ? `${product.name} (${selectedVariation.optionA || ''}${selectedVariation.optionB ? ` / ${selectedVariation.optionB}` : ''})`
      : product.name;
    const lineSku = (selectedVariation?.sku || product.sku || '').trim() || null;
    let existingInCart = 0;
    try {
      const raw = window.localStorage.getItem(CUSTOMER_CART_KEY);
      const list = raw ? JSON.parse(raw) : [];
      const cart = Array.isArray(list) ? list : [];
      const found = cart.find((row) => row?.id === cartLineId);
      existingInCart = Math.max(0, Number(found?.qty) || 0);
    } catch {
      existingInCart = 0;
    }
    if (Number.isFinite(availableStock)) {
      if (availableStock <= 0) {
        showToast('This product is out of stock.');
        return;
      }
      const room = availableStock - existingInCart;
      if (room <= 0) {
        showToast('You already have the maximum available quantity in your cart.');
        return;
      }
      if (quantity > room) {
        showToast(`Only ${room} more can be added (${availableStock} in stock).`);
        return;
      }
    }
    try {
      const raw = window.localStorage.getItem(CUSTOMER_CART_KEY);
      const list = raw ? JSON.parse(raw) : [];
      const cart = Array.isArray(list) ? list : [];
      const idx = cart.findIndex((row) => row?.id === cartLineId);
      if (idx >= 0) {
        cart[idx] = {
          ...cart[idx],
          qty: (Number(cart[idx].qty) || 0) + quantity,
          price: Number(displayPrice) || 0,
          image: itemImage || cart[idx].image || '',
          name: itemName,
          sku: lineSku ?? cart[idx].sku ?? null,
          product_id: product.id || cart[idx].product_id || null,
          variation: selectedVariation
            ? {
                optionA: selectedVariation.optionA || '',
                optionB: selectedVariation.optionB || '',
              }
            : cart[idx].variation || null,
        };
      } else {
        cart.push({
          id: cartLineId,
          product_id: product.id || null,
          sku: lineSku,
          name: itemName,
          price: Number(displayPrice) || 0,
          qty: quantity,
          image: itemImage,
          variation: selectedVariation
            ? {
                optionA: selectedVariation.optionA || '',
                optionB: selectedVariation.optionB || '',
              }
            : null,
        });
      }
      window.localStorage.setItem(CUSTOMER_CART_KEY, JSON.stringify(cart));
      window.dispatchEvent(new Event('cart-updated'));
    } catch {
      /* ignore localStorage errors */
    }
    showToast('Product added to cart');
  };

  const handleBuyItNow = () => {
    if (!product) return;
    const variationKey = selectedVariation
      ? `${selectedVariation.optionA || ''}__${selectedVariation.optionB || ''}`
      : '';
    const cartLineId = `${product.id || slugifyCatalog(product.name)}::${variationKey}`;
    const itemImage = selectedVariation?.image || product.images?.[activeImage] || product.images?.[0] || '';
    const itemName = selectedVariation
      ? `${product.name} (${selectedVariation.optionA || ''}${selectedVariation.optionB ? ` / ${selectedVariation.optionB}` : ''})`
      : product.name;
    const lineSku = (selectedVariation?.sku || product.sku || '').trim() || null;

    if (Number.isFinite(availableStock)) {
      if (availableStock <= 0) {
        showToast('This product is out of stock.');
        return;
      }
      if (quantity > availableStock) {
        showToast(`Only ${availableStock} in stock.`);
        return;
      }
    }

    try {
      const cart = [
        {
          id: cartLineId,
          product_id: product.id || null,
          sku: lineSku,
          name: itemName,
          price: Number(displayPrice) || 0,
          qty: quantity,
          image: itemImage,
          variation: selectedVariation
            ? {
                optionA: selectedVariation.optionA || '',
                optionB: selectedVariation.optionB || '',
              }
            : null,
        },
      ];
      window.localStorage.setItem(CUSTOMER_CART_KEY, JSON.stringify(cart));
      window.dispatchEvent(new Event('cart-updated'));
      window.location.href = '/checkout';
    } catch {
      showToast('Could not open checkout.');
    }
  };

  const handleAddToWishlist = async () => {
    if (!product) return;
    const productImage = selectedVariation?.image || product.images?.[activeImage] || product.images?.[0] || '';
    const result = await wishlistProductFromCard({
      id: product.id,
      name: product.name,
      image: productImage,
      priceNumber: Number(displayPrice) || 0,
    });
    if (result.kind === 'config') {
      showToast('Supabase not configured');
      return;
    }
    if (result.kind === 'duplicate') {
      setWishlistedOnAccount(true);
      showToast('Already in wishlist');
      return;
    }
    if (result.kind === 'error') {
      showToast('Could not add to wishlist');
      return;
    }
    setWishlistedOnAccount(true);
    showToast('Added to wishlist successfully');
  };

  const handleReviewSubmit = async (event) => {
    event.preventDefault();
    setReviewError('');
    const reviewerName = reviewForm.name.trim();
    const comment = reviewForm.comment.trim();
    const rating = Number.parseInt(reviewForm.rating, 10);
    if (!reviewerName) {
      setReviewError('Enter your name.');
      return;
    }
    if (!comment) {
      setReviewError('Enter your review.');
      return;
    }
    if (Number.isNaN(rating) || rating < 1 || rating > 5) {
      setReviewError('Rating must be between 1 and 5.');
      return;
    }
    if (!isSupabaseConfigured || !supabase || !product?.id) {
      setReviewError('Reviews require Supabase product data.');
      return;
    }
    setReviewSubmitting(true);
    const { data, error } = await supabase
      .from('product_reviews')
      .insert({
        product_id: product.id,
        reviewer_name: reviewerName,
        rating,
        comment,
      })
      .select('id, reviewer_name, rating, comment, created_at')
      .single();
    setReviewSubmitting(false);
    if (error) {
      setReviewError(error.message);
      return;
    }
    setReviews((prev) => [data, ...prev]);
    setReviewForm({ name: '', rating: '5', comment: '' });
    showToast('Review added.');
  };

  return (
    <div className="App">
      <SiteHeader />

      <main className="pdp-page container section-space">
        <section className="pdp-layout">
          <div className="pdp-gallery">
            <div className="pdp-main-image">
              <img src={product.images[activeImage]} alt={product.name} />
            </div>
            <div className="pdp-thumbs">
              {product.images.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  className={index === activeImage ? 'active' : ''}
                  onClick={() => handleThumbSelect(index)}
                >
                  <img src={image} alt={`${product.name} view ${index + 1}`} />
                </button>
              ))}
            </div>
          </div>

          <article className="pdp-info">
            <p className="eyebrow">Luxury Collection</p>
            <h1>{product.name}</h1>
            <p className="pdp-price">{formatGBP(totalPrice)}</p>

            {Array.isArray(product.variations) && product.variations.length > 0 && (
              <div className="pdp-options">
                {product.optionAValues.length > 0 && (
                  <div className="option-group">
                    <p>Option 1</p>
                    <div className="option-chips">
                      {product.optionAValues.map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={selectedOptionA === value ? 'selected' : ''}
                          onClick={() => setSelectedOptionA(value)}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {product.optionBValues.length > 0 && (
                  <div className="option-group">
                    <p>Option 2</p>
                    <div className="option-chips">
                      {product.optionBValues.map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={selectedOptionB === value ? 'selected' : ''}
                          onClick={() => setSelectedOptionB(value)}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="pdp-actions">
              {Number.isFinite(availableStock) && (
                <p className="pdp-stock-msg">
                  {availableStock <= 0 ? 'Out of stock' : `${availableStock} in stock`}
                </p>
              )}
              <div className="pdp-qty-wishlist-row">
                <div className="pdp-qty-pill" role="group" aria-label="Quantity">
                  <button
                    type="button"
                    className="pdp-qty-pill__btn"
                    onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <span className="pdp-qty-pill__value" aria-live="polite">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    className="pdp-qty-pill__btn"
                    onClick={() =>
                      setQuantity((value) =>
                        Number.isFinite(availableStock)
                          ? Math.min(value + 1, Math.max(1, availableStock))
                          : value + 1
                      )
                    }
                    disabled={Number.isFinite(availableStock) && quantity >= availableStock}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
                <button
                  type="button"
                  className={`pdp-wishlist-circle${wishlistedOnAccount ? ' is-saved' : ''}`}
                  onClick={handleAddToWishlist}
                  aria-label={wishlistedOnAccount ? 'In wishlist' : 'Add to wishlist'}
                >
                  {wishlistedOnAccount ? <FaHeart aria-hidden /> : <FaRegHeart aria-hidden />}
                </button>
              </div>
              <button
                className="primary-btn pdp-add-cart-btn"
                type="button"
                onClick={handleAddToCart}
                disabled={Number.isFinite(availableStock) && availableStock <= 0}
              >
                Add to Cart
              </button>
              <button
                className="primary-btn pdp-buy-now-btn"
                type="button"
                onClick={handleBuyItNow}
                disabled={Number.isFinite(availableStock) && availableStock <= 0}
              >
                Buy it now
              </button>
            </div>

            <div className="trust-row">
              <p>Secure checkout</p>
              <p>Easy returns</p>
              <p>Express delivery</p>
            </div>
          </article>
        </section>

        <section className="pdp-tabs section-space">
          <div className="tab-head">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={activeTab === tab.id ? 'active' : ''}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="tab-body">
            {activeTab !== 'reviews' ? (
              <p>{tabs.find((tab) => tab.id === activeTab)?.content}</p>
            ) : (
              <div>
                {reviewsLoading ? (
                  <p>Loading reviews…</p>
                ) : reviews.length > 0 ? (
                  <div className="admin-product-form-grid">
                    {reviews.map((review) => (
                      <article key={review.id} className="pdp-review-item">
                        <p className="pdp-review-author">{review.reviewer_name}</p>
                        <p className="pdp-review-stars">
                          {'★'.repeat(Math.max(1, Math.min(5, Number(review.rating) || 0)))}
                        </p>
                        <p className="pdp-review-comment">{review.comment}</p>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p>No reviews yet for this product.</p>
                )}

                <form onSubmit={handleReviewSubmit} className="pdp-review-form">
                  <h3 className="pdp-review-title">Add a review</h3>
                  <label>
                    Your name
                    <input
                      type="text"
                      value={reviewForm.name}
                      onChange={(event) => setReviewForm((f) => ({ ...f, name: event.target.value }))}
                    />
                  </label>
                  <label>
                    Rating
                    <select
                      value={reviewForm.rating}
                      onChange={(event) => setReviewForm((f) => ({ ...f, rating: event.target.value }))}
                    >
                      <option value="5">5</option>
                      <option value="4">4</option>
                      <option value="3">3</option>
                      <option value="2">2</option>
                      <option value="1">1</option>
                    </select>
                  </label>
                  <label>
                    Review
                    <textarea
                      rows={4}
                      value={reviewForm.comment}
                      onChange={(event) => setReviewForm((f) => ({ ...f, comment: event.target.value }))}
                    />
                  </label>
                  {reviewError && (
                    <p className="admin-login-error" role="alert">
                      {reviewError}
                    </p>
                  )}
                  <button className="primary-btn" type="submit" disabled={reviewSubmitting}>
                    {reviewSubmitting ? 'Submitting…' : 'Submit review'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </section>

        <section className="section-space">
          <div className="section-heading">
            <h2>Related Products</h2>
          </div>
          <div className="related-grid">
            {related.map((item) => (
              <ProductCardUi
                key={item.id || item.name}
                item={{
                  id: item.id,
                  name: item.name,
                  image: item.image,
                  priceNumber: item.priceNumber,
                  sku: item.sku,
                }}
                productHref={`/product/${encodeURIComponent(item.id || slugifyCatalog(item.name))}`}
                priceDisplay={item.price}
                isInCart={relatedCartBaseLineIds.has(
                  catalogProductCartLineId({
                    id: item.id,
                    name: item.name,
                  })
                )}
                onWishlistClick={(e) => {
                  void handleRelatedWishlist(item, e);
                }}
                onAddToCartClick={(e) => handleRelatedAddToCart(item, e)}
              />
            ))}
          </div>
        </section>
      </main>
      {toastMessage && (
        <div className="lux-toast" role="status" aria-live="polite">
          <span>✓</span> {toastMessage}
        </div>
      )}
      <SiteFooter />
    </div>
  );
}

function CartPage() {
  const cartPlaceholderImage =
    'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80';

  const [items, setItems] = useState(() => {
    try {
      const raw = window.localStorage.getItem(CUSTOMER_CART_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((item) => ({
          id: item?.id || `cart-${Math.random().toString(36).slice(2, 10)}`,
          product_id: item?.product_id || null,
          sku: item?.sku ?? null,
          name: item?.name || 'Product',
          price: Number(item?.price) || 0,
          qty: Math.max(1, Number(item?.qty) || 1),
          image: item?.image || cartPlaceholderImage,
          variation:
            item?.variation && typeof item.variation === 'object'
              ? {
                  optionA: item.variation.optionA || '',
                  optionB: item.variation.optionB || '',
                }
              : null,
          maxQty: typeof item?.maxQty === 'number' ? item.maxQty : undefined,
        }))
        .filter((item) => item.name);
    } catch {
      return [];
    }
  });

  useEffect(() => {
    window.localStorage.setItem(CUSTOMER_CART_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event('cart-updated'));
  }, [items]);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) return undefined;
    let cancelled = false;
    (async () => {
      const ids = [...new Set(items.map((i) => i.product_id).filter(Boolean))];
      if (ids.length === 0) return;

      const { data: products, error: pe } = await supabase.from('products').select('id, stock').in('id', ids);
      if (cancelled || pe || !Array.isArray(products)) return;

      let varRows = [];
      const { data: vars, error: ve } = await supabase
        .from('product_variations')
        .select('product_id, option_a, option_b, stock')
        .in('product_id', ids);
      if (!ve && Array.isArray(vars)) {
        varRows = vars;
      }

      const stockByProduct = Object.fromEntries(products.map((p) => [String(p.id), Number(p.stock) || 0]));

      const next = items
        .map((item) => {
          if (!item.product_id) return item;
          const pid = String(item.product_id);
          const base = stockByProduct[pid];
          if (base === undefined) return item;
          const vo = item.variation || {};
          const effA = String(vo.optionA || '').trim();
          const effB = String(vo.optionB || '').trim();
          const hasVarRow = varRows.some((v) => String(v.product_id) === pid);
          let maxQty = base;
          if (hasVarRow && (effA || effB)) {
            const row = varRows.find(
              (v) =>
                String(v.product_id) === pid &&
                String(v.option_a || '').trim() === effA &&
                String(v.option_b || '').trim() === effB
            );
            if (row) maxQty = Number(row.stock) || 0;
          }
          if (maxQty <= 0) return null;
          const qty = Math.min(Math.max(1, item.qty), maxQty);
          if (item.qty === qty && item.maxQty === maxQty) return item;
          return { ...item, qty, maxQty };
        })
        .filter(Boolean);

      const sig = (arr) =>
        JSON.stringify(
          arr.map((x) => ({
            id: x.id,
            qty: x.qty,
            maxQty: x.maxQty ?? null,
            product_id: x.product_id,
          }))
        );
      if (sig(next) !== sig(items) && !cancelled) {
        setItems(next);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [items, isSupabaseConfigured, supabase]);

  const updateQty = (id, delta) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const cap =
          typeof item.maxQty === 'number' && item.maxQty > 0 ? item.maxQty : Number.POSITIVE_INFINITY;
        const nextQty = Math.max(1, item.qty + delta);
        return { ...item, qty: Math.min(nextQty, cap) };
      })
    );
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const subtotal = items.reduce((sum, item) => sum + item.price * item.qty, 0);
  const delivery = calculateDeliveryFee(subtotal);
  const total = subtotal + delivery;

  return (
    <div className="App">
      <SiteHeader />

      <main className="container section-space cart-page">
        <div className="section-heading">
          <p className="eyebrow">Shopping Bag</p>
          <h1 className="cart-title">Your Cart</h1>
        </div>

        <div className="cart-layout">
          <section className="cart-list">
            {items.length === 0 ? (
              <div className="cart-empty">
                <p>Your cart is currently empty.</p>
                <a href="/shop/for-her">Continue shopping</a>
              </div>
            ) : (
              items.map((item) => (
                <article className="cart-item" key={item.id}>
                  <img src={item.image} alt={item.name} />
                  <div className="cart-item-info">
                    <h3>{item.name}</h3>
                    <p>{formatGBP(item.price)}</p>
                    <div className="qty-row">
                      <button type="button" onClick={() => updateQty(item.id, -1)}>-</button>
                      <span>{item.qty}</span>
                      <button type="button" onClick={() => updateQty(item.id, 1)}>+</button>
                    </div>
                    <button className="remove-btn" type="button" onClick={() => removeItem(item.id)}>
                      Remove
                    </button>
                  </div>
                  <p className="item-total">{formatGBP(item.price * item.qty)}</p>
                </article>
              ))
            )}
            <a className="continue-link" href="/shop/for-her">Continue shopping</a>
          </section>

          <aside className="cart-summary">
            <h2>Summary</h2>
            <div className="summary-row">
              <span>Subtotal</span>
              <span>{formatGBP(subtotal)}</span>
            </div>
            <div className="summary-row">
              <span>Delivery</span>
              <span>{delivery === 0 ? 'Free' : formatGBP(delivery)}</span>
            </div>
            <div className="summary-row total">
              <span>Total</span>
              <span>{formatGBP(total)}</span>
            </div>
            <p className="checkout-note" style={{ marginTop: '0.5rem' }}>
              Free delivery on orders of {formatGBP(FREE_DELIVERY_THRESHOLD)} or more. Under that, delivery is {formatGBP(STANDARD_DELIVERY_CHARGE)}.
            </p>
            <a className="primary-btn checkout-btn checkout-link" href="/checkout">Checkout</a>
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

/** Stripe: calls Edge Function `create-checkout-session` then redirects to Stripe Checkout. */
function CheckoutPage() {
  const [orderItems] = useState(() => {
    try {
      const raw = window.localStorage.getItem(CUSTOMER_CART_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.map((item) => ({
        product_id: item?.product_id || null,
        sku: item?.sku || null,
        variation_option_a: item?.variation?.optionA || null,
        variation_option_b: item?.variation?.optionB || null,
        name: item?.name || 'Product',
        qty: Math.max(1, Number(item?.qty) || 1),
        price: Number(item?.price) || 0,
      }));
    } catch {
      return [];
    }
  });
  const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.qty, 0);
  const delivery = calculateDeliveryFee(subtotal);
  const total = subtotal + delivery;
  const [checkoutForm, setCheckoutForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    zipCode: '',
    state: '',
    country: '',
  });
  const [placingOrder, setPlacingOrder] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const checkoutSubmitLockRef = useRef(false);

  const handlePlaceOrder = async () => {
    if (checkoutSubmitLockRef.current) return;
    setCheckoutError('');
    const fullName = `${checkoutForm.firstName} ${checkoutForm.lastName}`.trim();
    if (!fullName) {
      setCheckoutError('Please enter your name.');
      return;
    }
    if (!checkoutForm.email.trim()) {
      setCheckoutError('Please enter your email address.');
      return;
    }
    if (orderItems.length === 0) {
      setCheckoutError('Your cart is empty.');
      return;
    }
    if (!isSupabaseConfigured || !supabase) {
      setCheckoutError('Supabase is not configured.');
      return;
    }
    checkoutSubmitLockRef.current = true;
    setPlacingOrder(true);
    const { data, error } = await supabase.functions.invoke(EDGE_FN_CREATE_CHECKOUT, {
      body: {
        origin: window.location.origin,
        customer: {
          firstName: checkoutForm.firstName,
          lastName: checkoutForm.lastName,
          email: checkoutForm.email.trim(),
          phone: checkoutForm.phone,
          streetAddress: checkoutForm.streetAddress,
          city: checkoutForm.city,
          state: checkoutForm.state,
          zipCode: checkoutForm.zipCode,
          country: checkoutForm.country,
        },
        items: orderItems.map((item) => ({
          product_id: item.product_id,
          sku: item.sku,
          variation_option_a: item.variation_option_a,
          variation_option_b: item.variation_option_b,
          name: item.name,
          qty: item.qty,
          price: item.price,
        })),
      },
    });
    setPlacingOrder(false);
    if (error) {
      checkoutSubmitLockRef.current = false;
      setCheckoutError(await edgeFunctionErrorMessage(error, data));
      return;
    }
    const url = data?.url;
    if (!url || typeof url !== 'string') {
      checkoutSubmitLockRef.current = false;
      setCheckoutError(
        data?.error ? await edgeFunctionErrorMessage(null, data) : 'Could not start payment.'
      );
      return;
    }
    window.location.href = url;
  };

  return (
    <div className="App">
      <SiteHeader />

      <main className="container section-space checkout-page">
        <div className="section-heading">
          <p className="eyebrow">Secure Checkout</p>
          <h1 className="cart-title">Checkout</h1>
        </div>

        <div className="checkout-layout">
          <form className="checkout-form" onSubmit={(event) => event.preventDefault()}>
            <section className="checkout-section">
              <h2>Billing Details</h2>
              <div className="field-grid">
                <label>
                  First Name
                  <input
                    type="text"
                    placeholder="Ava"
                    value={checkoutForm.firstName}
                    onChange={(event) => setCheckoutForm((f) => ({ ...f, firstName: event.target.value }))}
                  />
                </label>
                <label>
                  Last Name
                  <input
                    type="text"
                    placeholder="Hart"
                    value={checkoutForm.lastName}
                    onChange={(event) => setCheckoutForm((f) => ({ ...f, lastName: event.target.value }))}
                  />
                </label>
              </div>
              <label>
                Email Address
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={checkoutForm.email}
                  onChange={(event) => setCheckoutForm((f) => ({ ...f, email: event.target.value }))}
                />
              </label>
              <label>
                Phone Number
                <input
                  type="tel"
                  placeholder="+44000000"
                  value={checkoutForm.phone}
                  onChange={(event) => setCheckoutForm((f) => ({ ...f, phone: event.target.value }))}
                />
              </label>
            </section>

            <section className="checkout-section">
              <h2>Shipping Details</h2>
              <label>
                Street Address
                <input
                  type="text"
                  placeholder="123 Dstyluxe Avenue"
                  value={checkoutForm.streetAddress}
                  onChange={(event) => setCheckoutForm((f) => ({ ...f, streetAddress: event.target.value }))}
                />
              </label>
              <div className="field-grid">
                <label>
                  City
                  <input
                    type="text"
                    placeholder="London"
                    value={checkoutForm.city}
                    onChange={(event) => setCheckoutForm((f) => ({ ...f, city: event.target.value }))}
                  />
                </label>
                <label>
                  Postcode
                  <input
                    type="text"
                    placeholder="AB1 1CD"
                    value={checkoutForm.zipCode}
                    onChange={(event) => setCheckoutForm((f) => ({ ...f, zipCode: event.target.value }))}
                  />
                </label>
              </div>
              <div className="field-grid">
                <label>
                  County
                  <input
                    type="text"
                    placeholder="Greater London"
                    value={checkoutForm.state}
                    onChange={(event) => setCheckoutForm((f) => ({ ...f, state: event.target.value }))}
                  />
                </label>
                <label>
                  Country
                  <input
                    type="text"
                    placeholder="United Kingdom"
                    value={checkoutForm.country}
                    onChange={(event) => setCheckoutForm((f) => ({ ...f, country: event.target.value }))}
                  />
                </label>
              </div>
            </section>

          </form>

          <aside className="checkout-summary">
            <h2>Order Summary</h2>
            <div className="summary-items">
              {orderItems.map((item) => (
                <div className="summary-item" key={item.name}>
                  <p>{item.name} x {item.qty}</p>
                  <span>{formatGBP(item.price * item.qty)}</span>
                </div>
              ))}
            </div>
            <div className="summary-row">
              <span>Subtotal</span>
              <span>{formatGBP(subtotal)}</span>
            </div>
            <div className="summary-row">
              <span>Items</span>
              <span>{orderItems.length}</span>
            </div>
            <div className="summary-row">
              <span>Delivery</span>
              <span>{delivery === 0 ? 'Free' : formatGBP(delivery)}</span>
            </div>
            <div className="summary-row total">
              <span>Total</span>
              <span>{formatGBP(total)}</span>
            </div>
            {checkoutError && (
              <p className="admin-login-error" role="alert">
                {checkoutError}
              </p>
            )}
            <button className="primary-btn checkout-btn" type="button" onClick={handlePlaceOrder} disabled={placingOrder}>
              {placingOrder ? 'Redirecting to secure payment…' : 'Continue to payment'}
            </button>
            <p className="checkout-note">
              Free delivery on orders of {formatGBP(FREE_DELIVERY_THRESHOLD)} or more. Under that, delivery is {formatGBP(STANDARD_DELIVERY_CHARGE)}.
            </p>
            <p className="checkout-note">
              You will complete payment on Stripe's secure checkout. Your card details never touch our servers.
            </p>
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

/** Stripe: thank-you with `session_id` polls Edge Function `finalize-stripe-checkout` (sends order email via same Resend path). */
function ThankYouPage() {
  const params = new URLSearchParams(window.location.search);
  const orderFromUrl = decodeURIComponent(params.get('order') || '').trim();
  const sessionId = decodeURIComponent(params.get('session_id') || '').trim();
  const [resolvedOrderNumber, setResolvedOrderNumber] = useState(orderFromUrl);
  const [orderedItems, setOrderedItems] = useState([]);
  /** Set when finalize-stripe-checkout returns `email_sent` / `email_error` (new deploys only). */
  const [confirmationEmailSent, setConfirmationEmailSent] = useState(null);
  const [confirmationEmailError, setConfirmationEmailError] = useState('');
  const orderNumber = resolvedOrderNumber;
  const needsStripeResolve = Boolean(sessionId && !orderFromUrl && !resolvedOrderNumber);
  const [loadingOrder, setLoadingOrder] = useState(
    Boolean((orderFromUrl || sessionId) && isSupabaseConfigured && supabase)
  );
  const [finalizeError, setFinalizeError] = useState('');

  useEffect(() => {
    if (!needsStripeResolve || !isSupabaseConfigured || !supabase) {
      return undefined;
    }
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 24;

    async function pollStripeOrder() {
      setFinalizeError('');
      while (!cancelled && attempts < maxAttempts) {
        console.log('finalize_checkout_attempt', { attempt: attempts + 1, sessionId });
        const { data, error } = await supabase.functions.invoke(EDGE_FN_FINALIZE_STRIPE, {
          body: { session_id: sessionId },
        });
        if (cancelled) return;
        if (error) {
          console.error('finalize_checkout_failed', { error, data });
          if (!cancelled) {
            setLoadingOrder(false);
            setFinalizeError(await edgeFunctionErrorMessage(error, data));
          }
          return;
        }
        console.log('finalize_checkout_response', data || {});
        if (data?.order_number) {
          setResolvedOrderNumber(data.order_number);
          if (typeof data.email_sent === 'boolean') {
            setConfirmationEmailSent(data.email_sent);
            setConfirmationEmailError(
              typeof data.email_error === 'string' ? data.email_error : ''
            );
            if (data.email_sent) {
              console.log('order_confirmation_email_sent', { order_number: data.order_number });
            } else {
              console.warn('order_confirmation_email_failed', {
                order_number: data.order_number,
                email_error: data.email_error,
              });
            }
          }
          try {
            window.localStorage.setItem(CUSTOMER_CART_KEY, JSON.stringify([]));
            window.dispatchEvent(new Event('cart-updated'));
          } catch {
            /* ignore */
          }
          window.history.replaceState(
            {},
            '',
            `/thank-you?order=${encodeURIComponent(data.order_number)}`
          );
          return;
        }
        if (data?.error && typeof data.error === 'string') {
          if (!cancelled) {
            setLoadingOrder(false);
            setFinalizeError(data.error);
          }
          return;
        }
        attempts += 1;
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (!cancelled) setLoadingOrder(false);
    }

    pollStripeOrder();
    return () => {
      cancelled = true;
    };
  }, [needsStripeResolve, sessionId]);

  useEffect(() => {
    let cancelled = false;
    async function loadOrderItems() {
      const waitingForStripeOrder = Boolean(sessionId && !resolvedOrderNumber);
      if (waitingForStripeOrder) {
        setLoadingOrder(true);
        return;
      }
      if (!orderNumber || !isSupabaseConfigured || !supabase) {
        setLoadingOrder(false);
        return;
      }
      setLoadingOrder(true);
      const { data: orderRow, error: orderErr } = await supabase
        .from('orders')
        .select('id')
        .eq('order_number', orderNumber)
        .single();
      if (cancelled) return;
      if (orderErr || !orderRow?.id) {
        setLoadingOrder(false);
        setOrderedItems([]);
        return;
      }
      const { data: items, error: itemsErr } = await supabase
        .from('order_items')
        .select('id, product_name, quantity, line_total')
        .eq('order_id', orderRow.id)
        .order('created_at', { ascending: true });
      if (cancelled) return;
      setLoadingOrder(false);
      if (itemsErr || !Array.isArray(items)) {
        setOrderedItems([]);
        return;
      }
      setOrderedItems(items);
    }
    loadOrderItems();
    return () => {
      cancelled = true;
    };
  }, [orderNumber, sessionId, resolvedOrderNumber]);

  const waitingForStripeOrder = Boolean(sessionId && !resolvedOrderNumber);

  return (
    <div className="App">
      <SiteHeader />

      <main className="container section-space thank-you-page">
        <section className="thank-you-card">
          <p className="eyebrow">Order Confirmed</p>
          <h1>Thank You for Your Order</h1>
          <p>
            Your order has been placed successfully.
            {confirmationEmailSent === false ? (
              <>
                {' '}
                We could not send the order confirmation email automatically. Your order is
                still confirmed.
                {confirmationEmailError ? (
                  <>
                    {' '}
                    <span className="thank-you-email-debug">({confirmationEmailError})</span>
                  </>
                ) : null}{' '}
                If you need a receipt, contact us with your order number below.
              </>
            ) : (
              <>
                {' '}
                {confirmationEmailSent === true
                  ? 'We have sent an order confirmation to the email address you used at checkout—check your inbox and spam folder.'
                  : 'You should receive an order confirmation at the email you used at checkout. If it does not arrive, check spam or contact us with your order number.'}
              </>
            )}
          </p>
          {waitingForStripeOrder && !orderNumber && (
            <p className="thank-you-stripe-wait">Confirming your payment…</p>
          )}
          {finalizeError && (
            <p className="admin-login-error" role="alert">
              {finalizeError}
            </p>
          )}
          {orderNumber && (
            <p className="thank-you-order-pill">
              <span>Order</span> {orderNumber}
            </p>
          )}
          {loadingOrder ? (
            <p>Loading ordered products…</p>
          ) : orderedItems.length > 0 ? (
            <div className="thank-you-items">
              {orderedItems.map((item) => (
                <article className="thank-you-item" key={item.id}>
                  <p className="thank-you-item-name">
                    {item.product_name} <span>x {item.quantity}</span>
                  </p>
                  <strong className="thank-you-item-price">
                    {formatGBP(item.line_total || 0)}
                  </strong>
                </article>
              ))}
            </div>
          ) : (
            orderNumber && <p>Ordered products will appear here shortly.</p>
          )}
          <div className="thank-you-actions">
            <a className="primary-btn" href="/shop/new-in">Continue Shopping</a>
            <a className="ghost-btn" href="/">Back to Home</a>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

const ADMIN_SESSION_KEY = 'dstyluxeAdminLoggedIn';
const ADMIN_EMAIL_KEY = 'dstyluxeAdminEmail';

/** Fixed admin login (override with REACT_APP_ADMIN_EMAIL / REACT_APP_ADMIN_PASSWORD in .env.local). */
const ADMIN_LOGIN_EMAIL =
  (process.env.REACT_APP_ADMIN_EMAIL || 'admin@dstyluxe.co.uk').trim().toLowerCase();
const ADMIN_LOGIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'Dstyluxe2026!Admin';
const ADMIN_PRODUCTS_STORAGE_KEY = 'dstyluxeAdminProducts';
const ADMIN_CATEGORIES_STORAGE_KEY = 'dstyluxeAdminCategories';
const ADMIN_SUBCATEGORIES_STORAGE_KEY = 'dstyluxeAdminSubcategories';

/** Use * so older DBs (id, name, slug, description only) still load; UI handles missing image/sort_order. */
const ADMIN_CATEGORY_SELECT_FIELDS = '*';
/** Parent name via FK; order by name only for DBs without sort_order. */
const ADMIN_SUBCATEGORY_LIST_SELECT = '*, categories(name)';

function slugifyCatalog(str) {
  const s = str
    .trim()
    .toLowerCase()
    .replace(/[''"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || 'item';
}

function humanizeCatalogSlug(slug) {
  const s = String(slug || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '');
  if (!s) return '';
  return s
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function splitProductTags(raw) {
  if (raw == null || raw === '') return [];
  return String(raw)
    .split(/[,;|]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function safeDecodeURIComponent(str) {
  try {
    return decodeURIComponent(String(str ?? ''));
  } catch {
    return String(str ?? '');
  }
}

const SHOP_NEW_ARRIVAL_DAYS = 30;
/** On `/shop/new-in`, show products created in this window (plus anything still tagged `new-in`). */
const SHOP_NEW_IN_RECENT_DAYS = 90;
/** On `/shop/new-in`, show at most this many newest products per main category (plus the same cap for “other”). */
const SHOP_NEW_IN_PER_CATEGORY_MAX = 4;
const SHOP_NEW_IN_CATEGORY_BUCKETS = [
  'for-her',
  'for-him',
  'beauty',
  'fragrance',
  'luxury-collection',
  'trending',
  'sale',
];

function newInCreatedAtSortMs(item) {
  if (!item?.created_at) return 0;
  const t = new Date(item.created_at).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * New In grid: walk products newest-first; each product counts toward every category it belongs to,
 * up to `SHOP_NEW_IN_PER_CATEGORY_MAX` per bucket. Products with no bucket (e.g. only `new-in`) use a small “other” cap.
 */
function pickNewInProductsByCategoryCap(items) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const cap = SHOP_NEW_IN_PER_CATEGORY_MAX;
  const counts = Object.fromEntries(SHOP_NEW_IN_CATEGORY_BUCKETS.map((s) => [s, 0]));
  let other = 0;
  const sorted = [...items].sort((a, b) => {
    const d = newInCreatedAtSortMs(b) - newInCreatedAtSortMs(a);
    if (d !== 0) return d;
    return String(a.id ?? '').localeCompare(String(b.id ?? ''));
  });
  const out = [];
  for (const p of sorted) {
    const slugs = getProductSectionSlugs(p.section);
    const matched = SHOP_NEW_IN_CATEGORY_BUCKETS.filter((c) => slugs.includes(c));
    if (matched.length === 0) {
      if (other < cap) {
        other += 1;
        out.push(p);
      }
      continue;
    }
    if (!matched.some((c) => counts[c] < cap)) continue;
    for (const c of matched) {
      if (counts[c] < cap) counts[c] += 1;
    }
    out.push(p);
  }
  return out;
}

const SHOP_PRICE_BUCKETS = [
  { id: 'under-100', label: 'Under £100', test: (n) => n > 0 && n < 100 },
  { id: '100-300', label: '£100 – £300', test: (n) => n >= 100 && n < 300 },
  { id: '300-500', label: '£300 – £500', test: (n) => n >= 300 && n < 500 },
  { id: '500-1500', label: '£500 – £1,500', test: (n) => n >= 500 && n <= 1500 },
  { id: '1500-plus', label: '£1,500+', test: (n) => n > 1500 },
];

const ADMIN_PRODUCT_SECTIONS = [
  { slug: 'new-in', label: 'New In' },
  { slug: 'sale', label: 'Sale' },
  { slug: 'trending', label: 'Trending' },
  { slug: 'luxury-collection', label: 'Luxury Collection' },
  { slug: 'for-her', label: 'For Her' },
  { slug: 'for-him', label: 'For Him' },
  { slug: 'beauty', label: 'Beauty' },
  { slug: 'fragrance', label: 'Fragrance' },
];

const DEFAULT_ADMIN_CATEGORIES = ADMIN_PRODUCT_SECTIONS.map((sec, i) => ({
  id: `c-${sec.slug}`,
  name: sec.label,
  slug: sec.slug,
  description: '',
  image: '',
  sort_order: i,
}));

const DEFAULT_ADMIN_SUBCATEGORIES = [];

function mergeWithDefaultAdminCategories(categories) {
  const base = Array.isArray(categories) ? categories.filter((c) => c?.slug) : [];
  const seen = new Set(base.map((c) => String(c.slug).trim().toLowerCase()));
  const missingDefaults = DEFAULT_ADMIN_CATEGORIES.filter(
    (c) => !seen.has(String(c.slug).trim().toLowerCase())
  );
  return [...base, ...missingDefaults];
}

const ADMIN_PRODUCT_SELECT_FIELDS =
  'id, name, description, sku, material, stock, price, section, sub_category, dimensions, weight, image, gallery_images';

function adminSectionLabel(slug) {
  return ADMIN_PRODUCT_SECTIONS.find((s) => s.slug === slug)?.label || slug;
}

const BULK_IMPORT_DEFAULT_IMAGE =
  'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=200&q=80';

function normalizeBulkExcelHeader(cell) {
  const raw = String(cell ?? '')
    .trim()
    .toLowerCase()
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, '_');
  const aliases = {
    product_name: 'name',
    title: 'name',
    product: 'name',
    category: 'section',
    category_slug: 'section',
    main_image: 'image',
    image_url: 'image',
    photo: 'image',
    qty: 'stock',
    quantity: 'stock',
    subcategory: 'sub_category',
    sub: 'sub_category',
    gallery: 'gallery_images',
  };
  return aliases[raw] || raw;
}

function parseBulkGalleryCell(val) {
  if (val == null || val === '') return [];
  if (Array.isArray(val)) return val.map((x) => String(x).trim()).filter(Boolean);
  const str = String(val).trim();
  if (str.startsWith('[')) {
    try {
      const j = JSON.parse(str);
      return Array.isArray(j) ? j.map((x) => String(x).trim()).filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return str
    .split(/[|;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function bulkCellToNumber(val) {
  if (val == null || val === '') return NaN;
  if (typeof val === 'number') return val;
  const s = String(val)
    .replace(/[$€£¥,\s]/g, '')
    .replace(/[^0-9.-]/g, '');
  if (!s) return NaN;
  return Number.parseFloat(s);
}

function bulkCellToInt(val, fallback = 0) {
  if (val == null || val === '') return fallback;
  if (typeof val === 'number') return Math.max(0, Math.floor(val));
  const n = Number.parseInt(String(val).replace(/[^0-9-]/g, ''), 10);
  return Number.isNaN(n) || n < 0 ? fallback : n;
}

function parseBulkExcelMatrixToProductRows(matrix) {
  if (!matrix || matrix.length < 2) {
    return {
      products: [],
      rowErrors: [],
      fatalError: 'Add a header row and at least one product row.',
    };
  }
  const headerRow = matrix[0].map(normalizeBulkExcelHeader);
  const colIndex = {};
  headerRow.forEach((h, i) => {
    if (h && colIndex[h] === undefined) colIndex[h] = i;
  });
  const required = ['name', 'price', 'section'];
  const missing = required.filter((k) => colIndex[k] === undefined);
  if (missing.length) {
    return {
      products: [],
      rowErrors: [],
      fatalError: `Missing required column(s): ${missing.join(', ')}. Use “Download template” for the correct headers (name, price, section).`,
    };
  }

  const products = [];
  const rowErrors = [];

  for (let r = 1; r < matrix.length; r += 1) {
    const row = matrix[r] || [];
    const isBlank = !row.length || row.every((c) => c === '' || c == null);
    if (isBlank) continue;

    const get = (key) => {
      const idx = colIndex[key];
      if (idx === undefined) return '';
      return row[idx];
    };

    const name = String(get('name') ?? '').trim();
    if (!name) {
      rowErrors.push(`Row ${r + 1}: missing name`);
      continue;
    }

    const price = bulkCellToNumber(get('price'));
    if (Number.isNaN(price) || price < 0) {
      rowErrors.push(`Row ${r + 1}: invalid price`);
      continue;
    }

    const section = String(get('section') ?? '').trim();
    if (!section) {
      rowErrors.push(`Row ${r + 1}: missing section`);
      continue;
    }

    const stock = bulkCellToInt(get('stock'), 0);
    const imageRaw = String(get('image') ?? '').trim();
    const image = imageRaw || BULK_IMPORT_DEFAULT_IMAGE;
    const gallery_images = parseBulkGalleryCell(get('gallery_images'));

    let compare_at_price = null;
    const cap = get('compare_at_price');
    if (cap !== '' && cap != null) {
      const x = bulkCellToNumber(cap);
      if (!Number.isNaN(x) && x >= 0) compare_at_price = x;
    }

    const isFeaturedRaw = get('is_featured');
    let is_featured = false;
    if (isFeaturedRaw !== '' && isFeaturedRaw != null) {
      const s = String(isFeaturedRaw).trim().toLowerCase();
      is_featured = s === '1' || s === 'true' || s === 'yes' || s === 'y';
    }

    products.push({
      name,
      description: String(get('description') ?? '').trim() || null,
      sku: String(get('sku') ?? '').trim() || null,
      material: String(get('material') ?? '').trim() || null,
      stock,
      price,
      compare_at_price,
      section,
      sub_category: String(get('sub_category') ?? '').trim() || null,
      status: String(get('status') ?? '').trim() || 'active',
      image,
      gallery_images,
      dimensions: String(get('dimensions') ?? '').trim() || null,
      weight: String(get('weight') ?? '').trim() || null,
      brand: String(get('brand') ?? '').trim() || null,
      tags: String(get('tags') ?? '').trim() || null,
      barcode: String(get('barcode') ?? '').trim() || null,
      is_featured,
      meta_title: String(get('meta_title') ?? '').trim() || null,
      meta_description: String(get('meta_description') ?? '').trim() || null,
    });
  }

  if (products.length === 0 && rowErrors.length === 0) {
    return {
      products: [],
      rowErrors: [],
      fatalError: 'No product rows found (blank rows are ignored).',
    };
  }

  return { products, rowErrors, fatalError: undefined };
}

function downloadBulkProductsExcelTemplate() {
  const headers = [
    'name',
    'price',
    'section',
    'image',
    'sku',
    'stock',
    'sub_category',
    'description',
    'material',
    'dimensions',
    'weight',
    'gallery_images',
    'compare_at_price',
    'status',
    'brand',
    'tags',
    'barcode',
    'is_featured',
    'meta_title',
    'meta_description',
  ];
  const example = [
    'Silk wrap dress',
    189,
    'for-her',
    BULK_IMPORT_DEFAULT_IMAGE,
    'DL-SAMPLE-001',
    12,
    'Dresses',
    'Example row — replace with your products.',
    '100% silk',
    '',
    '400g',
    '',
    '',
    'active',
    '',
    '',
    '',
    'no',
    '',
    '',
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, example]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Products');
  XLSX.writeFile(wb, 'dstyluxe-products-bulk-import-template.xlsx');
}

const DEFAULT_ADMIN_PRODUCTS = [
  {
    id: 'seed-1',
    name: 'Silk Evening Dress',
    description: 'Fluid silk silhouette for evening.',
    sku: 'DL-HER-001',
    material: '100% silk',
    stock: 24,
    price: 1290,
    section: 'for-her',
    sub_category: 'Dresses',
    dimensions: '',
    weight: '380g',
    image: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=200&q=80',
    gallery_images: [],
  },
  {
    id: 'seed-2',
    name: 'Structured Leather Bag',
    description: 'Italian leather tote with structured lines.',
    sku: 'DL-BAG-014',
    material: 'Calf leather',
    stock: 12,
    price: 1540,
    section: 'for-her',
    sub_category: 'Bags',
    dimensions: '32 × 28 × 12 cm',
    weight: '890g',
    image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?auto=format&fit=crop&w=200&q=80',
    gallery_images: [],
  },
  {
    id: 'seed-3',
    name: 'Signature Fragrance 100ml',
    description: 'Eau de parfum, woody floral.',
    sku: 'DL-BEA-220',
    material: 'Glass / fragrance',
    stock: 80,
    price: 230,
    section: 'beauty',
    sub_category: 'Fragrance',
    dimensions: '14 × 5 × 5 cm',
    weight: '320g',
    image: 'https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=200&q=80',
    gallery_images: [],
  },
];

function readAdminProductsFromStorage() {
  try {
    const raw = window.localStorage.getItem(ADMIN_PRODUCTS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

function readAdminCategoriesFromStorage() {
  try {
    const raw = window.localStorage.getItem(ADMIN_CATEGORIES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

function readAdminSubcategoriesFromStorage() {
  try {
    const raw = window.localStorage.getItem(ADMIN_SUBCATEGORIES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

const ADMIN_NAV_ITEMS = [
  { slug: 'dashboard', href: '/admin/dashboard', label: 'Dashboard' },
  { slug: 'orders', href: '/admin/orders', label: 'Orders' },
  { slug: 'products', href: '/admin/products', label: 'Products' },
  { slug: 'categories', href: '/admin/categories', label: 'Categories' },
  { slug: 'subcategories', href: '/admin/subcategories', label: 'Subcategories' },
  { slug: 'customers', href: '/admin/customers', label: 'Customers' },
  { slug: 'reports', href: '/admin/reports', label: 'Reports' },
  { slug: 'contact', href: '/admin/contact', label: 'Contact' },
  { slug: 'newsletter', href: '/admin/newsletter', label: 'Newsletter' },
  { slug: 'services', href: '/admin/custom-services', label: 'Services' },
];

function AdminSidebar({ activeNav, onLogout }) {
  return (
    <aside className="admin-sidebar" aria-label="Admin navigation">
      <div className="admin-sidebar-brand">
        <img
          src="https://i.postimg.cc/DzSXQ0HK/Whats-App-Image-2026-03-31-at-5-48-43-PM-2-removebg-preview.png"
          alt=""
        />
        <span>Admin</span>
      </div>
      <nav className="admin-sidebar-nav">
        {ADMIN_NAV_ITEMS.map((item) => (
          <a
            key={item.slug}
            className={`admin-nav-link${activeNav === item.slug ? ' is-active' : ''}`}
            href={item.href}
          >
            {item.label}
          </a>
        ))}
      </nav>
      <div className="admin-sidebar-footer">
        <a className="admin-nav-link admin-nav-muted" href="/">
          View storefront
        </a>
        <button className="admin-logout-btn" type="button" onClick={onLogout}>
          Sign out
        </button>
      </div>
    </aside>
  );
}

function AdminLayout({ activeNav, title, children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const topbarRef = useRef(null);

  useEffect(() => {
    if (window.localStorage.getItem(ADMIN_SESSION_KEY) !== 'true') {
      window.location.href = '/admin';
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 821px)');
    const onMq = () => {
      if (mq.matches) setMobileMenuOpen(false);
    };
    mq.addEventListener('change', onMq);
    return () => mq.removeEventListener('change', onMq);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    const onPointerDown = (e) => {
      if (topbarRef.current && !topbarRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    window.localStorage.removeItem(ADMIN_SESSION_KEY);
    window.localStorage.removeItem(ADMIN_EMAIL_KEY);
    window.location.href = '/admin';
  };

  return (
    <div className="admin-app admin-shell">
      <AdminSidebar activeNav={activeNav} onLogout={handleLogout} />
      <div className="admin-main-wrap">
        <header className="admin-topbar" ref={topbarRef}>
          <div className="admin-topbar-inner">
            <div className="admin-topbar-brand-block">
              <span className="admin-topbar-panel-label">Admin panel</span>
              <h1 className="admin-topbar-title">{title}</h1>
            </div>
            <button
              type="button"
              className="admin-topbar-menu-toggle"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="admin-mobile-nav-menu"
              onClick={() => setMobileMenuOpen((o) => !o)}
            >
              {mobileMenuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
            </button>
          </div>
          {mobileMenuOpen && (
            <div
              id="admin-mobile-nav-menu"
              className="admin-topbar-dropdown"
              role="navigation"
              aria-label="Admin menu"
            >
              <nav className="admin-topbar-dropdown-nav">
                {ADMIN_NAV_ITEMS.map((item) => (
                  <a
                    key={item.slug}
                    className={`admin-topbar-dropdown-link${activeNav === item.slug ? ' is-active' : ''}`}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
              <div className="admin-topbar-dropdown-footer">
                <a className="admin-topbar-dropdown-link admin-topbar-dropdown-muted" href="/" onClick={() => setMobileMenuOpen(false)}>
                  View storefront
                </a>
                <button
                  type="button"
                  className="admin-topbar-dropdown-logout"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </header>
        <main className="admin-main">{children}</main>
      </div>
    </div>
  );
}

function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (window.localStorage.getItem(ADMIN_SESSION_KEY) === 'true') {
      window.location.href = '/admin/dashboard';
    }
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    setError('');
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError('Please enter email and password.');
      return;
    }
    if (!trimmedEmail.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    if (password.length < 4) {
      setError('Password must be at least 4 characters.');
      return;
    }
    if (
      trimmedEmail.toLowerCase() !== ADMIN_LOGIN_EMAIL ||
      password !== ADMIN_LOGIN_PASSWORD
    ) {
      setError('Invalid email or password.');
      return;
    }
    window.localStorage.setItem(ADMIN_SESSION_KEY, 'true');
    window.localStorage.setItem(ADMIN_EMAIL_KEY, trimmedEmail);
    window.location.href = '/admin/dashboard';
  };

  return (
    <div className="admin-app admin-login-page">
      <main className="admin-login-main">
        <article className="auth-card auth-card-single admin-auth-card">
          <p className="eyebrow">Administration</p>
          <h2>Admin sign in</h2>
          <p className="admin-login-lead">Sign in to access the Dstyluxe admin console.</p>
          <form onSubmit={handleSubmit}>
            <label>
              Email
              <input
                type="email"
                autoComplete="username"
                placeholder="admin@dstyluxe.co.uk"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label>
              Password
              <input
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            {error && <p className="admin-login-error" role="alert">{error}</p>}
            <button className="primary-btn admin-login-submit" type="submit">
              Sign in
            </button>
          </form>
        </article>
      </main>
    </div>
  );
}

function buildReportsFromProducts(productList) {
  const list = Array.isArray(productList) ? productList : [];
  const bySection = {};
  const unitsBySection = {};
  const stockHealth = { ok: 0, low: 0, out: 0 };
  const subCatCount = {};

  list.forEach((p) => {
    const sec = p.section || 'unknown';
    bySection[sec] = (bySection[sec] || 0) + 1;
    unitsBySection[sec] = (unitsBySection[sec] || 0) + (Number(p.stock) || 0);
    const st = Number(p.stock);
    if (Number.isNaN(st) || st === 0) stockHealth.out += 1;
    else if (st <= 5) stockHealth.low += 1;
    else stockHealth.ok += 1;
    const sub = (p.sub_category || '').trim() || 'Unspecified';
    subCatCount[sub] = (subCatCount[sub] || 0) + 1;
  });

  const sectionPie = Object.entries(bySection).map(([slug, value]) => ({
    name: adminSectionLabel(slug),
    value,
  }));

  const healthPie = [
    { name: 'Healthy stock (>5)', value: stockHealth.ok },
    { name: 'Low (1–5 units)', value: stockHealth.low },
    { name: 'Out of stock', value: stockHealth.out },
  ];

  const sectionBar = Object.entries(bySection)
    .map(([slug, count]) => ({
      name: adminSectionLabel(slug),
      products: count,
      units: unitsBySection[slug] || 0,
    }))
    .sort((a, b) => b.products - a.products);

  const subBar = Object.entries(subCatCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, count]) => ({
      name: name.length > 22 ? `${name.slice(0, 21)}…` : name,
      products: count,
    }));

  return { sectionPie, healthPie, sectionBar, subBar };
}

/** Last `monthsBack` calendar months (including current); each row { month, orders } for Recharts. */
function buildOrdersTrendSeries(orderRows, monthsBack = 12) {
  const n = Math.max(1, Math.min(24, Number(monthsBack) || 12));
  const now = new Date();
  const keys = [];
  const monthLabels = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    monthLabels.push(
      d.toLocaleString('en-US', { month: 'short', year: 'numeric' })
    );
  }
  const counts = Object.fromEntries(keys.map((k) => [k, 0]));
  (Array.isArray(orderRows) ? orderRows : []).forEach((o) => {
    if (!o?.created_at) return;
    const dt = new Date(o.created_at);
    if (Number.isNaN(dt.getTime())) return;
    const k = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    if (Object.prototype.hasOwnProperty.call(counts, k)) counts[k] += 1;
  });
  return keys.map((k, idx) => ({
    month: monthLabels[idx],
    orders: counts[k],
  }));
}

function computeProductStats(productList) {
  const list = Array.isArray(productList) ? productList : [];
  let inventoryValue = 0;
  let lowStock = 0;
  let outOfStock = 0;
  list.forEach((p) => {
    const stock = Number(p.stock);
    const price = Number(p.price);
    if (!Number.isNaN(stock) && !Number.isNaN(price)) {
      inventoryValue += stock * price;
    }
    if (Number.isNaN(stock)) return;
    if (stock === 0) outOfStock += 1;
    else if (stock <= 5) lowStock += 1;
  });
  return {
    productCount: list.length,
    inventoryValue,
    lowStock,
    outOfStock,
  };
}

function AdminDashboardPage() {
  const [adminEmail, setAdminEmail] = useState('');
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');
  const [stats, setStats] = useState({
    productCount: 0,
    categoryCount: 0,
    subcategoryCount: 0,
    inventoryValue: 0,
    lowStock: 0,
    outOfStock: 0,
  });

  useEffect(() => {
    setAdminEmail(window.localStorage.getItem(ADMIN_EMAIL_KEY) || 'Admin');
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadStats() {
      setStatsLoading(true);
      setStatsError('');
      if (isSupabaseConfigured && supabase) {
        const [prodRes, catRes, subRes] = await Promise.all([
          supabase.from('products').select('stock, price'),
          supabase.from('categories').select('id', { count: 'exact', head: true }),
          supabase.from('subcategories').select('id', { count: 'exact', head: true }),
        ]);
        if (cancelled) return;
        setStatsLoading(false);
        if (prodRes.error) {
          setStatsError(prodRes.error.message);
          return;
        }
        if (catRes.error) {
          setStatsError(catRes.error.message);
          return;
        }
        if (subRes.error) {
          setStatsError(subRes.error.message);
          return;
        }
        const pStats = computeProductStats(prodRes.data);
        setStats({
          ...pStats,
          categoryCount: catRes.count ?? 0,
          subcategoryCount: subRes.count ?? 0,
        });
        return;
      }
      const products = readAdminProductsFromStorage() ?? DEFAULT_ADMIN_PRODUCTS;
      const categories = readAdminCategoriesFromStorage() ?? DEFAULT_ADMIN_CATEGORIES;
      const subcategories = readAdminSubcategoriesFromStorage() ?? DEFAULT_ADMIN_SUBCATEGORIES;
      const pStats = computeProductStats(products);
      if (!cancelled) {
        setStatsLoading(false);
        setStats({
          ...pStats,
          categoryCount: categories.length,
          subcategoryCount: subcategories.length,
        });
      }
    }
    loadStats();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayName = adminEmail.includes('@') ? adminEmail.split('@')[0] : adminEmail;
  const formatMoney = (n) =>
    Number.isFinite(n)
      ? n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })
      : '£0';

  return (
    <AdminLayout activeNav="dashboard" title="Dashboard">
      <section className="admin-welcome">
        <p className="eyebrow">Signed in</p>
        <h2>
          Welcome, <span className="admin-welcome-name">{displayName}</span>
        </h2>
        <p className="admin-welcome-copy">
          Overview of your catalog. Stats refresh when you open this page.
        </p>
      </section>

      {statsError && (
        <p className="admin-login-error admin-dash-error" role="alert">
          {statsError}
        </p>
      )}

      <section className="admin-dash-stats" aria-label="Store statistics">
        <h2 className="admin-dash-section-title">At a glance</h2>
        <div className="admin-dash-grid">
          <article className="admin-stat-card">
            <p className="admin-stat-label">Products</p>
            <p className="admin-stat-value">
              {statsLoading ? '…' : stats.productCount}
            </p>
            <p className="admin-stat-hint">Active SKUs in catalog</p>
          </article>
          <article className="admin-stat-card">
            <p className="admin-stat-label">Categories</p>
            <p className="admin-stat-value">
              {statsLoading ? '…' : stats.categoryCount}
            </p>
            <p className="admin-stat-hint">Top-level groups</p>
          </article>
          <article className="admin-stat-card">
            <p className="admin-stat-label">Subcategories</p>
            <p className="admin-stat-value">
              {statsLoading ? '…' : stats.subcategoryCount}
            </p>
            <p className="admin-stat-hint">Under categories</p>
          </article>
          <article className="admin-stat-card admin-stat-card-accent">
            <p className="admin-stat-label">Inventory value</p>
            <p className="admin-stat-value admin-stat-value-sm">
              {statsLoading ? '…' : formatMoney(stats.inventoryValue)}
            </p>
            <p className="admin-stat-hint">Σ price × stock</p>
          </article>
          <article className={`admin-stat-card${stats.lowStock > 0 ? ' admin-stat-card-warn' : ''}`}>
            <p className="admin-stat-label">Low stock</p>
            <p className="admin-stat-value">
              {statsLoading ? '…' : stats.lowStock}
            </p>
            <p className="admin-stat-hint">1–5 units left</p>
          </article>
          <article className={`admin-stat-card${stats.outOfStock > 0 ? ' admin-stat-card-alert' : ''}`}>
            <p className="admin-stat-label">Out of stock</p>
            <p className="admin-stat-value">
              {statsLoading ? '…' : stats.outOfStock}
            </p>
            <p className="admin-stat-hint">Zero quantity</p>
          </article>
        </div>
      </section>

      <section className="admin-dash-quick" aria-label="Quick links">
        <h2 className="admin-dash-section-title">Quick actions</h2>
        <div className="admin-dash-quick-grid">
          <a className="admin-dash-quick-card" href="/admin/products/new">
            <span className="admin-dash-quick-title">Add product</span>
            <span className="admin-dash-quick-desc">Create a new SKU</span>
          </a>
          <a className="admin-dash-quick-card" href="/admin/categories/new">
            <span className="admin-dash-quick-title">Add category</span>
            <span className="admin-dash-quick-desc">New top-level group</span>
          </a>
          <a className="admin-dash-quick-card" href="/admin/subcategories/new">
            <span className="admin-dash-quick-title">Add subcategory</span>
            <span className="admin-dash-quick-desc">Narrow the catalog</span>
          </a>
          <a className="admin-dash-quick-card" href="/admin/products">
            <span className="admin-dash-quick-title">View products</span>
            <span className="admin-dash-quick-desc">Table &amp; inventory</span>
          </a>
          <a className="admin-dash-quick-card" href="/admin/reports">
            <span className="admin-dash-quick-title">Reports</span>
            <span className="admin-dash-quick-desc">Charts &amp; catalog analytics</span>
          </a>
        </div>
      </section>
    </AdminLayout>
  );
}

const ADMIN_REPORTS_PRODUCT_FIELDS = 'section, sub_category, stock, price';

function AdminReportsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ordersTrend, setOrdersTrend] = useState(null);
  const [ordersTrendError, setOrdersTrendError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      setOrdersTrendError('');
      if (isSupabaseConfigured && supabase) {
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        startDate.setHours(0, 0, 0, 0);
        const [productsRes, ordersRes] = await Promise.all([
          supabase.from('products').select(ADMIN_REPORTS_PRODUCT_FIELDS),
          supabase
            .from('orders')
            .select('created_at')
            .gte('created_at', startDate.toISOString()),
        ]);
        if (cancelled) return;
        setLoading(false);
        if (productsRes.error) {
          setError(productsRes.error.message);
          setProducts([]);
        } else {
          setProducts(productsRes.data ?? []);
        }
        if (ordersRes.error) {
          setOrdersTrendError(ordersRes.error.message);
          setOrdersTrend([]);
        } else {
          setOrdersTrendError('');
          setOrdersTrend(buildOrdersTrendSeries(ordersRes.data ?? []));
        }
        return;
      }
      setProducts(readAdminProductsFromStorage() ?? DEFAULT_ADMIN_PRODUCTS);
      setOrdersTrend(null);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const report = useMemo(() => buildReportsFromProducts(products), [products]);

  return (
    <AdminLayout activeNav="reports" title="Reports">
      <p className="admin-reports-intro">
        Visual summaries from your product catalog and order history. Product charts use live catalog
        data; the orders chart shows counts per month for the last twelve months when Supabase is
        connected.
      </p>

      {error && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p className="admin-reports-loading">Loading report data…</p>
      ) : (
        <div className="admin-reports-grid">
          <article className="admin-report-card">
            <h3>Products by category</h3>
            <p className="admin-report-sub">Share of SKUs per main category</p>
            {report.sectionPie.length === 0 ? (
              <p className="admin-report-empty">No products to chart.</p>
            ) : (
              <div className="admin-report-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={report.sectionPie}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={88}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {report.sectionPie.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={REPORT_CHART_COLORS[index % REPORT_CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </article>

          <article className="admin-report-card">
            <h3>Inventory health</h3>
            <p className="admin-report-sub">SKU count by stock level</p>
            {products.length === 0 ? (
              <p className="admin-report-empty">No products to chart.</p>
            ) : (
              <div className="admin-report-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={report.healthPie}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={48}
                      outerRadius={88}
                      paddingAngle={2}
                    >
                      {report.healthPie.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={
                            index === 0
                              ? '#6b9e7d'
                              : index === 1
                                ? '#deac4d'
                                : '#c47d6f'
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </article>

          <article className="admin-report-card">
            <h3>SKU count &amp; units by category</h3>
            <p className="admin-report-sub">Columns: products · Line: total units in stock</p>
            {report.sectionBar.length === 0 ? (
              <p className="admin-report-empty">No products to chart.</p>
            ) : (
              <div className="admin-report-chart admin-report-chart-tall">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={report.sectionBar} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(23,21,19,0.08)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-28} textAnchor="end" height={70} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="products" name="Products" fill="#b88a36" radius={[6, 6, 0, 0]} />
                    <Line
                      type="monotone"
                      dataKey="units"
                      name="Units in stock"
                      stroke="#5c5346"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </article>

          <article className="admin-report-card">
            <h3>Top subcategories (by SKU count)</h3>
            <p className="admin-report-sub">From product sub-category field</p>
            {report.subBar.length === 0 ? (
              <p className="admin-report-empty">No sub-category data yet.</p>
            ) : (
              <div className="admin-report-chart admin-report-chart-tall">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={report.subBar}
                    layout="vertical"
                    margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(23,21,19,0.08)" />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="products" name="Products" fill="#deac4d" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </article>

          <article className="admin-report-card admin-report-card-wide">
            <h3>Orders by month</h3>
            <p className="admin-report-sub">Number of orders placed each month (last 12 months)</p>
            {ordersTrend == null ? (
              <p className="admin-report-empty">
                Connect Supabase and sign in to see order volume from your store.
              </p>
            ) : ordersTrend.length === 0 ? (
              <p className="admin-report-empty" role="alert">
                Order history could not be loaded for this chart
                {ordersTrendError ? `: ${ordersTrendError}` : '.'}
              </p>
            ) : (
              <div className="admin-report-chart admin-report-chart-tall">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={ordersTrend} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(23,21,19,0.08)" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="orders"
                      name="Orders"
                      stroke="#b88a36"
                      strokeWidth={2}
                      dot={{ fill: '#b88a36', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </article>
        </div>
      )}
    </AdminLayout>
  );
}

function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [orderItemsByOrderId, setOrderItemsByOrderId] = useState({});
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [listError, setListError] = useState('');
  const [activeOrder, setActiveOrder] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function loadOrders() {
      if (isSupabaseConfigured && supabase) {
        setLoading(true);
        setListError('');
        const { data, error } = await supabase
          .from('orders')
          .select(
            'id, order_number, status, payment_status, subtotal, total_amount, created_at, customers(full_name, email, phone, street_address, city, state, zip_code, country)'
          )
          .order('created_at', { ascending: false });
        if (cancelled) return;
        setLoading(false);
        if (error) {
          setListError(error.message);
          setOrders([]);
          return;
        }
        const orderRows = Array.isArray(data) ? data : [];
        setOrders(orderRows);
        const orderIds = orderRows.map((o) => o.id).filter(Boolean);
        if (orderIds.length > 0) {
          const { data: itemsData, error: itemsError } = await supabase
            .from('order_items')
            .select('order_id, product_name, quantity, unit_price, line_total, variation_option_a, variation_option_b')
            .in('order_id', orderIds)
            .order('created_at', { ascending: true });
          if (!itemsError && Array.isArray(itemsData)) {
            const grouped = {};
            itemsData.forEach((item) => {
              const key = String(item.order_id || '');
              if (!key) return;
              if (!grouped[key]) grouped[key] = [];
              grouped[key].push(item);
            });
            if (!cancelled) setOrderItemsByOrderId(grouped);
          } else if (!cancelled) {
            setOrderItemsByOrderId({});
          }
        } else {
          setOrderItemsByOrderId({});
        }
        return;
      }
      if (!cancelled) {
        setLoading(false);
        setOrders([]);
        setListError('Connect Supabase to view placed orders.');
      }
    }
    loadOrders();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AdminLayout activeNav="orders" title="Orders">
      <div className="admin-products-toolbar">
        <p className="admin-products-count">
          {loading ? 'Loading orders…' : `${orders.length} orders`}
        </p>
      </div>

      {listError && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {listError}
        </p>
      )}

      <div className="admin-table-wrap">
        {!loading && orders.length === 0 && !listError ? (
          <p className="admin-products-empty">No orders placed yet.</p>
        ) : (
          <table className="admin-products-table">
            <thead>
              <tr>
                <th scope="col">Order</th>
                <th scope="col">Customer</th>
                <th scope="col">Products</th>
                <th scope="col">Total</th>
                <th scope="col">Placed</th>
                <th scope="col" className="admin-table-actions-col">Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((row) => (
                <tr key={row.id}>
                  <td>{row.order_number || '—'}</td>
                  <td>
                    {row.customers?.full_name || '—'}
                    {row.customers?.email ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{row.customers.email}</div>
                    ) : null}
                  </td>
                  <td>
                    {(orderItemsByOrderId[String(row.id)] || []).length > 0 ? (
                      <div>
                        {(orderItemsByOrderId[String(row.id)] || []).map((item, idx) => (
                          <div key={`${row.id}-item-${idx}`}>
                            {item.product_name} x {item.quantity}
                          </div>
                        ))}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{formatGBP(row.total_amount || 0)}</td>
                  <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                  <td className="admin-table-actions-cell">
                    <button
                      type="button"
                      className="admin-icon-btn admin-icon-btn--edit"
                      title="View order details"
                      aria-label={`View ${row.order_number || 'order'} details`}
                      onClick={() => setActiveOrder(row)}
                    >
                      <AdminIconEye />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {activeOrder && (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onClick={() => setActiveOrder(null)}
        >
          <div
            className="admin-modal admin-modal--confirm admin-order-details-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-order-details-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="admin-order-details-title" className="admin-modal-title">
              Order Details
            </h2>
            <p className="admin-modal-text"><strong>Order:</strong> {activeOrder.order_number || '—'}</p>
            <p className="admin-modal-text">
              <strong>Placed:</strong>{' '}
              {activeOrder.created_at ? new Date(activeOrder.created_at).toLocaleString() : '—'}
            </p>
            <p className="admin-modal-text"><strong>Customer:</strong> {activeOrder.customers?.full_name || '—'}</p>
            <p className="admin-modal-text"><strong>Email:</strong> {activeOrder.customers?.email || '—'}</p>
            {activeOrder.customers?.phone && (
              <p className="admin-modal-text"><strong>Phone:</strong> {activeOrder.customers.phone}</p>
            )}
            <p className="admin-modal-text">
              <strong>Address:</strong>{' '}
              {[activeOrder.customers?.street_address, activeOrder.customers?.city, activeOrder.customers?.state, activeOrder.customers?.zip_code, activeOrder.customers?.country]
                .filter(Boolean)
                .join(', ') || '—'}
            </p>

            <div className="admin-table-wrap admin-order-details-table-wrap">
              <table className="admin-products-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(orderItemsByOrderId[String(activeOrder.id)] || []).map((item, idx) => (
                    <tr key={`order-detail-item-${idx}`}>
                      <td>
                        {item.product_name}
                        {(item.variation_option_a || item.variation_option_b) && (
                          <div className="admin-order-variation-note">
                            {[item.variation_option_a, item.variation_option_b].filter(Boolean).join(' / ')}
                          </div>
                        )}
                      </td>
                      <td>{item.quantity}</td>
                      <td>{formatGBP(item.unit_price || 0)}</td>
                      <td>{formatGBP(item.line_total || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="admin-modal-text admin-order-total-row">
              <strong>Subtotal:</strong> {formatGBP(activeOrder.subtotal || 0)}
            </p>
            <p className="admin-modal-text">
              <strong>Total:</strong> {formatGBP(activeOrder.total_amount || 0)}
            </p>
            <div className="admin-modal-actions">
              <button type="button" className="primary-btn" onClick={() => setActiveOrder(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function AdminProductsPage() {
  const [products, setProducts] = useState(() =>
    isSupabaseConfigured ? [] : readAdminProductsFromStorage() ?? DEFAULT_ADMIN_PRODUCTS
  );
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [listError, setListError] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState({});
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionError, setActionError] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkExcelInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function loadProducts() {
      if (isSupabaseConfigured && supabase) {
        setLoading(true);
        setListError('');
        const { data, error } = await supabase
          .from('products')
          .select(ADMIN_PRODUCT_SELECT_FIELDS)
          .order('created_at', { ascending: false });
        if (cancelled) return;
        setLoading(false);
        if (error) {
          setListError(error.message);
          setProducts([]);
          return;
        }
        setProducts(data ?? []);
        return;
      }
      setProducts(readAdminProductsFromStorage() ?? DEFAULT_ADMIN_PRODUCTS);
    }
    loadProducts();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadFilterOptions() {
      if (isSupabaseConfigured && supabase) {
        const [{ data: categories }, { data: subcategories }] = await Promise.all([
          supabase.from('categories').select('name, slug, sort_order').order('sort_order', { ascending: true }),
          supabase
            .from('subcategories')
            .select('name, categories(slug)')
            .order('name', { ascending: true }),
        ]);
        if (cancelled) return;
        setCategoryOptions(Array.isArray(categories) ? categories.filter((c) => c?.slug) : []);
        const grouped = {};
        (Array.isArray(subcategories) ? subcategories : []).forEach((row) => {
          const slug = row?.categories?.slug;
          const name = (row?.name || '').trim();
          if (!slug || !name) return;
          if (!grouped[slug]) grouped[slug] = [];
          if (!grouped[slug].includes(name)) grouped[slug].push(name);
        });
        setSubcategoriesByCategory(grouped);
        return;
      }

      const localCategories = readAdminCategoriesFromStorage() ?? DEFAULT_ADMIN_CATEGORIES;
      if (!cancelled) {
        setCategoryOptions(localCategories.map((c) => ({ name: c.name, slug: c.slug })));
      }
    }
    loadFilterOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      window.localStorage.setItem(ADMIN_PRODUCTS_STORAGE_KEY, JSON.stringify(products));
    }
  }, [products]);

  useEffect(() => {
    if (!pendingDelete) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !deleteSubmitting) setPendingDelete(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pendingDelete, deleteSubmitting]);

  const fallbackCategoryFilterOptions = useMemo(() => {
    const set = new Set();
    products.forEach((row) => {
      const section = row?.section;
      if (section) set.add(section);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const categoryFilterOptions = useMemo(() => {
    if (categoryOptions.length > 0) return categoryOptions.map((c) => c.slug);
    return fallbackCategoryFilterOptions;
  }, [categoryOptions, fallbackCategoryFilterOptions]);

  const subcategoryFilterOptions = useMemo(() => {
    const set = new Set();
    if (categoryFilter === 'all') {
      Object.values(subcategoriesByCategory).forEach((names) => {
        (names || []).forEach((name) => set.add(name));
      });
    } else {
      (subcategoriesByCategory[categoryFilter] || []).forEach((name) => set.add(name));
    }
    products.forEach((row) => {
      const belongs = categoryFilter === 'all' || (row?.section || '') === categoryFilter;
      if (belongs && row?.sub_category) set.add(row.sub_category);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [subcategoriesByCategory, categoryFilter, products]);

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return products.filter((row) => {
      const matchesCategory = categoryFilter === 'all' || (row?.section || '') === categoryFilter;
      if (!matchesCategory) return false;
      const matchesSubcategory =
        subcategoryFilter === 'all' || (row?.sub_category || '') === subcategoryFilter;
      if (!matchesSubcategory) return false;
      if (!q) return true;
      const haystack =
        `${row?.name || ''} ${row?.sku || ''} ${row?.sub_category || ''} ${adminSectionLabel(row?.section || '')}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [products, categoryFilter, subcategoryFilter, searchQuery]);

  const handleBulkExcelSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setActionError('');
    setBulkUploading(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheetName = wb.SheetNames[0];
      if (!sheetName) {
        setActionError('This workbook has no sheets.');
        return;
      }
      const sheet = wb.Sheets[sheetName];
      const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true });
      const { products: bulkRows, rowErrors, fatalError } = parseBulkExcelMatrixToProductRows(matrix);
      if (fatalError) {
        setActionError(fatalError);
        return;
      }
      if (!bulkRows.length) {
        setActionError(rowErrors.length ? rowErrors.join('\n') : 'Nothing to import.');
        return;
      }

      if (isSupabaseConfigured && supabase) {
        const chunkSize = 50;
        for (let i = 0; i < bulkRows.length; i += chunkSize) {
          const chunk = bulkRows.slice(i, i + chunkSize);
          const { error } = await supabase.from('products').insert(chunk);
          if (error) {
            setActionError(error.message);
            return;
          }
        }
        const { data: refreshed, error: refErr } = await supabase
          .from('products')
          .select(ADMIN_PRODUCT_SELECT_FIELDS)
          .order('created_at', { ascending: false });
        if (refErr) {
          setListError(refErr.message);
        } else {
          setProducts(refreshed ?? []);
        }
        const skippedNote = rowErrors.length ? ` ${rowErrors.length} row(s) skipped.` : '';
        window.dispatchEvent(
          new CustomEvent('admin-toast', {
            detail: `Imported ${bulkRows.length} product(s) from Excel.${skippedNote}`,
          })
        );
        if (rowErrors.length) {
          setActionError(
            `Some rows were skipped:\n${rowErrors.slice(0, 15).join('\n')}${rowErrors.length > 15 ? '\n…' : ''}`
          );
        }
        return;
      }

      const prev = readAdminProductsFromStorage() ?? DEFAULT_ADMIN_PRODUCTS;
      const base = Date.now();
      const newRows = bulkRows.map((p, idx) => ({
        id: `p-${base}-${idx}`,
        name: p.name,
        description: p.description || '',
        sku: p.sku || '',
        material: p.material || '',
        stock: p.stock,
        price: p.price,
        section: p.section,
        sub_category: p.sub_category || '',
        dimensions: p.dimensions || '',
        weight: p.weight || '',
        image: p.image,
        gallery_images: Array.isArray(p.gallery_images) ? p.gallery_images : [],
        product_type: 'simple',
        variations: [],
      }));
      window.localStorage.setItem(ADMIN_PRODUCTS_STORAGE_KEY, JSON.stringify([...newRows, ...prev]));
      setProducts((list) => [...newRows, ...list]);
      window.dispatchEvent(
        new CustomEvent('admin-toast', {
          detail: `Imported ${newRows.length} product(s) from Excel.${rowErrors.length ? ` ${rowErrors.length} row(s) skipped.` : ''}`,
        })
      );
      if (rowErrors.length) {
        setActionError(
          `Some rows were skipped:\n${rowErrors.slice(0, 15).join('\n')}${rowErrors.length > 15 ? '\n…' : ''}`
        );
      }
    } catch (err) {
      setActionError(err?.message || 'Could not read that Excel file.');
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <AdminLayout activeNav="products" title="Products">
      <div className="admin-products-toolbar">
        <p className="admin-products-count">
          {loading ? 'Loading products…' : `${filteredProducts.length} of ${products.length} products`}
        </p>
        <div className="admin-products-toolbar-actions">
          <div className="admin-bulk-excel">
            <input
              ref={bulkExcelInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              className="admin-bulk-excel-input"
              aria-hidden
              tabIndex={-1}
              onChange={handleBulkExcelSelected}
            />
            <button
              type="button"
              className="ghost-btn"
              disabled={loading || bulkUploading}
              aria-label="Bulk import products from an Excel file"
              onClick={() => bulkExcelInputRef.current?.click()}
            >
              {bulkUploading ? 'Importing…' : 'Bulk upload (Excel)'}
            </button>
            <button type="button" className="ghost-btn" onClick={downloadBulkProductsExcelTemplate}>
              Download template
            </button>
          </div>
          <a className="primary-btn" href="/admin/products/new">
            Add product
          </a>
        </div>
      </div>
      <p className="admin-bulk-excel-hint">
        Bulk import from <strong>.xlsx</strong> or <strong>.xls</strong>. Required columns:{' '}
        <code>name</code>, <code>price</code>, <code>section</code> (category slug, e.g. <code>for-her</code>). Leave{' '}
        <code>image</code> blank to use a default placeholder. Optional: <code>sku</code>, <code>stock</code>,{' '}
        <code>sub_category</code>, <code>description</code>, <code>gallery_images</code> (URLs separated by{' '}
        <code>|</code> or <code>;</code>), and more — see the template.
      </p>
      <div className="admin-subcategory-filters">
        <label>
          Category
          <select
            value={categoryFilter}
            onChange={(event) => {
              setCategoryFilter(event.target.value);
              setSubcategoryFilter('all');
            }}
            disabled={loading}
          >
            <option value="all">All categories</option>
            {categoryFilterOptions.map((slug) => (
              <option key={slug} value={slug}>
                {adminSectionLabel(slug)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Sub-category
          <select
            value={subcategoryFilter}
            onChange={(event) => setSubcategoryFilter(event.target.value)}
            disabled={loading}
          >
            <option value="all">All sub-categories</option>
            {subcategoryFilterOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Search
          <input
            type="search"
            placeholder="Search product..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            disabled={loading}
          />
        </label>
      </div>

      {listError && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {listError}
        </p>
      )}
      {actionError && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {actionError}
        </p>
      )}

      <div className="admin-table-wrap">
        {!loading && products.length === 0 && !listError ? (
          <p className="admin-products-empty">
            No products yet.{' '}
            <a href="/admin/products/new">Add a product</a> or run the SQL in Supabase (see{' '}
            <code>supabase/schema.sql</code>).
          </p>
        ) : !loading && filteredProducts.length === 0 && !listError ? (
          <p className="admin-products-empty">No products match your filters.</p>
        ) : (
          <table className="admin-products-table">
            <thead>
              <tr>
                <th scope="col">Image</th>
                <th scope="col">Name</th>
                <th scope="col">SKU</th>
                <th scope="col">Category</th>
                <th scope="col">Sub</th>
                <th scope="col">Stock</th>
                <th scope="col">Price</th>
                <th scope="col" className="admin-table-actions-col">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((item) => (
                <tr key={item.id}>
                  <td>
                    <img className="admin-product-thumb" src={item.image} alt="" />
                  </td>
                  <td>{item.name}</td>
                  <td>{item.sku || '—'}</td>
                  <td>{adminSectionLabel(item.section)}</td>
                  <td>{item.sub_category || '—'}</td>
                  <td>{item.stock != null ? item.stock : '—'}</td>
                  <td>{formatGBP(item.price)}</td>
                  <td className="admin-table-actions-cell">
                    <div className="admin-table-actions">
                      <a
                        className="admin-icon-btn admin-icon-btn--edit"
                        href={`/admin/products/edit/${encodeURIComponent(item.id)}`}
                        title="Edit product"
                        aria-label={`Edit ${item.name}`}
                      >
                        <AdminIconEdit />
                      </a>
                      <button
                        type="button"
                        className="admin-icon-btn admin-icon-btn--danger"
                        title="Delete product"
                        aria-label={`Delete ${item.name}`}
                        onClick={() => {
                          setActionError('');
                          setPendingDelete({ id: item.id, name: item.name });
                        }}
                      >
                        <AdminIconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {pendingDelete && (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onClick={() => !deleteSubmitting && setPendingDelete(null)}
        >
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-product-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-modal-icon-wrap admin-modal-icon-wrap--warn">
              <AdminIconTrash />
            </div>
            <h3 id="delete-product-title">Delete product?</h3>
            <p>
              <strong>{pendingDelete.name}</strong> will be permanently deleted.
            </p>
            <div className="admin-modal-actions">
              <button
                type="button"
                className="ghost-btn"
                disabled={deleteSubmitting}
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn admin-danger-btn"
                disabled={deleteSubmitting}
                onClick={async () => {
                  if (!pendingDelete) return;
                  const idToRemove = pendingDelete.id;
                  const deletedName = pendingDelete.name;
                  setDeleteSubmitting(true);
                  setActionError('');

                  if (isSupabaseConfigured && supabase) {
                    const { error } = await supabase.from('products').delete().eq('id', idToRemove);
                    if (error) {
                      setDeleteSubmitting(false);
                      setActionError(error.message);
                      return;
                    }
                  }

                  setProducts((prev) => prev.filter((row) => String(row.id) !== String(idToRemove)));
                  setDeleteSubmitting(false);
                  setPendingDelete(null);
                  window.dispatchEvent(new CustomEvent('admin-toast', { detail: `${deletedName} deleted.` }));
                }}
              >
                {deleteSubmitting ? 'Deleting…' : 'Delete product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const EMPTY_PRODUCT_FORM = {
  name: '',
  description: '',
  sku: '',
  material: '',
  stock: '0',
  price: '',
  section: 'for-her',
  sub_category: '',
  dimensions: '',
  weight: '',
};

const MAX_GALLERY_FILES = 8;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_LOCAL_IMAGE_BYTES = 2 * 1024 * 1024;
const EMPTY_VARIATION_OPTION = { name: '', values: '' };

function buildVariationMatrix(optionAValues, optionBValues, prevRows = []) {
  const prevMap = new Map((prevRows || []).map((row) => [row.key, row]));
  const left = optionAValues.length > 0 ? optionAValues : ['Default'];
  const right = optionBValues.length > 0 ? optionBValues : ['Default'];
  const result = [];
  left.forEach((a) => {
    right.forEach((b) => {
      const key = `${a}__${b}`;
      const label = optionBValues.length > 0 ? `${a} / ${b}` : a;
      const prev = prevMap.get(key);
      result.push({
        key,
        optionA: a,
        optionB: b,
        label,
        price: prev?.price || '',
        stock: prev?.stock || '0',
        sku: prev?.sku || '',
        image: prev?.image || '',
        file: prev?.file || null,
      });
    });
  });
  return result;
}

function AdminProductNewPage() {
  const editProductId = getAdminProductEditIdFromPath();
  const isEditMode = Boolean(editProductId);
  const [form, setForm] = useState(EMPTY_PRODUCT_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [formLoading, setFormLoading] = useState(isEditMode);
  const [productType, setProductType] = useState('simple');
  const [variationOptions, setVariationOptions] = useState([
    { ...EMPTY_VARIATION_OPTION, name: 'Color' },
    { ...EMPTY_VARIATION_OPTION, name: 'Size' },
  ]);
  const [variationRows, setVariationRows] = useState([]);
  const [mainFile, setMainFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState(DEFAULT_ADMIN_CATEGORIES);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [taxLoading, setTaxLoading] = useState(true);
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [existingGalleryImages, setExistingGalleryImages] = useState([]);

  const mainPreviewUrl = useMemo(() => (mainFile ? URL.createObjectURL(mainFile) : ''), [mainFile]);
  const galleryPreviewUrls = useMemo(
    () => galleryFiles.map((f) => URL.createObjectURL(f)),
    [galleryFiles]
  );

  useEffect(() => {
    return () => {
      if (mainPreviewUrl) URL.revokeObjectURL(mainPreviewUrl);
      galleryPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [mainPreviewUrl, galleryPreviewUrls]);

  useEffect(() => {
    let cancelled = false;
    async function loadEditProduct() {
      if (!isEditMode) {
        setFormLoading(false);
        return;
      }

      if (isSupabaseConfigured && supabase) {
        const { data, error } = await supabase
          .from('products')
          .select(ADMIN_PRODUCT_SELECT_FIELDS)
          .eq('id', editProductId)
          .single();
        if (cancelled) return;
        if (error || !data) {
          setFormError(error?.message || 'Product not found.');
          setFormLoading(false);
          return;
        }
        setForm({
          name: data.name || '',
          description: data.description || '',
          sku: data.sku || '',
          material: data.material || '',
          stock: String(data.stock ?? 0),
          price: String(data.price ?? ''),
          section: data.section || '',
          sub_category: data.sub_category || '',
          dimensions: data.dimensions || '',
          weight: data.weight || '',
        });
        const { data: dbVariations } = await supabase
          .from('product_variations')
          .select('option_a, option_b, price, stock, sku, image_url')
          .eq('product_id', editProductId)
          .order('option_a', { ascending: true })
          .order('option_b', { ascending: true });
        if (Array.isArray(dbVariations) && dbVariations.length > 0) {
          setProductType('variable');
          const optionA = Array.from(new Set(dbVariations.map((v) => v.option_a).filter(Boolean)));
          const optionB = Array.from(new Set(dbVariations.map((v) => v.option_b).filter(Boolean)));
          setVariationOptions([
            { name: 'Color', values: optionA.join(', ') },
            { name: 'Size', values: optionB.join(', ') },
          ]);
          setVariationRows(
            dbVariations.map((v) => ({
              key: `${v.option_a || 'Default'}__${v.option_b || 'Default'}`,
              optionA: v.option_a || 'Default',
              optionB: v.option_b || 'Default',
              label: v.option_b ? `${v.option_a} / ${v.option_b}` : v.option_a || 'Default',
              price: String(v.price ?? ''),
              stock: String(v.stock ?? 0),
              sku: v.sku || '',
              image: v.image_url || '',
              file: null,
            }))
          );
        } else {
          setProductType('simple');
          setVariationRows([]);
        }
        setExistingImageUrl(data.image || '');
        setExistingGalleryImages(Array.isArray(data.gallery_images) ? data.gallery_images : []);
        setFormLoading(false);
        return;
      }

      const list = readAdminProductsFromStorage() ?? DEFAULT_ADMIN_PRODUCTS;
      const row = list.find((item) => String(item.id) === String(editProductId));
      if (!cancelled) {
        if (!row) {
          setFormError('Product not found.');
          setFormLoading(false);
          return;
        }
        setForm({
          name: row.name || '',
          description: row.description || '',
          sku: row.sku || '',
          material: row.material || '',
          stock: String(row.stock ?? 0),
          price: String(row.price ?? ''),
          section: row.section || '',
          sub_category: row.sub_category || '',
          dimensions: row.dimensions || '',
          weight: row.weight || '',
        });
        const localVariations = Array.isArray(row.variations) ? row.variations : [];
        if (localVariations.length > 0) {
          setProductType('variable');
          const optionA = Array.from(new Set(localVariations.map((v) => v?.optionA).filter(Boolean)));
          const optionB = Array.from(new Set(localVariations.map((v) => v?.optionB).filter(Boolean)));
          setVariationOptions([
            { name: 'Color', values: optionA.join(', ') },
            { name: 'Size', values: optionB.join(', ') },
          ]);
          setVariationRows(
            localVariations.map((v) => ({
              key: `${v.optionA || 'Default'}__${v.optionB || 'Default'}`,
              optionA: v.optionA || 'Default',
              optionB: v.optionB || 'Default',
              label: v.optionB ? `${v.optionA} / ${v.optionB}` : v.optionA || 'Default',
              price: String(v?.price ?? ''),
              stock: String(v?.stock ?? '0'),
              sku: v?.sku || '',
              image: v?.image || '',
              file: null,
            }))
          );
        } else {
          setProductType('simple');
          setVariationRows([]);
        }
        setExistingImageUrl(row.image || '');
        setExistingGalleryImages(Array.isArray(row.gallery_images) ? row.gallery_images : []);
        setFormLoading(false);
      }
    }
    loadEditProduct();
    return () => {
      cancelled = true;
    };
  }, [editProductId, isEditMode]);

  useEffect(() => {
    let cancelled = false;
    async function loadTaxonomy() {
      if (isSupabaseConfigured && supabase) {
        const [{ data: categories }, { data: subcategories }] = await Promise.all([
          supabase.from('categories').select('id, name, slug').order('name', { ascending: true }),
          supabase
            .from('subcategories')
            .select('id, name, slug, category_id')
            .order('name', { ascending: true }),
        ]);

        if (cancelled) return;

        const loadedCats =
          Array.isArray(categories) && categories.length > 0 ? categories : DEFAULT_ADMIN_CATEGORIES;
        const cats = mergeWithDefaultAdminCategories(loadedCats);
        setCategoryOptions(cats);
        setSubcategoryOptions(Array.isArray(subcategories) ? subcategories : []);
        setForm((prev) => {
          const hasCurrent = cats.some((c) => c.slug === prev.section);
          if (hasCurrent) return prev;
          return { ...prev, section: cats[0]?.slug || '', sub_category: '' };
        });
        setTaxLoading(false);
        return;
      }

      if (!cancelled) {
        const localCategories = readAdminCategoriesFromStorage() ?? DEFAULT_ADMIN_CATEGORIES;
        const localSubcategories = readAdminSubcategoriesFromStorage() ?? DEFAULT_ADMIN_SUBCATEGORIES;
        const cats = mergeWithDefaultAdminCategories(localCategories);
        setCategoryOptions(cats);
        setSubcategoryOptions(localSubcategories);
        setForm((prev) => {
          const hasCurrent = cats.some((c) => c.slug === prev.section);
          if (hasCurrent) return prev;
          return { ...prev, section: cats[0]?.slug || '', sub_category: '' };
        });
        setTaxLoading(false);
      }
    }

    loadTaxonomy();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeCategory = categoryOptions.find((c) => c.slug === form.section);
  const activeSubcategoryOptions = subcategoryOptions.filter(
    (row) => String(row.category_id) === String(activeCategory?.id || '')
  );

  useEffect(() => {
    if (productType !== 'variable') return;
    const optionAValues = (variationOptions[0]?.values || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    const optionBValues = (variationOptions[1]?.values || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    setVariationRows((prev) => buildVariationMatrix(optionAValues, optionBValues, prev));
  }, [productType, variationOptions]);

  const onMainFileInput = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormError('Choose an image file (JPEG, PNG, WebP, or GIF).');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setFormError('Each image must be 5MB or smaller.');
      return;
    }
    setFormError('');
    setMainFile(file);
  };

  const onGalleryInput = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    const images = picked.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) return;
    const tooBig = images.find((f) => f.size > MAX_IMAGE_BYTES);
    if (tooBig) {
      setFormError('Each image must be 5MB or smaller.');
      return;
    }
    setFormError('');
    setGalleryFiles((prev) => [...prev, ...images].slice(0, MAX_GALLERY_FILES));
  };

  const removeGalleryAt = (index) => {
    setGalleryFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onMainDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file?.type.startsWith('image/')) return;
    if (file.size > MAX_IMAGE_BYTES) {
      setFormError('Each image must be 5MB or smaller.');
      return;
    }
    setFormError('');
    setMainFile(file);
  };

  const onGalleryDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) return;
    if (files.some((f) => f.size > MAX_IMAGE_BYTES)) {
      setFormError('Each image must be 5MB or smaller.');
      return;
    }
    setFormError('');
    setGalleryFiles((prev) => [...prev, ...files].slice(0, MAX_GALLERY_FILES));
  };

  const updateVariationOption = (index, key, value) => {
    setVariationOptions((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  };

  const updateVariationRow = (index, key, value) => {
    setVariationRows((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    const name = form.name.trim();
    let price = Number.parseFloat(form.price);
    let stock = Number.parseInt(form.stock, 10);
    let normalizedVariations = [];

    if (!name) {
      setFormError('Enter a product name.');
      return;
    }
    if (productType === 'simple') {
      if (Number.isNaN(price) || price < 0) {
        setFormError('Enter a valid price.');
        return;
      }
    } else {
      const raw = variationRows
        .map((v) => ({
          optionA: (v?.optionA || '').trim(),
          optionB: (v?.optionB || '').trim(),
          name: (v?.label || '').trim(),
          price: Number.parseFloat(v?.price),
          stock: Number.parseInt(v?.stock, 10),
          sku: (v?.sku || '').trim(),
          image: (v?.image || '').trim(),
          file: v?.file || null,
        }))
        .filter((v) => v.name);
      if (raw.length === 0) {
        setFormError('Set option values and add at least one variation combination.');
        return;
      }
      const invalid = raw.find(
        (v) => !v.name || Number.isNaN(v.price) || v.price < 0 || Number.isNaN(v.stock) || v.stock < 0
      );
      if (invalid) {
        setFormError('Each variation needs valid price and stock (0 or more).');
        return;
      }
      normalizedVariations = raw;
      price = Math.min(...raw.map((v) => v.price));
    }
    if (Number.isNaN(stock) || stock < 0) {
      setFormError('Enter a valid stock quantity (0 or more).');
      return;
    }

    setSaving(true);
    let imageUrl = existingImageUrl || '';
    const galleryUrls = Array.isArray(existingGalleryImages) ? [...existingGalleryImages] : [];

    try {
      if (mainFile) {
        if (isSupabaseConfigured && supabase) {
          imageUrl = await uploadProductImage(supabase, mainFile);
        } else if (mainFile.size > MAX_LOCAL_IMAGE_BYTES) {
          setSaving(false);
          setFormError('Without Supabase, main image must be under 2MB.');
          return;
        } else {
          imageUrl = await fileToDataUrl(mainFile);
        }
      } else if (!imageUrl) {
        imageUrl =
          'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=200&q=80';
      }

      if (galleryFiles.length > 0) {
        galleryUrls.length = 0;
      }
      for (let i = 0; i < galleryFiles.length; i += 1) {
        const file = galleryFiles[i];
        if (isSupabaseConfigured && supabase) {
          galleryUrls.push(await uploadProductImage(supabase, file));
        } else if (file.size > MAX_LOCAL_IMAGE_BYTES) {
          throw new Error(
            'Without Supabase, gallery images must be under 2MB each, or connect Supabase and run supabase/storage.sql.'
          );
        } else {
          galleryUrls.push(await fileToDataUrl(file));
        }
      }

      let totalVariationStock = 0;
      if (productType === 'variable') {
        for (let i = 0; i < normalizedVariations.length; i += 1) {
          const variation = normalizedVariations[i];
          let variationImage = variation.image;
          if (variation.file) {
            if (isSupabaseConfigured && supabase) {
              variationImage = await uploadProductImage(supabase, variation.file, 'products');
            } else if (variation.file.size > MAX_LOCAL_IMAGE_BYTES) {
              throw new Error('Without Supabase, variation image must be under 2MB.');
            } else {
              variationImage = await fileToDataUrl(variation.file);
            }
          }
          normalizedVariations[i] = { ...variation, image: variationImage || '' };
          totalVariationStock += variation.stock;
          if (variationImage && !galleryUrls.includes(variationImage)) {
            galleryUrls.push(variationImage);
          }
        }
        if (stock === 0 && totalVariationStock > 0) {
          // Auto-derive product stock from variations if user leaves stock at 0.
          stock = totalVariationStock;
        }
      }

      if (!imageUrl && productType === 'variable' && normalizedVariations[0]?.image) {
        imageUrl = normalizedVariations[0].image;
      }
    } catch (err) {
      setSaving(false);
      const msg = err?.message || 'Image upload failed.';
      setFormError(
        /bucket|not found|Bucket/i.test(msg)
          ? `${msg} If using Supabase, run supabase/storage.sql in the SQL editor to create the product-images bucket.`
          : msg
      );
      return;
    }

    const row = {
      name,
      description: form.description.trim() || null,
      sku: form.sku.trim() || null,
      material: form.material.trim() || null,
      stock,
      price,
      section: form.section,
      sub_category: form.sub_category.trim() || null,
      dimensions: form.dimensions.trim() || null,
      weight: form.weight.trim() || null,
      image: imageUrl,
      gallery_images: galleryUrls,
    };

    if (isSupabaseConfigured && supabase) {
      const query = isEditMode
        ? supabase.from('products').update(row).eq('id', editProductId)
        : supabase.from('products').insert(row);
      const { data, error } = await query.select(ADMIN_PRODUCT_SELECT_FIELDS).single();
      setSaving(false);
      if (error) {
        setFormError(error.message);
        return;
      }
      if (productType === 'variable' && data?.id) {
        const deleteRes = await supabase.from('product_variations').delete().eq('product_id', data.id);
        if (deleteRes.error && !/relation .* does not exist/i.test(deleteRes.error.message || '')) {
          setFormError(deleteRes.error.message);
          return;
        }
        const rowsToInsert = normalizedVariations.map((v) => ({
          product_id: data.id,
          option_a: v.optionA || null,
          option_b: v.optionB || null,
          price: v.price,
          stock: v.stock,
          sku: v.sku || null,
          image_url: v.image || null,
        }));
        if (rowsToInsert.length > 0) {
          const insRes = await supabase.from('product_variations').insert(rowsToInsert);
          if (insRes.error && !/relation .* does not exist/i.test(insRes.error.message || '')) {
            setFormError(insRes.error.message);
            return;
          }
        }
      }
      window.location.href = '/admin/products';
      return;
    }

    const prev = readAdminProductsFromStorage();
    const list = Array.isArray(prev) ? prev : [];
    const localRow = {
      id: isEditMode ? editProductId : `p-${Date.now()}`,
      ...row,
      product_type: productType,
      variations:
        productType === 'variable'
          ? normalizedVariations.map((v) => ({
              name: v.name,
              optionA: v.optionA || '',
              optionB: v.optionB || '',
              price: v.price,
              stock: v.stock,
              sku: v.sku || '',
              image: v.image || '',
            }))
          : [],
      description: row.description || '',
      sku: row.sku || '',
      material: row.material || '',
      sub_category: row.sub_category || '',
      dimensions: row.dimensions || '',
      weight: row.weight || '',
      gallery_images: Array.isArray(row.gallery_images) ? row.gallery_images : [],
    };
    const nextList = isEditMode
      ? [localRow, ...list.filter((item) => String(item.id) !== String(editProductId))]
      : [localRow, ...list];
    window.localStorage.setItem(
      ADMIN_PRODUCTS_STORAGE_KEY,
      JSON.stringify(nextList)
    );
    setSaving(false);
    window.location.href = '/admin/products';
  };

  if (formLoading) {
    return (
      <AdminLayout activeNav="products" title="Edit product">
        <p className="admin-products-empty">Loading product…</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeNav="products" title={isEditMode ? 'Edit product' : 'Add product'}>
      <a className="ghost-btn admin-product-new-back" href="/admin/products">
        ← Back to products
      </a>

      <form className="admin-product-form admin-product-form-page auth-card" onSubmit={handleSubmit}>
        <h2 className="admin-product-form-title">{isEditMode ? 'Edit product' : 'New product'}</h2>
        <p className="admin-product-form-intro">
          Fill in catalog details and upload product images. Drag and drop onto the dashed areas works too.
        </p>

        <div className="admin-product-form-grid admin-product-form-grid-wide">
          <label>
            Product type *
            <select
              value={productType}
              onChange={(event) => {
                const nextType = event.target.value;
                setProductType(nextType);
                if (nextType === 'simple') {
                  setVariationRows([]);
                } else if (variationRows.length === 0) {
                  const optionAValues = (variationOptions[0]?.values || '')
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean);
                  const optionBValues = (variationOptions[1]?.values || '')
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean);
                  setVariationRows(buildVariationMatrix(optionAValues, optionBValues));
                }
              }}
            >
              <option value="simple">Simple product</option>
              <option value="variable">Variable product (with variations)</option>
            </select>
          </label>

          <label className="admin-product-form-span">
            Product name *
            <input
              type="text"
              value={form.name}
              onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
              placeholder="e.g. Cashmere evening wrap"
              required
            />
          </label>

          <label className="admin-product-form-span">
            Description
            <textarea
              rows={4}
              value={form.description}
              onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))}
              placeholder="Short description for admin and future product pages…"
            />
          </label>

          <div className="admin-product-form-span admin-upload-section">
            <span className="admin-upload-heading">Main image</span>
            <p className="admin-upload-hint">
              Hero photo for listings. Upload a file.
            </p>
            <div
              className="admin-upload-zone"
              onDragOver={(event) => event.preventDefault()}
              onDrop={onMainDrop}
            >
              <input
                id="admin-main-image-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="admin-file-input"
                onChange={onMainFileInput}
              />
              <label htmlFor="admin-main-image-input" className="admin-upload-zone-label">
                <span className="admin-upload-zone-title">Drop image here or click to browse</span>
                <span className="admin-upload-zone-meta">JPEG, PNG, WebP, GIF · max 5MB</span>
              </label>
            </div>
            {(mainPreviewUrl || existingImageUrl) && (
              <div className="admin-upload-preview-row">
                <div className="admin-upload-preview admin-upload-preview-main">
                  <img src={mainPreviewUrl || existingImageUrl} alt="Main preview" />
                </div>
                <button
                  className="ghost-btn"
                  type="button"
                  onClick={() => {
                    setMainFile(null);
                    setExistingImageUrl('');
                  }}
                >
                  Remove image
                </button>
              </div>
            )}
          </div>

          <div className="admin-product-form-span admin-upload-section">
            <span className="admin-upload-heading">Gallery images</span>
            <p className="admin-upload-hint">
              Extra photos for the product detail page. Up to {MAX_GALLERY_FILES} images.
            </p>
            <div
              className="admin-upload-zone"
              onDragOver={(event) => event.preventDefault()}
              onDrop={onGalleryDrop}
            >
              <input
                id="admin-gallery-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="admin-file-input"
                multiple
                onChange={onGalleryInput}
              />
              <label htmlFor="admin-gallery-input" className="admin-upload-zone-label">
                <span className="admin-upload-zone-title">Drop gallery images here or click to browse</span>
                <span className="admin-upload-zone-meta">Select multiple · max 5MB each</span>
              </label>
            </div>
            {(galleryPreviewUrls.length > 0 || existingGalleryImages.length > 0) && (
              <ul className="admin-gallery-preview-grid">
                {existingGalleryImages.map((url, index) => (
                  <li key={`existing-${url}-${index}`} className="admin-gallery-preview-item">
                    <img src={url} alt="" />
                    <button
                      type="button"
                      className="admin-gallery-remove"
                      onClick={() =>
                        setExistingGalleryImages((prev) => prev.filter((_, i) => i !== index))
                      }
                    >
                      Remove
                    </button>
                  </li>
                ))}
                {galleryPreviewUrls.map((url, index) => (
                  <li key={`${url}-${index}`} className="admin-gallery-preview-item">
                    <img src={url} alt="" />
                    <button type="button" className="admin-gallery-remove" onClick={() => removeGalleryAt(index)}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label>
            Category *
            <select
              value={form.section}
              onChange={(event) =>
                setForm((f) => ({
                  ...f,
                  section: event.target.value,
                  sub_category: '',
                }))
              }
              disabled={taxLoading || categoryOptions.length === 0}
            >
              {categoryOptions.map((c) => (
                <option key={c.id || c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Sub-category
            <select
              value={form.sub_category}
              onChange={(event) => setForm((f) => ({ ...f, sub_category: event.target.value }))}
              disabled={taxLoading || !activeCategory}
            >
              <option value="">
                {taxLoading ? 'Loading…' : activeCategory ? '— Optional —' : 'Add a category first'}
              </option>
              {activeSubcategoryOptions.map((opt) => (
                <option key={opt.id || `${opt.category_id}-${opt.slug || opt.name}`} value={opt.name}>
                  {opt.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            SKU
            <input
              type="text"
              value={form.sku}
              onChange={(event) => setForm((f) => ({ ...f, sku: event.target.value }))}
              placeholder="e.g. DL-HER-042"
            />
          </label>

          <label>
            Material / composition
            <input
              type="text"
              value={form.material}
              onChange={(event) => setForm((f) => ({ ...f, material: event.target.value }))}
              placeholder="e.g. 100% silk, Italian leather"
            />
          </label>

          <label>
            Price (GBP) *
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.price}
              onChange={(event) => setForm((f) => ({ ...f, price: event.target.value }))}
              placeholder="0.00"
              required={productType === 'simple'}
              disabled={productType === 'variable'}
            />
          </label>

          <label>
            Stock quantity *
            <input
              type="number"
              min="0"
              step="1"
              value={form.stock}
              onChange={(event) => setForm((f) => ({ ...f, stock: event.target.value }))}
              required
            />
          </label>

          {productType === 'variable' && (
            <div className="admin-product-form-span">
              <span className="admin-upload-heading">Variations *</span>
              <p className="admin-upload-hint">
                Choose up to 2 options (for example Color and Size), then set price/stock/image for each
                generated combination.
              </p>
              <div className="admin-product-form-grid">
                <label>
                  Option 1 name
                  <input
                    type="text"
                    value={variationOptions[0]?.name || ''}
                    onChange={(event) => updateVariationOption(0, 'name', event.target.value)}
                    placeholder="e.g. Color"
                  />
                </label>
                <label>
                  Option 1 values (comma separated)
                  <input
                    type="text"
                    value={variationOptions[0]?.values || ''}
                    onChange={(event) => updateVariationOption(0, 'values', event.target.value)}
                    placeholder="e.g. Red, Blue, Black"
                  />
                </label>
                <label>
                  Option 2 name (optional)
                  <input
                    type="text"
                    value={variationOptions[1]?.name || ''}
                    onChange={(event) => updateVariationOption(1, 'name', event.target.value)}
                    placeholder="e.g. Size"
                  />
                </label>
                <label>
                  Option 2 values (comma separated)
                  <input
                    type="text"
                    value={variationOptions[1]?.values || ''}
                    onChange={(event) => updateVariationOption(1, 'values', event.target.value)}
                    placeholder="e.g. S, M, L"
                  />
                </label>
              </div>
              {variationRows.length > 0 && (
                <div className="admin-table-wrap admin-variation-table-wrap">
                  <table className="admin-products-table">
                    <thead>
                      <tr>
                        <th>Variation</th>
                        <th>Price</th>
                        <th>Stock</th>
                        <th>Image File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {variationRows.map((row, index) => (
                        <tr key={row.key}>
                          <td>{row.label}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.price}
                              onChange={(event) => updateVariationRow(index, 'price', event.target.value)}
                              placeholder="0.00"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={row.stock}
                              onChange={(event) => updateVariationRow(index, 'stock', event.target.value)}
                              placeholder="0"
                            />
                          </td>
                          <td>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={(event) => {
                                const file = event.target.files?.[0] || null;
                                event.target.value = '';
                                if (!file) return;
                                if (!file.type.startsWith('image/')) {
                                  setFormError('Choose an image file (JPEG, PNG, WebP, or GIF).');
                                  return;
                                }
                                if (file.size > MAX_IMAGE_BYTES) {
                                  setFormError('Each image must be 5MB or smaller.');
                                  return;
                                }
                                setFormError('');
                                updateVariationRow(index, 'file', file);
                              }}
                            />
                            {(row.file || row.image) && (
                              <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                                {row.file ? `Selected: ${row.file.name}` : 'Using saved image'}
                                {row.image && (
                                  <div style={{ marginTop: '0.35rem' }}>
                                    <img
                                      src={row.image}
                                      alt=""
                                      style={{
                                        width: '42px',
                                        height: '42px',
                                        objectFit: 'cover',
                                        borderRadius: '6px',
                                        border: '1px solid var(--border-soft)',
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <label>
            Dimensions
            <input
              type="text"
              value={form.dimensions}
              onChange={(event) => setForm((f) => ({ ...f, dimensions: event.target.value }))}
              placeholder="e.g. 30 × 20 × 10 cm"
            />
          </label>

          <label>
            Weight (shipping)
            <input
              type="text"
              value={form.weight}
              onChange={(event) => setForm((f) => ({ ...f, weight: event.target.value }))}
              placeholder="e.g. 450 g"
            />
          </label>
        </div>

        {formError && (
          <p className="admin-login-error" role="alert">
            {formError}
          </p>
        )}

        <div className="admin-product-form-actions">
          <a className="ghost-btn" href="/admin/products">
            Cancel
          </a>
          <button className="primary-btn" type="submit" disabled={saving}>
            {saving ? 'Saving…' : isEditMode ? 'Update product' : 'Save product'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

const EMPTY_CATEGORY_FORM = {
  name: '',
  description: '',
};

function categoryImageBucketErrorDetail(rawMsg) {
  const m = (rawMsg || '').trim();
  return [
    m,
    '',
    'The Storage bucket "product-images" is missing in this Supabase project (or the name does not match).',
    'Fix: Dashboard → SQL → New query. Copy the entire file supabase/storage.sql from this app, paste, Run.',
    'Then refresh this page and upload again. Category files are stored under folder categories/ in that bucket.',
  ].join('\n');
}

const EMPTY_SUBCATEGORY_FORM = {
  category_id: '',
  name: '',
  description: '',
};

function AdminIconEdit() {
  return (
    <svg className="admin-table-icon-svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden>
      <path
        fill="currentColor"
        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 0 0 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
      />
    </svg>
  );
}

function AdminIconTrash() {
  return (
    <svg className="admin-table-icon-svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden>
      <path
        fill="currentColor"
        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
      />
    </svg>
  );
}

function AdminIconEye() {
  return (
    <svg className="admin-table-icon-svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden>
      <path
        fill="currentColor"
        d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-6.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z"
      />
    </svg>
  );
}

function getAdminCategoryEditIdFromPath() {
  const m = window.location.pathname.match(/\/admin\/categories\/edit\/([^/]+)/i);
  return m ? decodeURIComponent(m[1].trim()) : '';
}

function getAdminSubcategoryEditIdFromPath() {
  const m = window.location.pathname.match(/\/admin\/subcategories\/edit\/([^/]+)/i);
  return m ? decodeURIComponent(m[1].trim()) : '';
}

function getAdminProductEditIdFromPath() {
  const m = window.location.pathname.match(/\/admin\/products\/edit\/([^/]+)/i);
  return m ? decodeURIComponent(m[1].trim()) : '';
}

function AdminIconCheckCircle() {
  return (
    <svg className="admin-modal-icon-svg admin-modal-icon-svg--success" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
      />
    </svg>
  );
}

function AdminCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [actionError, setActionError] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!pendingDelete) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !deleteSubmitting) setPendingDelete(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingDelete, deleteSubmitting]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isSupabaseConfigured || !supabase) {
        if (!cancelled) {
          setLoading(false);
          setCategories([]);
          setListError('');
        }
        return;
      }
      setLoading(true);
      setListError('');
      setActionError('');
      const { data, error } = await supabase
        .from('categories')
        .select(ADMIN_CATEGORY_SELECT_FIELDS)
        .order('name', { ascending: true });
      if (cancelled) return;
      setLoading(false);
      if (error) {
        setListError(error.message);
        setCategories([]);
        return;
      }
      setCategories(data ?? []);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AdminLayout activeNav="categories" title="Categories">
      <div className="admin-products-toolbar">
        <p className="admin-products-count">
          {loading ? 'Loading categories…' : `${categories.length} categories`}
          {isSupabaseConfigured && supabase && (
            <span className="admin-products-source"> · From Supabase</span>
          )}
        </p>
        {isSupabaseConfigured && supabase ? (
          <a className="primary-btn" href="/admin/categories/new">
            Add category
          </a>
        ) : null}
      </div>
      {listError && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {listError}
        </p>
      )}
      {actionError && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {actionError}
        </p>
      )}
      <div className="admin-table-wrap">
        {!isSupabaseConfigured || !supabase ? (
          <div className="admin-products-empty admin-supabase-setup-hint">
            <p>
              <strong>Supabase is not connected in this build.</strong> Categories load only from your
              database. Put your project URL and anon key in <code>.env.local</code> at the project root
              (same folder as <code>package.json</code>), then <strong>stop and run</strong>{' '}
              <code>npm start</code> again — Create React App reads env vars only when the dev server
              starts.
            </p>
            <p className="admin-supabase-setup-example">
              Example <code>.env.local</code>:
              <br />
              <code>REACT_APP_SUPABASE_URL=https://YOUR-PROJECT.supabase.co</code>
              <br />
              <code>REACT_APP_SUPABASE_ANON_KEY=your-anon-key-from-dashboard</code>
            </p>
            {process.env.NODE_ENV === 'development' && (
              <div className="admin-supabase-env-debug" role="status">
                <strong>Detected in this session</strong>
                <ul>
                  {(() => {
                    const d = getSupabaseEnvDebugInfo();
                    return (
                      <>
                        <li>
                          <code>REACT_APP_SUPABASE_URL</code>:{' '}
                          {d.hasUrl ? (
                            <>
                              set → <code>{d.url}</code>
                            </>
                          ) : (
                            <span>missing or empty</span>
                          )}
                        </li>
                        <li>
                          <code>REACT_APP_SUPABASE_ANON_KEY</code>:{' '}
                          {d.hasKey ? (
                            <>set ({d.anonKeyLength} characters)</>
                          ) : (
                            <span>missing or empty</span>
                          )}
                        </li>
                      </>
                    );
                  })()}
                </ul>
              </div>
            )}
          </div>
        ) : !loading && categories.length === 0 && !listError ? (
          <p className="admin-products-empty">
            No categories yet. <a href="/admin/categories/new">Add a category</a> or run{' '}
            <code>supabase/categories.sql</code> in the Supabase SQL editor.
          </p>
        ) : (
          <table className="admin-products-table">
            <thead>
              <tr>
                <th scope="col">Image</th>
                <th scope="col">Name</th>
                <th scope="col">Description</th>
                <th scope="col" className="admin-table-actions-col">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {categories.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.image ? (
                      <img className="admin-product-thumb" src={row.image} alt="" />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{row.name}</td>
                  <td>{row.description || '—'}</td>
                  <td className="admin-table-actions-cell">
                    <div className="admin-table-actions">
                      <a
                        className="admin-icon-btn admin-icon-btn--edit"
                        href={`/admin/categories/edit/${encodeURIComponent(row.id)}`}
                        title="Edit category"
                        aria-label={`Edit ${row.name}`}
                      >
                        <AdminIconEdit />
                      </a>
                      <button
                        type="button"
                        className="admin-icon-btn admin-icon-btn--danger"
                        title="Delete category"
                        aria-label={`Delete ${row.name}`}
                        onClick={() => {
                          if (!supabase) return;
                          setPendingDelete({ id: row.id, name: row.name });
                        }}
                      >
                        <AdminIconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pendingDelete && (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onClick={() => !deleteSubmitting && setPendingDelete(null)}
        >
          <div
            className="admin-modal admin-modal--confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-delete-category-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-modal-icon-wrap admin-modal-icon-wrap--warn">
              <AdminIconTrash />
            </div>
            <h2 id="admin-delete-category-title" className="admin-modal-title">
              Remove this category?
            </h2>
            <p className="admin-modal-text">
              <strong>{pendingDelete.name}</strong> will be permanently deleted. Subcategories linked to it
              will be removed as well.
            </p>
            <div className="admin-modal-actions">
              <button
                type="button"
                className="ghost-btn"
                disabled={deleteSubmitting}
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-modal-btn-danger"
                disabled={deleteSubmitting}
                onClick={async () => {
                  if (!supabase || !pendingDelete) return;
                  const idToRemove = pendingDelete.id;
                  const deletedName = pendingDelete.name;
                  setActionError('');
                  setDeleteSubmitting(true);
                  const { error: delErr } = await supabase
                    .from('categories')
                    .delete()
                    .eq('id', idToRemove);
                  setDeleteSubmitting(false);
                  setPendingDelete(null);
                  if (delErr) {
                    setActionError(delErr.message);
                    return;
                  }
                  setCategories((prev) => prev.filter((c) => c.id !== idToRemove));
                  setToast({ message: `“${deletedName}” was deleted.` });
                }}
              >
                {deleteSubmitting ? 'Deleting…' : 'Delete category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="admin-toast admin-toast--success" role="status" aria-live="polite">
          <span className="admin-toast-icon" aria-hidden>
            <AdminIconCheckCircle />
          </span>
          <span className="admin-toast-message">{toast.message}</span>
        </div>
      )}
    </AdminLayout>
  );
}

function AdminCategoryNewPage() {
  const [form, setForm] = useState(EMPTY_CATEGORY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [mainFile, setMainFile] = useState(null);

  const mainPreviewUrl = useMemo(() => (mainFile ? URL.createObjectURL(mainFile) : ''), [mainFile]);

  useEffect(() => {
    return () => {
      if (mainPreviewUrl) URL.revokeObjectURL(mainPreviewUrl);
    };
  }, [mainPreviewUrl]);

  const onMainFileInput = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormError('Choose an image file (JPEG, PNG, WebP, or GIF).');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setFormError('Each image must be 5MB or smaller.');
      return;
    }
    setFormError('');
    setMainFile(file);
  };

  const onMainDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file?.type.startsWith('image/')) return;
    if (file.size > MAX_IMAGE_BYTES) {
      setFormError('Each image must be 5MB or smaller.');
      return;
    }
    setFormError('');
    setMainFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    const name = form.name.trim();
    if (!name) {
      setFormError('Enter a category name.');
      return;
    }
    const slug = slugifyCatalog(name);

    if (!isSupabaseConfigured || !supabase) {
      setFormError(
        'Categories are saved only to Supabase. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env and restart the dev server.'
      );
      return;
    }

    setSaving(true);
    let imageValue = null;

    try {
      if (mainFile) {
        const url = await uploadProductImage(supabase, mainFile, 'categories');
        if (typeof url !== 'string' || !url.trim()) {
          setSaving(false);
          setFormError('Could not resolve an image URL after upload. Try again or check Storage in Supabase.');
          return;
        }
        imageValue = url.trim();
      }
    } catch (err) {
      setSaving(false);
      const msg = err?.message || 'Image upload failed.';
      setFormError(
        /bucket|not found|Bucket/i.test(msg) ? categoryImageBucketErrorDetail(msg) : msg
      );
      return;
    }

    const row = {
      name,
      slug,
      description: form.description.trim() || null,
      sort_order: 0,
      image: imageValue,
    };

    const { error } = await supabase.from('categories').insert(row);
    setSaving(false);
    if (error) {
      setFormError(
        error.code === '23505' || /unique|duplicate/i.test(error.message)
          ? 'A category with this name (URL slug) already exists.'
          : error.message
      );
      return;
    }
    window.location.href = '/admin/categories';
  };

  return (
    <AdminLayout activeNav="categories" title="Add category">
      <a className="ghost-btn admin-product-new-back" href="/admin/categories">
        ← Back to categories
      </a>
      <form className="admin-product-form admin-product-form-page auth-card" onSubmit={handleSubmit}>
        <h2 className="admin-product-form-title">New category</h2>
        <p className="admin-product-form-intro">
          The URL slug is generated from the name. Optionally upload a category image; if you skip it, no
          image is stored. Categories are saved in Supabase only.
        </p>
        <div className="admin-product-form-grid admin-product-form-grid-wide">
          <label className="admin-product-form-span">
            Name *
            <input
              value={form.name}
              onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
              placeholder="e.g. Evening wear"
              required
            />
          </label>
          <label className="admin-product-form-span">
            Description
            <textarea
              rows={3}
              value={form.description}
              onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))}
              placeholder="Optional internal notes"
            />
          </label>

          <div className="admin-product-form-span admin-upload-section">
            <span className="admin-upload-heading">Category image</span>
            <p className="admin-upload-hint">Optional hero image for this category.</p>
            <div
              className="admin-upload-zone"
              onDragOver={(event) => event.preventDefault()}
              onDrop={onMainDrop}
            >
              <input
                id="admin-category-image-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="admin-file-input"
                onChange={onMainFileInput}
              />
              <label htmlFor="admin-category-image-input" className="admin-upload-zone-label">
                <span className="admin-upload-zone-title">Drop image here or click to browse</span>
                <span className="admin-upload-zone-meta">JPEG, PNG, WebP, GIF · max 5MB</span>
              </label>
            </div>
            {mainPreviewUrl && (
              <div className="admin-upload-preview-row">
                <div className="admin-upload-preview admin-upload-preview-main">
                  <img src={mainPreviewUrl} alt="Category preview" />
                </div>
                <button className="ghost-btn" type="button" onClick={() => setMainFile(null)}>
                  Remove upload
                </button>
              </div>
            )}
          </div>
        </div>
        {formError && (
          <p className="admin-login-error admin-error-preline" role="alert">
            {formError}
          </p>
        )}
        <div className="admin-product-form-actions">
          <a className="ghost-btn" href="/admin/categories">
            Cancel
          </a>
          <button
            className="primary-btn"
            type="submit"
            disabled={saving || !isSupabaseConfigured || !supabase}
          >
            {saving ? 'Saving…' : 'Save category'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

function AdminCategoryEditPage() {
  const categoryId = getAdminCategoryEditIdFromPath();
  const [loadState, setLoadState] = useState('loading');
  const [loadErr, setLoadErr] = useState('');
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState(EMPTY_CATEGORY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [mainFile, setMainFile] = useState(null);

  const mainPreviewUrl = useMemo(() => (mainFile ? URL.createObjectURL(mainFile) : ''), [mainFile]);

  useEffect(() => {
    return () => {
      if (mainPreviewUrl) URL.revokeObjectURL(mainPreviewUrl);
    };
  }, [mainPreviewUrl]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!categoryId) {
        setLoadState('notfound');
        return;
      }
      if (!isSupabaseConfigured || !supabase) {
        setLoadState('error');
        setLoadErr('Supabase is not configured.');
        return;
      }
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('id', categoryId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setLoadState('error');
        setLoadErr(error.message);
        return;
      }
      if (!data) {
        setLoadState('notfound');
        return;
      }
      setExisting(data);
      setForm({
        name: data.name || '',
        description: data.description || '',
      });
      setLoadState('ready');
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  const onMainFileInput = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormError('Choose an image file (JPEG, PNG, WebP, or GIF).');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setFormError('Each image must be 5MB or smaller.');
      return;
    }
    setFormError('');
    setMainFile(file);
  };

  const onMainDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file?.type.startsWith('image/')) return;
    if (file.size > MAX_IMAGE_BYTES) {
      setFormError('Each image must be 5MB or smaller.');
      return;
    }
    setFormError('');
    setMainFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    const name = form.name.trim();
    if (!name) {
      setFormError('Enter a category name.');
      return;
    }
    if (!isSupabaseConfigured || !supabase || !existing) {
      setFormError('Cannot save: Supabase is not available.');
      return;
    }

    const slug = slugifyCatalog(name);
    setSaving(true);
    let imageValue = null;

    try {
      if (mainFile) {
        const url = await uploadProductImage(supabase, mainFile, 'categories');
        if (typeof url !== 'string' || !url.trim()) {
          setSaving(false);
          setFormError('Could not resolve an image URL after upload. Try again or check Storage in Supabase.');
          return;
        }
        imageValue = url.trim();
      } else {
        const cur = existing.image;
        imageValue =
          cur != null && String(cur).trim() !== '' ? String(cur).trim() : null;
      }
    } catch (err) {
      setSaving(false);
      const msg = err?.message || 'Image upload failed.';
      setFormError(
        /bucket|not found|Bucket/i.test(msg) ? categoryImageBucketErrorDetail(msg) : msg
      );
      return;
    }

    const sortOrder = existing.sort_order != null ? Number(existing.sort_order) : 0;

    const { error } = await supabase
      .from('categories')
      .update({
        name,
        slug,
        description: form.description.trim() || null,
        sort_order: Number.isNaN(sortOrder) ? 0 : sortOrder,
        image: imageValue,
      })
      .eq('id', categoryId);
    setSaving(false);
    if (error) {
      setFormError(
        error.code === '23505' || /unique|duplicate/i.test(error.message)
          ? 'A category with this name (URL slug) already exists.'
          : error.message
      );
      return;
    }
    window.location.href = '/admin/categories';
  };

  if (loadState === 'loading') {
    return (
      <AdminLayout activeNav="categories" title="Edit category">
        <p className="admin-products-count">Loading category…</p>
      </AdminLayout>
    );
  }

  if (loadState === 'notfound') {
    return (
      <AdminLayout activeNav="categories" title="Edit category">
        <p className="admin-products-empty">Category not found.</p>
        <a className="ghost-btn" href="/admin/categories">
          ← Back to categories
        </a>
      </AdminLayout>
    );
  }

  if (loadState === 'error') {
    return (
      <AdminLayout activeNav="categories" title="Edit category">
        <p className="admin-login-error" role="alert">
          {loadErr}
        </p>
        <a className="ghost-btn" href="/admin/categories">
          ← Back to categories
        </a>
      </AdminLayout>
    );
  }

  const currentImgSrc = mainPreviewUrl || existing?.image || null;

  return (
    <AdminLayout activeNav="categories" title="Edit category">
      <a className="ghost-btn admin-product-new-back" href="/admin/categories">
        ← Back to categories
      </a>
      <form className="admin-product-form admin-product-form-page auth-card" onSubmit={handleSubmit}>
        <h2 className="admin-product-form-title">Edit category</h2>
        <p className="admin-product-form-intro">
          The URL slug is updated from the name. Upload a new image to replace the current one, or save
          without uploading to leave the image as-is (including no image).
        </p>
        <div className="admin-product-form-grid admin-product-form-grid-wide">
          <label className="admin-product-form-span">
            Name *
            <input
              value={form.name}
              onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
              placeholder="e.g. Evening wear"
              required
            />
          </label>
          <label className="admin-product-form-span">
            Description
            <textarea
              rows={3}
              value={form.description}
              onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))}
              placeholder="Optional internal notes"
            />
          </label>

          <div className="admin-product-form-span admin-upload-section">
            <span className="admin-upload-heading">Category image</span>
            <p className="admin-upload-hint">Optional. Upload a file to replace the current image.</p>
            {currentImgSrc && (
              <div className="admin-upload-preview-row admin-upload-preview-row-current">
                <div className="admin-upload-preview admin-upload-preview-main">
                  <img src={currentImgSrc} alt="" />
                </div>
                {mainPreviewUrl ? (
                  <button className="ghost-btn" type="button" onClick={() => setMainFile(null)}>
                    Remove new upload
                  </button>
                ) : null}
              </div>
            )}
            <div
              className="admin-upload-zone"
              onDragOver={(event) => event.preventDefault()}
              onDrop={onMainDrop}
            >
              <input
                id="admin-category-edit-image-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="admin-file-input"
                onChange={onMainFileInput}
              />
              <label htmlFor="admin-category-edit-image-input" className="admin-upload-zone-label">
                <span className="admin-upload-zone-title">Drop new image here or click to browse</span>
                <span className="admin-upload-zone-meta">JPEG, PNG, WebP, GIF · max 5MB</span>
              </label>
            </div>
          </div>
        </div>
        {formError && (
          <p className="admin-login-error admin-error-preline" role="alert">
            {formError}
          </p>
        )}
        <div className="admin-product-form-actions">
          <a className="ghost-btn" href="/admin/categories">
            Cancel
          </a>
          <button
            className="primary-btn"
            type="submit"
            disabled={saving || !isSupabaseConfigured || !supabase}
          >
            {saving ? 'Saving…' : 'Update category'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

function AdminSubcategoriesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [listError, setListError] = useState('');
  const [actionError, setActionError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!pendingDelete) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !deleteSubmitting) setPendingDelete(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingDelete, deleteSubmitting]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (isSupabaseConfigured && supabase) {
        setLoading(true);
        setListError('');
        setActionError('');
        const { data, error } = await supabase
          .from('subcategories')
          .select(ADMIN_SUBCATEGORY_LIST_SELECT)
          .order('name', { ascending: true });
        if (cancelled) return;
        setLoading(false);
        if (error) {
          setListError(error.message);
          setRows([]);
          return;
        }
        setRows(data ?? []);
        return;
      }
      const local = readAdminSubcategoriesFromStorage() ?? DEFAULT_ADMIN_SUBCATEGORIES;
      const cats = readAdminCategoriesFromStorage() ?? DEFAULT_ADMIN_CATEGORIES;
      const catMap = Object.fromEntries(cats.map((c) => [String(c.id), c.name]));
      setLoading(false);
      setRows(
        local.map((s) => ({
          ...s,
          categories: { name: catMap[String(s.category_id)] || '—' },
        }))
      );
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const supabaseList = Boolean(isSupabaseConfigured && supabase);
  const categoryFilterOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const name = row?.categories?.name;
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);
  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesCategory =
        categoryFilter === 'all' || (row?.categories?.name || '') === categoryFilter;
      if (!matchesCategory) return false;
      if (!query) return true;
      const haystack = `${row?.categories?.name || ''} ${row?.name || ''} ${row?.description || ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [rows, categoryFilter, searchQuery]);

  return (
    <AdminLayout activeNav="subcategories" title="Subcategories">
      <div className="admin-products-toolbar">
        <p className="admin-products-count">
          {loading ? 'Loading subcategories…' : `${filteredRows.length} of ${rows.length} subcategories`}
          {supabaseList && <span className="admin-products-source"> · From Supabase</span>}
        </p>
        <a className="primary-btn" href="/admin/subcategories/new">
          Add subcategory
        </a>
      </div>
      <div className="admin-subcategory-filters">
        <label>
          Category
          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            disabled={loading}
          >
            <option value="all">All categories</option>
            {categoryFilterOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Search
          <input
            type="search"
            placeholder="Search subcategory..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            disabled={loading}
          />
        </label>
      </div>
      {listError && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {listError}
        </p>
      )}
      {actionError && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {actionError}
        </p>
      )}
      <div className="admin-table-wrap">
        {!loading && rows.length === 0 && !listError ? (
          <p className="admin-products-empty">
            No subcategories yet. <a href="/admin/subcategories/new">Add one</a> (add a category first if
            needed) or run <code>supabase/schema.sql</code>.
          </p>
        ) : !loading && filteredRows.length === 0 && !listError ? (
          <p className="admin-products-empty">No subcategories match your filters.</p>
        ) : (
          <table className="admin-products-table">
            <thead>
              <tr>
                <th scope="col">Parent category</th>
                <th scope="col">Name</th>
                <th scope="col">Description</th>
                <th scope="col" className="admin-table-actions-col">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.categories?.name || '—'}</td>
                  <td>{row.name}</td>
                  <td>{row.description || '—'}</td>
                  <td className="admin-table-actions-cell">
                    {supabaseList ? (
                      <div className="admin-table-actions">
                        <a
                          className="admin-icon-btn admin-icon-btn--edit"
                          href={`/admin/subcategories/edit/${encodeURIComponent(row.id)}`}
                          title="Edit subcategory"
                          aria-label={`Edit ${row.name}`}
                        >
                          <AdminIconEdit />
                        </a>
                        <button
                          type="button"
                          className="admin-icon-btn admin-icon-btn--danger"
                          title="Delete subcategory"
                          aria-label={`Delete ${row.name}`}
                          onClick={() => setPendingDelete({ id: row.id, name: row.name })}
                        >
                          <AdminIconTrash />
                        </button>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pendingDelete && (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onClick={() => !deleteSubmitting && setPendingDelete(null)}
        >
          <div
            className="admin-modal admin-modal--confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-delete-subcategory-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-modal-icon-wrap admin-modal-icon-wrap--warn">
              <AdminIconTrash />
            </div>
            <h2 id="admin-delete-subcategory-title" className="admin-modal-title">
              Remove this subcategory?
            </h2>
            <p className="admin-modal-text">
              <strong>{pendingDelete.name}</strong> will be permanently deleted.
            </p>
            <div className="admin-modal-actions">
              <button
                type="button"
                className="ghost-btn"
                disabled={deleteSubmitting}
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-modal-btn-danger"
                disabled={deleteSubmitting}
                onClick={async () => {
                  if (!supabase || !pendingDelete) return;
                  const idToRemove = pendingDelete.id;
                  const deletedName = pendingDelete.name;
                  setActionError('');
                  setDeleteSubmitting(true);
                  const { error: delErr } = await supabase
                    .from('subcategories')
                    .delete()
                    .eq('id', idToRemove);
                  setDeleteSubmitting(false);
                  setPendingDelete(null);
                  if (delErr) {
                    setActionError(delErr.message);
                    return;
                  }
                  setRows((prev) => prev.filter((r) => r.id !== idToRemove));
                  setToast({ message: `“${deletedName}” was deleted.` });
                }}
              >
                {deleteSubmitting ? 'Deleting…' : 'Delete subcategory'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="admin-toast admin-toast--success" role="status" aria-live="polite">
          <span className="admin-toast-icon" aria-hidden>
            <AdminIconCheckCircle />
          </span>
          <span className="admin-toast-message">{toast.message}</span>
        </div>
      )}
    </AdminLayout>
  );
}

function AdminCustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [deletingId, setDeletingId] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadCustomers() {
      if (!isSupabaseConfigured || !supabase) {
        if (!cancelled) {
          setLoading(false);
          setListError('Supabase is not configured. Add .env keys and restart the app.');
        }
        return;
      }

      setLoading(true);
      setListError('');
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_list_customers');

      if (cancelled) return;

      if (!rpcError) {
        setLoading(false);
        setCustomers(Array.isArray(rpcData) ? rpcData : []);
        return;
      }

      // Fallback if RPC is not created yet: try direct table select.
      const { data, error } = await supabase
        .from('app_users')
        .select('id, full_name, email, is_active, created_at')
        .order('created_at', { ascending: false });

      if (cancelled) return;
      setLoading(false);
      if (error) {
        setListError(rpcError?.message || error.message);
        setCustomers([]);
        return;
      }
      setCustomers(data ?? []);
    }

    loadCustomers();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDeleteCustomer = async (row) => {
    if (!row?.id) return;
    const emailLabel = row?.email ? ` (${row.email})` : '';
    const ok = window.confirm(`Delete this customer${emailLabel}?\n\nThis action cannot be undone.`);
    if (!ok) return;
    if (!isSupabaseConfigured || !supabase) {
      setListError('Supabase is not configured. Add .env keys and restart the app.');
      return;
    }

    setDeletingId(String(row.id));
    setListError('');

    let deleteError = null;
    const { error: rpcError } = await supabase.rpc('admin_delete_customer', {
      p_user_id: row.id,
    });
    if (rpcError) {
      const { error } = await supabase.from('app_users').delete().eq('id', row.id);
      deleteError = error || rpcError;
    }

    setDeletingId('');
    if (deleteError) {
      setListError(deleteError.message || 'Could not delete customer.');
      return;
    }
    setCustomers((prev) => prev.filter((x) => String(x.id) !== String(row.id)));
  };

  return (
    <AdminLayout activeNav="customers" title="Customers">
      <div className="admin-products-toolbar">
        <p className="admin-products-count">
          {loading ? 'Loading customers…' : `${customers.length} customers`}
        </p>
      </div>

      {listError && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {listError}
        </p>
      )}

      <div className="admin-table-wrap">
        {!loading && customers.length === 0 && !listError ? (
          <p className="admin-products-empty">No customers yet. Users will appear here after sign up.</p>
        ) : (
          <table className="admin-products-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Status</th>
                <th scope="col">Signed up</th>
                <th scope="col" className="admin-table-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((row) => (
                <tr key={row.id}>
                  <td>{row.full_name || '—'}</td>
                  <td>{row.email || '—'}</td>
                  <td>{row.is_active ? 'Active' : 'Inactive'}</td>
                  <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                  <td className="admin-table-actions-cell">
                    <div className="admin-table-actions">
                      <button
                        type="button"
                        className="admin-icon-btn admin-icon-btn--danger"
                        onClick={() => {
                          void handleDeleteCustomer(row);
                        }}
                        title="Delete customer"
                        aria-label="Delete customer"
                        disabled={deletingId === String(row.id)}
                      >
                        <AdminIconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}

function AdminContactPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadMessages() {
      if (!isSupabaseConfigured || !supabase) {
        if (!cancelled) {
          setLoading(false);
          setListError('Supabase is not configured. Add .env keys and restart the app.');
        }
        return;
      }

      setLoading(true);
      setListError('');
      const { data, error } = await supabase
        .from('contact_messages')
        .select('id, full_name, email, subject, message, status, source_page, created_at')
        .order('created_at', { ascending: false });

      if (cancelled) return;
      setLoading(false);
      if (error) {
        setListError(error.message);
        setMessages([]);
        return;
      }
      setMessages(Array.isArray(data) ? data : []);
    }

    loadMessages();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AdminLayout activeNav="contact" title="Contact">
      <div className="admin-products-toolbar">
        <p className="admin-products-count">
          {loading ? 'Loading messages…' : `${messages.length} messages`}
        </p>
      </div>

      {listError && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {listError}
        </p>
      )}

      <div className="admin-table-wrap">
        {!loading && messages.length === 0 && !listError ? (
          <p className="admin-products-empty">No contact form responses yet.</p>
        ) : (
          <table className="admin-products-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Subject</th>
                <th scope="col">Message</th>
                <th scope="col">Status</th>
                <th scope="col">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((row) => (
                <tr key={row.id}>
                  <td>{row.full_name || '—'}</td>
                  <td>{row.email || '—'}</td>
                  <td>{row.subject || '—'}</td>
                  <td style={{ minWidth: 260 }}>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{row.message || '—'}</div>
                  </td>
                  <td>{row.status || 'new'}</td>
                  <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}

function AdminNewsletterPage() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadSubscribers() {
      if (!isSupabaseConfigured || !supabase) {
        if (!cancelled) {
          setLoading(false);
          setListError('Supabase is not configured. Add .env keys and restart the app.');
        }
        return;
      }

      setLoading(true);
      setListError('');
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .select('id, email, source, is_active, subscribed_at')
        .order('subscribed_at', { ascending: false });

      if (cancelled) return;
      setLoading(false);
      if (error) {
        setListError(error.message);
        setSubscribers([]);
        return;
      }
      setSubscribers(Array.isArray(data) ? data : []);
    }

    loadSubscribers();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AdminLayout activeNav="newsletter" title="Newsletter">
      <div className="admin-products-toolbar">
        <p className="admin-products-count">
          {loading ? 'Loading subscribers…' : `${subscribers.length} subscribers`}
        </p>
      </div>

      {listError && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {listError}
        </p>
      )}

      <div className="admin-table-wrap">
        {!loading && subscribers.length === 0 && !listError ? (
          <p className="admin-products-empty">No newsletter subscribers yet.</p>
        ) : (
          <table className="admin-products-table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Source</th>
                <th scope="col">Status</th>
                <th scope="col">Subscribed</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((row) => (
                <tr key={row.id}>
                  <td>{row.email || '—'}</td>
                  <td>{row.source || 'footer'}</td>
                  <td>{row.is_active ? 'Active' : 'Inactive'}</td>
                  <td>{row.subscribed_at ? new Date(row.subscribed_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}

function AdminCustomServicesPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadRequests() {
      if (!isSupabaseConfigured || !supabase) {
        if (!cancelled) {
          setLoading(false);
          setListError('Supabase is not configured. Add .env keys and restart the app.');
        }
        return;
      }

      setLoading(true);
      setListError('');
      const { data, error } = await supabase
        .from('custom_service_requests')
        .select('id, full_name, email, service_type, preferred_date, message, status, created_at')
        .order('created_at', { ascending: false });

      if (cancelled) return;
      setLoading(false);
      if (error) {
        setListError(error.message);
        setRequests([]);
        return;
      }
      setRequests(Array.isArray(data) ? data : []);
    }

    loadRequests();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AdminLayout activeNav="services" title="Custom Services">
      <div className="admin-products-toolbar">
        <p className="admin-products-count">
          {loading ? 'Loading requests…' : `${requests.length} requests`}
        </p>
      </div>

      {listError && (
        <p className="admin-login-error admin-products-list-error" role="alert">
          {listError}
        </p>
      )}

      <div className="admin-table-wrap">
        {!loading && requests.length === 0 && !listError ? (
          <p className="admin-products-empty">No custom service requests yet.</p>
        ) : (
          <table className="admin-products-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Service</th>
                <th scope="col">Preferred Date</th>
                <th scope="col">Message</th>
                <th scope="col">Status</th>
                <th scope="col">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((row) => (
                <tr key={row.id}>
                  <td>{row.full_name || '—'}</td>
                  <td>{row.email || '—'}</td>
                  <td>{row.service_type || '—'}</td>
                  <td>{row.preferred_date || '—'}</td>
                  <td style={{ minWidth: 240 }}>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{row.message || '—'}</div>
                  </td>
                  <td>{row.status || 'new'}</td>
                  <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}

function AdminSubcategoryEditPage() {
  const subcategoryId = getAdminSubcategoryEditIdFromPath();
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [loadState, setLoadState] = useState('loading');
  const [loadErr, setLoadErr] = useState('');
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState(EMPTY_SUBCATEGORY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadCats() {
      if (isSupabaseConfigured && supabase) {
        const { data } = await supabase
          .from('categories')
          .select('id, name, slug')
          .order('name', { ascending: true });
        if (!cancelled) setCategories(data ?? []);
      } else if (!cancelled) {
        setCategories(readAdminCategoriesFromStorage() ?? DEFAULT_ADMIN_CATEGORIES);
      }
      if (!cancelled) setCatLoading(false);
    }
    loadCats();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!subcategoryId) {
        setLoadState('notfound');
        return;
      }
      if (!isSupabaseConfigured || !supabase) {
        setLoadState('error');
        setLoadErr('Subcategories are edited in Supabase only. Configure your .env keys.');
        return;
      }
      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .eq('id', subcategoryId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setLoadState('error');
        setLoadErr(error.message);
        return;
      }
      if (!data) {
        setLoadState('notfound');
        return;
      }
      setExisting(data);
      setForm({
        category_id: String(data.category_id),
        name: data.name || '',
        description: data.description || '',
      });
      setLoadState('ready');
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [subcategoryId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    const name = form.name.trim();
    if (!form.category_id) {
      setFormError('Select a parent category.');
      return;
    }
    if (!name) {
      setFormError('Enter a subcategory name.');
      return;
    }
    if (!isSupabaseConfigured || !supabase || !existing) {
      setFormError('Cannot save: Supabase is not available.');
      return;
    }
    const slug = slugifyCatalog(name);
    const sortOrder = existing.sort_order != null ? Number(existing.sort_order) : 0;
    setSaving(true);
    const { error } = await supabase
      .from('subcategories')
      .update({
        category_id: form.category_id,
        name,
        slug,
        description: form.description.trim() || null,
        sort_order: Number.isNaN(sortOrder) ? 0 : sortOrder,
      })
      .eq('id', subcategoryId);
    setSaving(false);
    if (error) {
      setFormError(
        error.code === '23505' || /unique|duplicate/i.test(error.message)
          ? 'A subcategory with this name already exists under that category.'
          : error.message
      );
      return;
    }
    window.location.href = '/admin/subcategories';
  };

  if (loadState === 'loading') {
    return (
      <AdminLayout activeNav="subcategories" title="Edit subcategory">
        <p className="admin-products-count">Loading subcategory…</p>
      </AdminLayout>
    );
  }
  if (loadState === 'notfound') {
    return (
      <AdminLayout activeNav="subcategories" title="Edit subcategory">
        <p className="admin-products-empty">Subcategory not found.</p>
        <a className="ghost-btn" href="/admin/subcategories">
          ← Back to subcategories
        </a>
      </AdminLayout>
    );
  }
  if (loadState === 'error') {
    return (
      <AdminLayout activeNav="subcategories" title="Edit subcategory">
        <p className="admin-login-error" role="alert">
          {loadErr}
        </p>
        <a className="ghost-btn" href="/admin/subcategories">
          ← Back to subcategories
        </a>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeNav="subcategories" title="Edit subcategory">
      <a className="ghost-btn admin-product-new-back" href="/admin/subcategories">
        ← Back to subcategories
      </a>
      <form className="admin-product-form admin-product-form-page auth-card" onSubmit={handleSubmit}>
        <h2 className="admin-product-form-title">Edit subcategory</h2>
        <p className="admin-product-form-intro">
          The URL slug is updated from the name. You can move this subcategory to another parent category.
        </p>
        <div className="admin-product-form-grid admin-product-form-grid-wide">
          <label className="admin-product-form-span">
            Parent category *
            <select
              value={form.category_id}
              onChange={(event) => setForm((f) => ({ ...f, category_id: event.target.value }))}
              required
              disabled={catLoading}
            >
              <option value="">{catLoading ? 'Loading…' : '— Select —'}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="admin-product-form-span">
            Name *
            <input
              value={form.name}
              onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
              placeholder="e.g. Evening dresses"
              required
            />
          </label>
          <label className="admin-product-form-span">
            Description
            <textarea
              rows={3}
              value={form.description}
              onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))}
              placeholder="Optional internal notes"
            />
          </label>
        </div>
        {formError && (
          <p className="admin-login-error" role="alert">
            {formError}
          </p>
        )}
        <div className="admin-product-form-actions">
          <a className="ghost-btn" href="/admin/subcategories">
            Cancel
          </a>
          <button
            className="primary-btn"
            type="submit"
            disabled={saving || catLoading || categories.length === 0}
          >
            {saving ? 'Saving…' : 'Update subcategory'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

function AdminSubcategoryNewPage() {
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_SUBCATEGORY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadCats() {
      if (isSupabaseConfigured && supabase) {
        const { data } = await supabase
          .from('categories')
          .select('id, name, slug')
          .order('name', { ascending: true });
        if (!cancelled) setCategories(data ?? []);
      } else if (!cancelled) {
        setCategories(readAdminCategoriesFromStorage() ?? DEFAULT_ADMIN_CATEGORIES);
      }
      if (!cancelled) setCatLoading(false);
    }
    loadCats();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    const name = form.name.trim();
    if (!form.category_id) {
      setFormError('Select a parent category.');
      return;
    }
    if (!name) {
      setFormError('Enter a subcategory name.');
      return;
    }
    const slug = slugifyCatalog(name);
    const row = {
      category_id: form.category_id,
      name,
      slug,
      description: form.description.trim() || null,
      sort_order: 0,
    };
    if (isSupabaseConfigured && supabase) {
      setSaving(true);
      const { error } = await supabase.from('subcategories').insert(row);
      setSaving(false);
      if (error) {
        setFormError(error.message);
        return;
      }
      window.location.href = '/admin/subcategories';
      return;
    }
    const list = readAdminSubcategoriesFromStorage() ?? [];
    if (list.some((s) => s.category_id === form.category_id && s.slug === slug)) {
      setFormError('A subcategory with this name already exists under that category.');
      return;
    }
    const localRow = {
      id: `sub-${Date.now()}`,
      ...row,
      description: row.description || '',
    };
    window.localStorage.setItem(
      ADMIN_SUBCATEGORIES_STORAGE_KEY,
      JSON.stringify([localRow, ...list])
    );
    window.location.href = '/admin/subcategories';
  };

  return (
    <AdminLayout activeNav="subcategories" title="Add subcategory">
      <a className="ghost-btn admin-product-new-back" href="/admin/subcategories">
        ← Back to subcategories
      </a>
      <form className="admin-product-form admin-product-form-page auth-card" onSubmit={handleSubmit}>
        <h2 className="admin-product-form-title">New subcategory</h2>
        <p className="admin-product-form-intro">
          Subcategories belong to a parent category. The URL slug is generated from the name. Create
          categories first if this list is empty.
        </p>
        <div className="admin-product-form-grid admin-product-form-grid-wide">
          <label className="admin-product-form-span">
            Parent category *
            <select
              value={form.category_id}
              onChange={(event) => setForm((f) => ({ ...f, category_id: event.target.value }))}
              required
              disabled={catLoading}
            >
              <option value="">{catLoading ? 'Loading…' : '— Select —'}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="admin-product-form-span">
            Name *
            <input
              value={form.name}
              onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
              placeholder="e.g. Evening dresses"
              required
            />
          </label>
          <label className="admin-product-form-span">
            Description
            <textarea
              rows={3}
              value={form.description}
              onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))}
              placeholder="Optional internal notes"
            />
          </label>
        </div>
        {formError && (
          <p className="admin-login-error" role="alert">
            {formError}
          </p>
        )}
        <div className="admin-product-form-actions">
          <a className="ghost-btn" href="/admin/subcategories">
            Cancel
          </a>
          <button
            className="primary-btn"
            type="submit"
            disabled={saving || catLoading || categories.length === 0}
          >
            {saving ? 'Saving…' : 'Save subcategory'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

function AccountPage() {
  const [isSignup, setIsSignup] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    if (window.localStorage.getItem(CUSTOMER_SESSION_KEY) === 'true') {
      window.location.href = '/';
    }
  }, []);

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    setAuthError('');
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setAuthError('Enter your email.');
      return;
    }
    if (!password) {
      setAuthError('Enter your password.');
      return;
    }
    if (!isSupabaseConfigured || !supabase) {
      setAuthError('Supabase is not configured. Add your .env keys and restart the app.');
      return;
    }

    try {
      setSubmitting(true);
      if (isSignup) {
        const cleanName = fullName.trim();
        if (!cleanName) {
          setSubmitting(false);
          setAuthError('Enter your full name.');
          return;
        }
        console.log('signup_submit_started', { email: cleanEmail });
        persistAuthDebugLog('log', 'signup_submit_started', { email: cleanEmail });
        const { data, error } = await supabase.rpc('signup_user', {
          p_full_name: cleanName,
          p_email: cleanEmail,
          p_password: password,
        });
        if (error) throw error;
        const first = Array.isArray(data) ? data[0] : null;
        window.localStorage.setItem(CUSTOMER_SESSION_KEY, 'true');
        window.localStorage.setItem(CUSTOMER_EMAIL_KEY, first?.email || cleanEmail);
        window.localStorage.setItem(CUSTOMER_NAME_KEY, cleanName);
        console.log('signup_submit_success', { email: first?.email || cleanEmail });
        persistAuthDebugLog('log', 'signup_submit_success', { email: first?.email || cleanEmail });
        try {
          const { data: emailData, error: emailError } = await supabase.functions.invoke(
            EDGE_FN_SEND_CUSTOMER_EMAIL,
            {
              body: {
                type: 'signup',
                to: first?.email || cleanEmail,
                customerName: cleanName,
              },
            }
          );
          if (emailError) {
            const detail = await edgeFunctionErrorMessage(emailError, emailData);
            console.error('signup_welcome_email_failed', {
              detail,
              raw: emailError,
              data: emailData,
            });
            persistAuthDebugLog('error', 'signup_welcome_email_failed', {
              detail,
              data: emailData,
            });
          } else {
            console.log('signup_welcome_email_sent', emailData || {});
            persistAuthDebugLog('log', 'signup_welcome_email_sent', emailData || {});
          }
        } catch (emailErr) {
          const detail = await edgeFunctionErrorMessage(emailErr, null);
          console.error('signup_welcome_email_failed', { detail, raw: emailErr });
          persistAuthDebugLog('error', 'signup_welcome_email_failed', { detail });
        }
      } else {
        const { data, error } = await supabase.rpc('login_user', {
          p_email: cleanEmail,
          p_password: password,
        });
        if (error) throw error;
        const first = Array.isArray(data) ? data[0] : null;
        window.localStorage.setItem(CUSTOMER_SESSION_KEY, 'true');
        window.localStorage.setItem(CUSTOMER_EMAIL_KEY, first?.email || cleanEmail);
        window.localStorage.setItem(CUSTOMER_NAME_KEY, first?.full_name || '');
      }
    } catch (err) {
      const msg = err?.message || 'Authentication failed.';
      setSubmitting(false);
      setAuthError(msg);
      return;
    }
    setSubmitting(false);
    // Small delay helps browser devtools show auth/email logs before navigation.
    window.setTimeout(() => {
      window.location.href = '/';
    }, 200);
  };

  return (
    <div className="App">
      <SiteHeader />
      <main className="container section-space account-page">
        <section className="account-single">
          <article className="auth-card auth-card-single">
            <h2>{isSignup ? 'Sign Up' : 'Sign In'}</h2>
            <form onSubmit={handleAuthSubmit}>
              {isSignup && (
                <>
                  <label>
                    Full Name
                    <input
                      type="text"
                      placeholder="Your name"
                      value={fullName}
                      onChange={(event) => setFullName(event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Phone Number
                    <input type="tel" placeholder="+1 000 000 0000" />
                  </label>
                  <label>
                    Date of Birth
                    <input type="date" />
                  </label>
                  <label>
                    Address
                    <input type="text" placeholder="Street address" />
                  </label>
                  <label>
                    City
                    <input type="text" placeholder="City" />
                  </label>
                  <label>
                    Country
                    <input type="text" placeholder="Country" />
                  </label>
                </>
              )}
              <label>
                Email
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  placeholder={isSignup ? 'Create password' : '••••••••'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </label>
              {authError && (
                <p className="admin-login-error" role="alert">
                  {authError}
                </p>
              )}
              <button className="primary-btn" type="submit" disabled={submitting}>
                {submitting ? 'Please wait…' : isSignup ? 'Create Account' : 'Sign In'}
              </button>
            </form>
            <p className="auth-switch">
              {isSignup ? 'Already have an account?' : 'If you want to sign up,'}{' '}
              <button
                type="button"
                onClick={() => {
                  setAuthError('');
                  setIsSignup((value) => !value);
                }}
              >
                {isSignup ? 'click here to sign in' : 'click here'}
              </button>
            </p>
          </article>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function summarizeCustomerOrderItems(items) {
  if (!Array.isArray(items) || items.length === 0) return '—';
  const parts = items.slice(0, 4).map((i) => {
    const n = i.product_name || 'Item';
    const q = Math.max(1, Number(i.quantity) || 1);
    return `${n} × ${q}`;
  });
  const joined = parts.join(', ');
  return items.length > 4 ? `${joined}…` : joined;
}

function customerOrderStatusClass(status) {
  const s = (status || '').toLowerCase();
  if (/(delivered|complete|fulfilled)/.test(s)) return 'is-delivered';
  return 'is-transit';
}

function ProfilePage() {
  const [name] = useState(window.localStorage.getItem(CUSTOMER_NAME_KEY) || '—');
  const [email] = useState(window.localStorage.getItem(CUSTOMER_EMAIL_KEY) || '—');

  useEffect(() => {
    if (window.localStorage.getItem(CUSTOMER_SESSION_KEY) !== 'true') {
      window.location.href = '/account';
    }
  }, []);

  return (
    <div className="App">
      <SiteHeader />
      <main className="container section-space account-page">
        <section className="account-single">
          <article className="auth-card auth-card-single">
            <h2>My Profile</h2>
            <p className="admin-login-lead">Your account details are shown below.</p>
            <form onSubmit={(event) => event.preventDefault()}>
              <label>
                Full Name
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  readOnly
                />
              </label>
              <label>
                Email
                <input type="email" value={email} readOnly />
              </label>
            </form>
          </article>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (window.localStorage.getItem(CUSTOMER_SESSION_KEY) !== 'true') {
      window.location.href = '/account';
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (window.localStorage.getItem(CUSTOMER_SESSION_KEY) !== 'true') {
        setLoading(false);
        return;
      }
      const email = (window.localStorage.getItem(CUSTOMER_EMAIL_KEY) || '').trim().toLowerCase();
      if (!email) {
        setLoading(false);
        setLoadError('Missing account email. Please sign in again.');
        setOrders([]);
        return;
      }
      if (!isSupabaseConfigured || !supabase) {
        setLoading(false);
        setLoadError('Supabase is not configured. Orders cannot be loaded.');
        setOrders([]);
        return;
      }

      setLoading(true);
      setLoadError('');

      const { data: customerRow, error: custErr } = await supabase
        .from('customers')
        .select('id')
        .eq('email', email)
        .maybeSingle();

      if (cancelled) return;

      if (custErr) {
        setLoading(false);
        setLoadError(custErr.message);
        setOrders([]);
        return;
      }

      if (!customerRow?.id) {
        setLoading(false);
        setOrders([]);
        return;
      }

      const { data: orderRows, error: ordersErr } = await supabase
        .from('orders')
        .select('id, order_number, status, payment_status, total_amount, created_at')
        .eq('customer_id', customerRow.id)
        .order('created_at', { ascending: false });

      if (cancelled) return;

      if (ordersErr) {
        setLoading(false);
        setLoadError(ordersErr.message);
        setOrders([]);
        return;
      }

      const list = Array.isArray(orderRows) ? orderRows : [];
      const orderIds = list.map((o) => o.id).filter(Boolean);

      let itemsByOrderId = {};
      if (orderIds.length > 0) {
        const { data: itemsData, error: itemsErr } = await supabase
          .from('order_items')
          .select('order_id, product_name, quantity')
          .in('order_id', orderIds)
          .order('created_at', { ascending: true });
        if (cancelled) return;
        if (!itemsErr && Array.isArray(itemsData)) {
          itemsData.forEach((item) => {
            const key = String(item.order_id || '');
            if (!key) return;
            if (!itemsByOrderId[key]) itemsByOrderId[key] = [];
            itemsByOrderId[key].push(item);
          });
        }
      }

      if (cancelled) return;
      setLoading(false);
      setOrders(
        list.map((row) => ({
          ...row,
          lineItems: itemsByOrderId[String(row.id)] || [],
        }))
      );
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="App">
      <SiteHeader />
      <main className="container section-space orders-page">
        <div className="orders-head">
          <p className="eyebrow">My Account</p>
          <h1 className="cart-title">Recent Orders</h1>
          <p>Track your latest purchases and delivery status.</p>
        </div>

        {loadError && (
          <p className="admin-login-error admin-products-list-error" role="alert">
            {loadError}
          </p>
        )}

        <section className="orders-list">
          {loading ? (
            <p className="order-meta">Loading your orders…</p>
          ) : orders.length === 0 ? (
            !loadError ? (
              <p className="order-meta">
                No orders yet. When you place an order with this email at checkout, it will show up here.
              </p>
            ) : null
          ) : (
            orders.map((order) => {
              const statusLabel =
                order.status ||
                order.payment_status ||
                'Processing';
              const placed = order.created_at
                ? new Date(order.created_at).toLocaleString()
                : '—';
              const totalStr = formatGBP(order.total_amount || 0);
              const itemsSummary = summarizeCustomerOrderItems(order.lineItems);
              return (
                <article className="order-card" key={order.id}>
                  <div>
                    <p className="order-id">{order.order_number || '—'}</p>
                    <p className="order-meta">{placed}</p>
                    <p className="order-meta">{itemsSummary}</p>
                  </div>
                  <div className="order-right">
                    <p
                      className={`order-status ${customerOrderStatusClass(statusLabel)}`}
                    >
                      {statusLabel}
                    </p>
                    <strong>{totalStr}</strong>
                  </div>
                </article>
              );
            })
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function CustomServicesPage() {
  const steps = [
    { title: 'Consultation', detail: 'Share your preferences, occasion, and wardrobe goals.' },
    { title: 'Curation', detail: 'Our stylists craft a personalized luxury selection for you.' },
    { title: 'Delivery & Follow-up', detail: 'Receive concierge delivery with fit and styling guidance.' },
  ];
  const [form, setForm] = useState({
    fullName: '',
    email: window.localStorage.getItem(CUSTOMER_EMAIL_KEY) || '',
    serviceType: 'Private Styling',
    preferredDate: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');

    const payload = {
      full_name: form.fullName.trim(),
      email: form.email.trim().toLowerCase(),
      service_type: form.serviceType.trim(),
      preferred_date: form.preferredDate || null,
      message: form.message.trim() || null,
    };

    if (!payload.full_name || payload.full_name.length < 2) {
      setSubmitError('Please enter your full name.');
      return;
    }
    if (!payload.email || !payload.email.includes('@')) {
      setSubmitError('Please enter a valid email address.');
      return;
    }
    if (!payload.service_type) {
      setSubmitError('Please select a service type.');
      return;
    }
    if (!isSupabaseConfigured || !supabase) {
      setSubmitError('Supabase is not configured. Please try again later.');
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.from('custom_service_requests').insert([payload]);
    setSubmitting(false);

    if (error) {
      setSubmitError(error.message || 'Could not submit your request.');
      return;
    }

    setSubmitSuccess('Your request has been submitted successfully.');
    setForm((prev) => ({ ...prev, serviceType: 'Private Styling', preferredDate: '', message: '' }));
  };

  return (
    <div className="App">
      <SiteHeader />
      <main className="custom-services-page">
        <section className="custom-hero section-space">
          <div className="container">
            <p className="eyebrow">Custom Services</p>
            <h1>Private Fashion & Styling Concierge</h1>
            <p>
              Exclusive appointments, bespoke wardrobe edits, and premium guidance designed
              around your personal style.
            </p>
          </div>
        </section>

        <section className="section-space">
          <div className="container custom-desc-grid">
            <article className="custom-desc-card">
              <h2>Services Tailored to You</h2>
              <p>
                From private shopping sessions to event styling and gift concierge, our team
                delivers a seamless luxury experience with discretion and precision.
              </p>
            </article>
            <div className="custom-desc-visual" />
          </div>
        </section>

        <section className="section-space custom-process">
          <div className="container">
            <div className="section-heading">
              <h2>How It Works</h2>
            </div>
            <div className="process-grid">
              {steps.map((step, index) => (
                <article className="process-card" key={step.title}>
                  <span>{`0${index + 1}`}</span>
                  <h3>{step.title}</h3>
                  <p>{step.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section-space">
          <div className="container">
            <article className="custom-form-card">
              <h2>Request a Private Consultation</h2>
              <form className="custom-form" onSubmit={handleSubmit}>
                <div className="custom-form-grid">
                  <label>
                    Full Name
                    <input
                      type="text"
                      placeholder="Your name"
                      value={form.fullName}
                      onChange={(event) => setForm((prev) => ({ ...prev, fullName: event.target.value }))}
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                    />
                  </label>
                </div>
                <div className="custom-form-grid">
                  <label>
                    Service Type
                    <select
                      value={form.serviceType}
                      onChange={(event) => setForm((prev) => ({ ...prev, serviceType: event.target.value }))}
                    >
                      <option>Private Styling</option>
                      <option>Wardrobe Curation</option>
                      <option>Gift Concierge</option>
                    </select>
                  </label>
                  <label>
                    Preferred Date
                    <input
                      type="date"
                      value={form.preferredDate}
                      onChange={(event) => setForm((prev) => ({ ...prev, preferredDate: event.target.value }))}
                    />
                  </label>
                </div>
                <label>
                  Message
                  <textarea
                    rows="5"
                    placeholder="Tell us what you are looking for..."
                    value={form.message}
                    onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
                  />
                </label>
                {submitError && (
                  <p className="admin-login-error" role="alert">
                    {submitError}
                  </p>
                )}
                {submitSuccess && (
                  <p className="admin-products-empty" role="status">
                    {submitSuccess}
                  </p>
                )}
                <button className="primary-btn" type="submit" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send Inquiry'}
                </button>
              </form>
            </article>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function DeliveryReturnsPage() {
  return (
    <div className="App">
      <SiteHeader />
      <main className="container section-space info-page">
        <div className="section-heading">
          <p className="eyebrow">Information</p>
          <h1 className="cart-title">Delivery & Returns</h1>
        </div>

        <section className="info-grid">
          <article className="info-card">
            <div className="info-icon">🚚</div>
            <h2>Delivery Details</h2>
            <p>
              Complimentary delivery is available on qualifying orders. All items are
              carefully packaged with premium presentation and secure handling.
            </p>
          </article>

          <article className="info-card">
            <div className="info-icon">⏱</div>
            <h2>Shipping Timelines</h2>
            <p>
              Standard delivery: 3-5 business days. Express delivery: 1-2 business days.
              International shipping timelines vary by destination.
            </p>
          </article>

          <article className="info-card">
            <div className="info-icon">↩</div>
            <h2>Returns Policy</h2>
            <p>
              Returns are accepted within 14 days in original condition. Once approved,
              refunds are processed to the original payment method.
            </p>
          </article>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function ContactPage() {
  const [form, setForm] = useState({
    fullName: '',
    email: window.localStorage.getItem(CUSTOMER_EMAIL_KEY) || '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');

    const payload = {
      full_name: form.fullName.trim(),
      email: form.email.trim().toLowerCase(),
      subject: form.subject.trim(),
      message: form.message.trim(),
      source_page: '/contact',
    };

    if (!payload.full_name || payload.full_name.length < 2) {
      setSubmitError('Please enter your full name.');
      return;
    }
    if (!payload.email || !payload.email.includes('@')) {
      setSubmitError('Please enter a valid email address.');
      return;
    }
    if (!payload.subject || payload.subject.length < 2) {
      setSubmitError('Please enter a subject.');
      return;
    }
    if (!payload.message || payload.message.length < 5) {
      setSubmitError('Please enter a longer message.');
      return;
    }
    if (!isSupabaseConfigured || !supabase) {
      setSubmitError('Supabase is not configured. Please try again later.');
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.from('contact_messages').insert([payload]);
    setSubmitting(false);
    if (error) {
      setSubmitError(error.message);
      return;
    }

    setSubmitSuccess('Thanks, your message has been sent. We will contact you shortly.');
    setForm((prev) => ({ ...prev, subject: '', message: '' }));
  };

  return (
    <div className="App">
      <SiteHeader />
      <main className="container section-space contact-page">
        <div className="section-heading">
          <p className="eyebrow">Contact</p>
          <h1 className="cart-title">Get in Touch</h1>
        </div>

        <section className="contact-grid">
          <article className="contact-form-card">
            <h2>Send a Message</h2>
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="contact-form-grid">
                <label>
                  Full Name
                  <input
                    type="text"
                    placeholder="Your name"
                    value={form.fullName}
                    onChange={(event) => setForm((prev) => ({ ...prev, fullName: event.target.value }))}
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                  />
                </label>
              </div>
              <label>
                Subject
                <input
                  type="text"
                  placeholder="How can we help?"
                  value={form.subject}
                  onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
                />
              </label>
              <label>
                Message
                <textarea
                  rows="6"
                  placeholder="Write your message..."
                  value={form.message}
                  onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
                />
              </label>
              {submitError && (
                <p className="admin-login-error" role="alert">
                  {submitError}
                </p>
              )}
              {submitSuccess && (
                <p className="admin-products-empty" role="status">
                  {submitSuccess}
                </p>
              )}
              <button className="primary-btn" type="submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send Message'}
              </button>
            </form>
          </article>

          <article className="contact-info-card">
            <h2>Contact Details</h2>
            <p><strong>Email:</strong> contact@dstyluxe.co.uk</p>
            <p><strong>Phone:</strong> 0330 0 168 168</p>
            <p><strong>Address:</strong> 38 Thrales Close, Luton, UK, LU3 3RS</p>
          </article>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function HelpFaqPage() {
  const faqs = [
    {
      q: 'How long does delivery usually take?',
      a: 'Delivery times depend on your location and selected shipping method. Estimated timelines are shown during checkout.',
    },
    {
      q: 'Can I return an item?',
      a: 'Most items can be returned within the return window if they are unused and in original condition.',
    },
    {
      q: 'How do I track my order?',
      a: 'After your order is shipped, tracking details are provided so you can follow delivery progress.',
    },
    {
      q: 'Can I change or cancel an order?',
      a: 'If your order has not been processed yet, changes or cancellation may be possible from your account or order section.',
    },
    {
      q: 'What payment methods are accepted?',
      a: 'Common card and digital payment methods are usually supported. Available options are shown at checkout.',
    },
    {
      q: 'Do prices include taxes and shipping?',
      a: 'Taxes and shipping costs are calculated based on your location and order details, then shown before payment.',
    },
    {
      q: 'Can I ship to a different address?',
      a: 'Yes, you can usually enter a separate shipping address during checkout if needed.',
    },
    {
      q: 'What should I do if my package is delayed?',
      a: 'Check your tracking updates first. If there is no movement for an extended period, contact support for assistance.',
    },
    {
      q: 'How do I use a discount code?',
      a: 'Enter your code in the promo or discount field at checkout. The total updates automatically if the code is valid.',
    },
  ];

  return (
    <div className="App">
      <SiteHeader />
      <main className="container section-space faq-page help-faq-page">
        <div className="section-heading">
          <p className="eyebrow">Support</p>
          <h1 className="cart-title">Help / FAQ</h1>
        </div>

        <section className="faq-list">
          {faqs.map((item) => (
            <article className="faq-item" key={item.q}>
              <h3>{item.q}</h3>
              <p>{item.a}</p>
            </article>
          ))}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function AboutUsPage() {
  return (
    <div className="App">
      <SiteHeader />
      <main className="about-page">
        <section className="about-hero section-space">
          <div className="container">
            <p className="eyebrow">About Us</p>
            <h1>Dstyluxe&reg;</h1>
            <p>
              Welcome to Dstyluxe
            </p>
          </div>
        </section>

        <section className="section-space">
          <div className="container policy-content">
            <article className="policy-block">
              <p>
                Dstyluxe&reg; is a luxury brand for the sophisticated and stylish. With our
                comprehensive Bespoke and Corporate services, offering everything from fashion items to
                lifestyle products, we embody the belief that "anything is possible" to help you find
                exactly what you are looking for.
              </p>
              <p>
                As a leading platform in the fashion and lifestyle sector, Dstyluxe operates under a
                registered trademark, providing the ultimate destination for contemporary fashion and
                premium beauty products. Founded with a commitment to empowering individuals through
                style, Dstyluxe is dedicated to presenting a meticulously curated selection of clothing,
                fragrances, gifts, and beauty essentials that combine luxury, comfort, and
                affordability.
              </p>
            </article>

            <article className="about-card">
              <h2>Fashion Collection</h2>
              <p>
                At Dstyluxe, we believe that fashion is an expression of individuality. Our clothing
                and fashion line features a diverse range of stylish and trend-setting pieces designed
                for every occasion - from casual everyday wear to elegant evening attire.
              </p>
              <p>
                Each item is crafted with attention to detail, ensuring a perfect fit and high-quality
                materials. Whether you are looking for the latest fashion trends or timeless classics,
                our collection has something for everyone.
              </p>
            </article>
            <article className="about-card">
              <h2>Beauty Line</h2>
              <p>
                In addition to fashionable attire, Styluxe offers an exquisite range of beauty products
                designed to enhance your natural beauty. We are committed to using cruelty-free and
                eco-friendly practices, ensuring that our products are not only good for you but also
                good for the planet.
              </p>
            </article>
            <article className="about-card">
              <h2>Customer Experience</h2>
              <p>
                At Dstyluxe, our customers are at the heart of everything we do. We strive to create an
                exceptional shopping experience, from browsing our online store to receiving your order.
              </p>
              <p>
                Our friendly and knowledgeable customer service team is always ready to assist you with
                any inquiries, styling tips, or product recommendations. We are dedicated to ensuring
                that every customer feels valued and satisfied with their purchase.
              </p>
            </article>
            <article className="about-card">
              <h2>Community and Sustainability</h2>
              <p>
                We believe in giving back to the community and promoting sustainable practices.
                Dstyluxe actively participates in initiatives that support local charities and
                environmental causes.
              </p>
              <p>
                Our commitment to sustainability drives us to continuously seek eco-friendly materials
                and packaging solutions, ensuring that we contribute positively to our world.
              </p>
            </article>
            <article className="about-card">
              <h2>Join the Dstyluxe Family</h2>
              <p>
                We invite you to explore our collections and discover why so many fashion and beauty
                enthusiasts are falling in love with Dstyluxe. Whether you are treating yourself or
                searching for the perfect gift, we are confident that you will find something special
                with us.
              </p>
              <p>
                Join our growing community on social media for the latest updates, styling inspiration,
                and exclusive offers. Thank you for choosing Dstyluxe as your go-to brand for fashion
                and beauty. We look forward to being a part of your style journey.
              </p>
            </article>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function PrivacyPolicyPage() {
  return (
    <div className="App">
      <SiteHeader />
      <main className="container section-space policy-page privacy-policy-page">
        <div className="section-heading">
          <p className="eyebrow">Legal</p>
          <h1 className="cart-title">Privacy Policy</h1>
        </div>

        <section className="policy-content">
          <article className="policy-block">
            <h2>Information We Collect</h2>
            <p>
              We collect personal details such as name, contact information, and order history
              to process purchases and provide tailored services.
            </p>
          </article>

          <article className="policy-block">
            <h2>How We Use Your Data</h2>
            <p>
              Your data is used to fulfill orders, improve your shopping experience, and send
              service updates. Marketing communication is sent only with your consent.
            </p>
          </article>

          <article className="policy-block">
            <h2>Data Security</h2>
            <p>
              We use encrypted systems and secure payment processing to protect your information
              from unauthorized access.
            </p>
          </article>

          <article className="policy-block">
            <h2>Your Rights</h2>
            <p>
              You may request access, correction, or deletion of personal data by contacting
              our support team at contact@dstyluxe.co.uk.
            </p>
          </article>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function ReturnsPolicyPage() {
  return (
    <div className="App">
      <SiteHeader />
      <main className="container section-space policy-page returns-policy-page">
        <div className="section-heading">
          <p className="eyebrow">Information</p>
          <h1 className="cart-title">Returns Policy</h1>
        </div>

        <section className="policy-content">
          <article className="policy-block">
            <p>
              At Dstyluxe, we are committed to ensuring your satisfaction with every purchase. If you
              find it necessary to return an item, we are here to assist you. Our Returns Policy is
              designed to provide clarity and ease while ensuring that our customers feel supported in
              their decisions.
            </p>
          </article>

          <article className="policy-block">
            <h2>Return Period</h2>
            <p>
              You may return most items within 14 days from the date of receipt. To initiate a return,
              please contact us prior to shipping the item back. Our team is dedicated to guiding you
              through the process and addressing any concerns you may have.
            </p>
            <p>
              Please always check the product information section for the item you have ordered,
              available on our website or app, for details on whether the item is returnable.
            </p>
          </article>

          <article className="policy-block">
            <h2>Refunds and Exchanges</h2>
            <p>
              Please note that we do not offer exchanges or refunds for all items. However, returned
              items that are eligible may receive a refund, exchange for items of equal value, or
              replacement with a new item. Our goal is to ensure you are completely satisfied with your
              selection whenever possible.
            </p>
          </article>

          <article className="policy-block">
            <h2>Damaged Items</h2>
            <p>
              In the unfortunate event that you receive a damaged item, we will gladly replace or
              refund it. To assist us in processing your return, we may request that you return the
              damaged item for assessment or, in some cases, provide photographic or videographic
              evidence of the damage.
            </p>
          </article>

          <article className="policy-block">
            <h2>Customised Items</h2>
            <p>
              Please note that customised items are non-returnable unless there has been an error on our
              part. We recommend double-checking all engraving and customisation details before
              finalising your order.
            </p>
            <p>
              While we strive for accuracy, if an error occurs on our side, we will promptly correct it
              at no additional cost to you. If a mistake is made during the ordering process, we will
              work with you to find a mutual agreement, though we cannot accept responsibility for those
              errors.
            </p>
          </article>

          <article className="policy-block">
            <h2>Unsatisfactory Items</h2>
            <p>
              If your item does not meet your expectations, please reach out to us to discuss your
              concerns. We are committed to understanding your needs and finding a resolution that works
              for both parties. If you are returning any order, please contact us and our team will
              work with you to find a mutual agreement.
            </p>
          </article>

          <article className="policy-block">
            <h2>Delivery Issues</h2>
            <p>
              Please be aware that it is your responsibility to collect items from the postal service or
              arrange redelivery with our couriers. If items are returned to us due to failure to
              collect, a redelivery fee may apply. In cases where delivery issues arise due to postal
              service errors, we will resend the item without additional cost to you.
            </p>
          </article>

          <article className="policy-block">
            <h2>Responsibility</h2>
            <p>
              Returned items are your responsibility until they reach us, so make sure they are packed
              properly and cannot get damaged on the way.
            </p>
            <p>
              As the parcel remains your responsibility until it arrives back to us, please ensure that
              you get proof of postage in case you need to contact us about your return.
            </p>
          </article>

          <article className="policy-block">
            <h2>After that?</h2>
            <p>
              We do not accept returns for unwanted items after the relevant returns period above. If
              you try to make a return, we may elect to send it back to your default delivery address
              and ask you to cover the delivery costs.
            </p>
          </article>

          <article className="policy-block">
            <h2>Non-Delivery</h2>
            <p>
              If your item has not arrived, please contact us as soon as possible. Occasionally, items
              may be lost in transit or returned by customs. We are here to assist you in resolving such
              issues.
            </p>
          </article>

          <article className="policy-block">
            <h2>Return Postage Costs</h2>
            <p>
              As we strive for accuracy, please note that return postage costs will be the
              responsibility of the customer. Once we receive the order, we aim to refund you within 14
              days of receiving the returned item if applicable. We appreciate your understanding in
              this matter.
            </p>
          </article>

          <article className="policy-block">
            <h2>Return Address</h2>
            <p>All returns should be sent to:</p>
            <p>
              Dstyluxe
              <br />
              38 Thrales Close,
              <br />
              Luton, United Kingdom.
              <br />
              LU3 3RS.
            </p>
            <p>
              For assistance with returns, please email us at contact@dstyluxe.co.uk. We appreciate your
              understanding and look forward to resolving any issues to your satisfaction. Thank you for
              choosing Dstyluxe.
            </p>
          </article>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function ShippingServicePage() {
  return (
    <div className="App">
      <SiteHeader />
      <main className="container section-space policy-page shipping-service-page">
        <div className="section-heading">
          <p className="eyebrow">Information</p>
          <h1 className="cart-title">Shipping Service</h1>
        </div>

        <section className="policy-content">
          <article className="policy-block">
            <p>
              At Dstyluxe, we are committed to providing you with an exceptional shopping experience,
              and that includes fast and reliable shipping. We take great care in packaging your orders
              and strive to ship them as quickly as possible.
            </p>
            <p>
              Most orders are processed and dispatched within 24 business hours of being placed,
              ensuring you receive your items promptly. Please note that weekends, promotional periods,
              and holidays are excluded from processing and shipping times.
            </p>
          </article>

          <article className="policy-block">
            <h2>Free Delivery Offer</h2>
            <p>
              Enjoy free delivery within the United Kingdom on orders over GBP 20. It is our way of
              saying thank you for choosing Dstyluxe.
            </p>
          </article>

          <article className="policy-block">
            <h2>Reliable Delivery Partners</h2>
            <p>
              We have partnered with reputable and trusted postal services to ensure your orders are
              delivered promptly, including Royal Mail, DPD, UPS, Amazon Logistics, EVRI, Yodel, and
              DHL.
            </p>
            <p>
              Most parcels are delivered within 2 working days from dispatch, and we aim to send your
              orders within 24 hours of placement.
            </p>
          </article>

          <article className="policy-block">
            <h2>Flexible Delivery Options</h2>
            <p>
              We understand that every customer has unique needs. If you wish to select an alternative
              shipping method or require express delivery, please contact us for availability. Please
              note that additional charges may apply.
            </p>
          </article>

          <article className="policy-block">
            <h2>Comprehensive Delivery Information</h2>
            <p>
              We ship directly to your door from Monday to Saturday, though some of our couriers do not
              operate on Bank Holidays.
            </p>
            <p>
              While we aim to deliver within the provided timeframes, please be aware that delivery
              times may be affected by factors beyond our control. In the event of a delay, we will
              keep you updated and take all necessary steps to resolve any issues.
            </p>
          </article>

          <article className="policy-block">
            <h2>International Deliveries</h2>
            <p>
              We are pleased to offer international shipping options. Delivery costs for international
              orders will be calculated at checkout, or you can reach out to us for assistance.
            </p>
            <p>
              Please note that international deliveries and items on sale are not returnable. Specific
              areas, including Northern Ireland, the Isle of Man, the Isles of Scilly, the Channel
              Islands, and the Isle of Wight, may require additional delivery time.
            </p>
          </article>

          <article className="policy-block">
            <h2>Weight-Based Charges</h2>
            <p>
              For certain international and European deliveries, shipping costs may vary based on
              weight. We reserve the right to adjust delivery charges for parcels that exceed standard
              dimensions.
            </p>
            <p>
              For larger deliveries, we will contact you to discuss shipping and any import duties that
              may apply.
            </p>
          </article>

          <article className="policy-block">
            <h2>Changes to Shipping Policy</h2>
            <p>
              Dstyluxe reserves the right to modify this shipping policy at any time. Any changes will
              be posted on this page, and the effective date will be updated accordingly.
            </p>
          </article>

          <article className="policy-block">
            <h2>Customer Service</h2>
            <p>
              If you have any questions or concerns regarding our shipping policy, please do not
              hesitate to reach out to our customer service team at contact@dstyluxe.co.uk. Our team is
              available to assist you.
            </p>
            <p>
              At Dstyluxe, we continuously update our delivery options to provide you with the best
              service possible. Thank you for choosing us, and we look forward to delivering your order
              with care.
            </p>
          </article>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function TermsPage() {
  return (
    <div className="App">
      <SiteHeader />
      <main className="container section-space policy-page terms-page">
        <div className="section-heading">
          <p className="eyebrow">Legal</p>
          <h1 className="cart-title">Terms &amp; Conditions</h1>
        </div>

        <section className="policy-content">
          <article className="policy-block">
            <h2>Use of Website</h2>
            <p>
              By using this website, you agree to comply with these terms and all applicable
              laws. The content is provided for personal, non-commercial use.
            </p>
          </article>

          <article className="policy-block">
            <h2>Orders &amp; Payment</h2>
            <p>
              Orders are subject to availability and confirmation. Prices and product details
              may be updated without notice. Payment is securely processed at checkout.
            </p>
          </article>

          <article className="policy-block">
            <h2>Shipping &amp; Returns</h2>
            <p>
              Delivery timelines are estimates and may vary. Returns are accepted according
              to our returns policy and only for eligible items in original condition.
            </p>
          </article>

          <article className="policy-block">
            <h2>Intellectual Property</h2>
            <p>
              All website content, including branding, images, and text, is owned by
              Dstyluxe and may not be copied or reused without permission.
            </p>
          </article>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function WishlistPage() {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [removingId, setRemovingId] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadWishlist() {
      const userEmail = (window.localStorage.getItem(CUSTOMER_EMAIL_KEY) || '').trim().toLowerCase();
      const loggedIn = Boolean(userEmail) && window.localStorage.getItem(CUSTOMER_SESSION_KEY) === 'true';

      if (!loggedIn) {
        setLoading(true);
        setListError('');
        const rows = readGuestWishlistRows();
        if (cancelled) return;
        setWishlistItems(
          rows.map((r) => ({
            id: `guest:${String(r.product_ref)}`,
            product_ref: r.product_ref,
            product_name: r.product_name,
            product_price: r.product_price,
            product_image: r.product_image,
            created_at: r.added_at,
            _guest: true,
          }))
        );
        setLoading(false);
        return;
      }

      if (!isSupabaseConfigured || !supabase) {
        setLoading(false);
        setListError('Supabase is not configured. Wishlist cannot be loaded.');
        setWishlistItems([]);
        return;
      }

      setLoading(true);
      setListError('');
      const { data, error } = await supabase
        .from('wishlist_items')
        .select('id, product_ref, product_name, product_price, product_image, created_at')
        .eq('user_email', userEmail)
        .order('created_at', { ascending: false });

      if (cancelled) return;
      setLoading(false);
      if (error) {
        setListError(error.message);
        setWishlistItems([]);
        return;
      }
      setWishlistItems(Array.isArray(data) ? data : []);
    }

    loadWishlist();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRemove = async (itemId, productRef, isGuest) => {
    if (!itemId) return;
    if (isGuest || String(itemId).startsWith('guest:')) {
      const ref = productRef || String(itemId).replace(/^guest:/, '');
      setRemovingId(String(itemId));
      removeGuestWishlistProductRef(ref);
      setRemovingId('');
      setWishlistItems((prev) => prev.filter((row) => String(row.id) !== String(itemId)));
      return;
    }
    const userEmail = (window.localStorage.getItem(CUSTOMER_EMAIL_KEY) || '').trim().toLowerCase();
    if (!userEmail || !isSupabaseConfigured || !supabase) return;
    setRemovingId(String(itemId));
    const { error } = await supabase
      .from('wishlist_items')
      .delete()
      .eq('id', itemId)
      .eq('user_email', userEmail);
    setRemovingId('');
    if (error) {
      setListError(error.message);
      return;
    }
    setWishlistItems((prev) => prev.filter((row) => String(row.id) !== String(itemId)));
    try {
      window.dispatchEvent(new Event('wishlist-updated'));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="App">
      <SiteHeader />
      <main className="container section-space wishlist-page">
        <div className="section-heading">
          <p className="eyebrow">My Account</p>
          <h1 className="cart-title">Wishlist</h1>
        </div>
        {listError && (
          <p className="admin-login-error admin-products-list-error" role="alert">
            {listError}
          </p>
        )}
        <section className="wishlist-grid">
          {loading ? (
            <p className="order-meta">Loading wishlist…</p>
          ) : wishlistItems.length === 0 ? (
            !listError ? <p className="order-meta">Your wishlist is empty.</p> : null
          ) : (
            wishlistItems.map((item) => {
              const productUrl = item.product_ref
                ? `/product/${encodeURIComponent(item.product_ref)}`
                : '/shop/new-in';
              return (
                <ProductCardUi
                  key={item.id}
                  item={{
                    id: item.product_ref,
                    name: item.product_name || 'Untitled product',
                    image:
                      item.product_image ||
                      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=900&q=80',
                    priceNumber: Number(item.product_price) || 0,
                  }}
                  productHref={productUrl}
                  priceDisplay={formatGBP(item.product_price || 0)}
                  hideWishlist
                  hideCart
                  footer={
                    <div className="wishlist-actions">
                      <a className="primary-btn wishlist-btn" href={productUrl}>
                        View Product
                      </a>
                      <button
                        className="ghost-btn"
                        type="button"
                        onClick={() => handleRemove(item.id, item.product_ref, item._guest)}
                        disabled={removingId === String(item.id)}
                      >
                        {removingId === String(item.id) ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  }
                />
              );
            })
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function App() {
  const path = window.location.pathname.toLowerCase();
  if (path.startsWith('/admin/subcategories/edit')) {
    return <AdminSubcategoryEditPage />;
  }
  if (path.startsWith('/admin/subcategories/new')) {
    return <AdminSubcategoryNewPage />;
  }
  if (path.startsWith('/admin/subcategories')) {
    return <AdminSubcategoriesPage />;
  }
  if (path.startsWith('/admin/customers')) {
    return <AdminCustomersPage />;
  }
  if (path.startsWith('/admin/contact')) {
    return <AdminContactPage />;
  }
  if (path.startsWith('/admin/newsletter')) {
    return <AdminNewsletterPage />;
  }
  if (path.startsWith('/admin/custom-services')) {
    return <AdminCustomServicesPage />;
  }
  if (path.startsWith('/admin/categories/edit')) {
    return <AdminCategoryEditPage />;
  }
  if (path.startsWith('/admin/categories/new')) {
    return <AdminCategoryNewPage />;
  }
  if (path.startsWith('/admin/categories')) {
    return <AdminCategoriesPage />;
  }
  if (path.startsWith('/admin/products/new')) {
    return <AdminProductNewPage />;
  }
  if (path.startsWith('/admin/products/edit')) {
    return <AdminProductNewPage />;
  }
  if (path.startsWith('/admin/orders')) {
    return <AdminOrdersPage />;
  }
  if (path.startsWith('/admin/products')) {
    return <AdminProductsPage />;
  }
  if (path.startsWith('/admin/dashboard')) {
    return <AdminDashboardPage />;
  }
  if (path.startsWith('/admin/reports')) {
    return <AdminReportsPage />;
  }
  if (path.startsWith('/admin')) {
    return <AdminLoginPage />;
  }
  if (path.startsWith('/wishlist')) {
    return <WishlistPage />;
  }
  if (path.startsWith('/terms')) {
    return <TermsPage />;
  }
  if (path.startsWith('/privacy-policy')) {
    return <PrivacyPolicyPage />;
  }
  if (path.startsWith('/shipping-service')) {
    return <ShippingServicePage />;
  }
  if (path.startsWith('/returns-policy')) {
    return <ReturnsPolicyPage />;
  }
  if (path.startsWith('/about-us')) {
    return <AboutUsPage />;
  }
  if (path.startsWith('/help-faq')) {
    return <HelpFaqPage />;
  }
  if (path.startsWith('/contact')) {
    return <ContactPage />;
  }
  if (path.startsWith('/delivery-returns')) {
    return <DeliveryReturnsPage />;
  }
  if (path.startsWith('/custom-services')) {
    return <CustomServicesPage />;
  }
  if (path.startsWith('/orders')) {
    return <OrdersPage />;
  }
  if (path.startsWith('/profile')) {
    return <ProfilePage />;
  }
  if (path.startsWith('/account')) {
    return <AccountPage />;
  }
  if (path.startsWith('/thank-you')) {
    return <ThankYouPage />;
  }
  if (path.startsWith('/checkout')) {
    return <CheckoutPage />;
  }
  if (path.startsWith('/cart')) {
    return <CartPage />;
  }
  if (path.startsWith('/product')) {
    return <ProductPage />;
  }
  if (path.startsWith('/shop')) {
    const parts = path.split('/');
    const sectionSlug = parts[2] || 'for-her';
    const subcategorySlug = parts[3] || '';
    return <ShopPage sectionSlug={sectionSlug} subcategorySlug={subcategorySlug} />;
  }
  return <HomePage />;
}

export default App;

